import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import {
  parseTravelHoursAmount,
  snapToHourlyTravelStep,
} from "../utils/shiftTravelDisplay";
import {
  X,
  User,
  FileText,
  FolderOpen,
  Clock,
  Users,
  Briefcase,
  Settings,
  MapPin,
  Shield,
} from "lucide-react";
import { AtlasListToolbar, SortableTableHeader, TableHeaderCell } from "./list-toolbar";
import { EmbeddedFilterPanel, useEmbeddedFilterLayout } from "./embedded-filter";
import {
  AtlasFilterChipBar,
  FilterSelect,
  FilterToggle,
  buildClientLocationsFilterChips,
  CLIENT_LOCATIONS_SORT_OPTIONS,
  buildClientPositionsFilterChips,
  CLIENT_POSITIONS_SORT_OPTIONS,
  buildClientPreferencesFilterChips,
  CLIENT_PREFERENCES_SORT_OPTIONS,
  CLIENT_PREFERENCES_TYPE_OPTIONS,
  CLIENT_PREFERENCES_DEFAULT_FILTERS,
  buildClientFilesFilterChips,
  CLIENT_FILES_SORT_OPTIONS,
  CLIENT_FILE_TYPE_OPTIONS,
  CLIENT_FILE_STATUS_OPTIONS,
  CLIENT_FILES_DEFAULT_FILTERS,
  buildClientContactsFilterChips,
  CLIENT_CONTACTS_SORT_OPTIONS,
  buildClientNotesFilterChips,
  CLIENT_NOTES_SORT_OPTIONS,
  CLIENT_NOTES_RELATED_OPTIONS,
  FilterMultiSelect,
  FilterField,
} from "./filters";
import { useNavigate } from "react-router-dom";
import API from "../api";
import { BASE_URL } from "../config";
import { showSuccess, showError, showConfirm } from "../utils/dialog";
import { formatAppDate, formatAppDateTime } from "../utils/appDateTime";
import { set } from "date-fns";
import AddressAutocomplete from "./AddressAutocomplete";
import GoogleAutocompleteInput from "./GoogleAutocompleteInput";
import ContactDetailModal from "./ContactDetailModal";
import PersonnelDetailModal from "./PersonnelDetailModal";
import RichAuditsPanel from "./RichAuditsPanel";
import ActionDropdown from "./ActionDropdown";
import DocumentPreviewModal from "./DocumentPreviewModal";
import DateInput from "./DateInput";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { isValidEmail } from "../utils/email";
import { resolveBackendUrl } from "../utils/accountAvatarUrl";
import {
  countryCodeToName,
  COUNTRY_NAME_TO_CODE,
  COUNTRY_CODE_TO_NAME,
} from "../utils/countries";
import { useAppPermissions } from "../context/PermissionContext";
import ProfilePhotoInput from "./ProfilePhotoInput";

const normalizeCountryToISO2 = (val) => {
  if (!val) return null;

  // Already ISO-2
  if (val.length === 2) return val.toUpperCase();

  // Exact map (United States -> US)
  if (COUNTRY_NAME_TO_CODE[val]) {
    return COUNTRY_NAME_TO_CODE[val];
  }

  // Handle common Google variants
  if (val.toLowerCase().includes("usa")) return "US";
  if (val.toLowerCase().includes("united states")) return "US";
  if (val.toLowerCase().includes("uk")) return "GB";
  if (val.toLowerCase().includes("united kingdom")) return "GB";

  // Handle common country names
  if (val.toLowerCase().includes("canada")) return "CA";
  if (val.toLowerCase().includes("canada")) return "CA";

  return null;
};


// Extract ISO2 country code from a Google-style region value like "Ontario, Canada"
const extractCountryISO2FromRegionValue = (val) => {
  if (!val || typeof val !== "string") return null;
  const parts = val
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2) return null;
  const maybeCountry = parts[parts.length - 1];

  return normalizeCountryToISO2(maybeCountry);
};

// Global helper to determine the acting account id for audit attribution.
// This is intentionally module-scoped so every nested tab/component can use it.
export const getAuthorAccountId = () => {
  try {
    const explicit = localStorage.getItem("accountId");

    if (explicit && !Number.isNaN(parseInt(explicit, 10)))
      return parseInt(explicit, 10);

    const rawUser = localStorage.getItem("user");
    if (rawUser) {
      const u = JSON.parse(rawUser);
      if (u && (u.account_id || u.accountId || u.id))
        return u.account_id || u.accountId || u.id;
    }
  } catch (_) { }
  return undefined;
};

const UnsavedChangesDialog = ({ isOpen, onSaveAndContinue, onDiscardAndContinue, onReview }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[90] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900">You Have Unsaved Changes</h4>
        <p className="text-sm text-gray-600 mt-2">
          What would you like to do with your changes?
        </p>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onSaveAndContinue}
            className="px-4 py-2 bg-sidebar-active text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-semibold"
          >
            SAVE & CONTINUE
          </button>
          <button
            type="button"
            onClick={onDiscardAndContinue}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-semibold"
          >
            DISCARD CHANGES & CONTINUE
          </button>
          <button
            type="button"
            onClick={onReview}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-semibold"
          >
            REVIEW CHANGES
          </button>
        </div>
      </div>
    </div>
  );
};

function TimesheetSignoffEmailChipsInput({ value, onChange }) {
  const [inputValue, setInputValue] = useState("");

  const emailChips = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== "string") return [];
    return value
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
  }, [value]);

  const updateChips = (nextChips) => {
    onChange(nextChips.join(", "));
  };

  const handleAddChip = (text) => {
    const trimmed = text.trim().replace(/,/g, "");
    if (!trimmed) return;
    if (!emailChips.includes(trimmed)) {
      updateChips([...emailChips, trimmed]);
    }
    setInputValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      e.preventDefault();
      handleAddChip(inputValue);
    } else if (e.key === "Backspace" && !inputValue && emailChips.length > 0) {
      updateChips(emailChips.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (inputValue) {
      handleAddChip(inputValue);
    }
  };

  const handleRemoveChip = (indexToRemove) => {
    updateChips(emailChips.filter((_, i) => i !== indexToRemove));
  };

  return (
    <div
      className="w-full min-h-[46px] border border-gray-300 rounded-xl px-3 py-2 bg-white focus-within:ring-2 focus-within:ring-sidebar-active/20 focus-within:border-sidebar-active transition-all flex flex-wrap items-center gap-2 cursor-text"
      onClick={() => {
        const el = document.getElementById("timesheet-chip-input");
        if (el) el.focus();
      }}
    >
      {emailChips.map((chip, idx) => (
        <span
          key={`${chip}-${idx}`}
          className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-800 text-sm font-medium px-3 py-1 rounded-full border border-gray-200 shadow-sm"
        >
          <span>{chip}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveChip(idx);
            }}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full p-0.5 transition-colors leading-none ml-0.5"
            aria-label={`Remove ${chip}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </span>
      ))}

      <input
        id="timesheet-chip-input"
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={emailChips.length === 0 ? "email@example.com" : ""}
        className="flex-1 min-w-[160px] bg-transparent border-none outline-none text-sm text-gray-800 focus:ring-0 p-1"
      />
    </div>
  );
}

const ClientDetailModal = (props) => {
  const {
    client,
    isOpen,
    onClose,
    selectedTab,
    onSaved: onSavedCallback,
    readOnly = false,
    overlayZClass = "z-50",
  } = props;
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [clientData, setClientData] = useState(null);

  const { canWrite } = useAppPermissions();
  const canShowPreferencesTab = canWrite("preferences");

  const tabs = useMemo(() => {
    const all = [
      { id: "profile", label: "Profile", icon: User },
      { id: "notes", label: "Notes", icon: FileText },
      { id: "files", label: "Files", icon: FolderOpen },
      { id: "shift-settings", label: "Shift Settings", icon: Clock },
      { id: "contacts", label: "Contacts", icon: Users },
      { id: "positions", label: "Positions", icon: Users },
      { id: "preferences", label: "Preferences", icon: FileText },
      { id: "locations", label: "Locations", icon: FolderOpen },
      { id: "audits", label: "Audits", icon: FileText },
    ];
    if (!canShowPreferencesTab) {
      return all.filter((t) => t.id !== "preferences");
    }
    return all;
  }, [canShowPreferencesTab]);

  useLayoutEffect(() => {
    if (!canShowPreferencesTab && activeTab === "preferences") {
      setActiveTab("profile");
    }
  }, [canShowPreferencesTab, activeTab]);

  useEffect(() => {
    if (isOpen && client?.id) {
      fetchClientDetails();
    }
  }, [isOpen, client?.id]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab("profile");
  }, [isOpen, client?.id]);

  const fetchClientDetails = async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const response = await API.get(`${BASE_URL}/api/clients/${client.id}/details`);
      setClientData(response.data.data || response.data);
    } catch (error) {
      console.error("Error fetching client details:", error);
    } finally {
      setLoading(false);
    }
  };

  const safeClose = () => {
    if (onClose) onClose();
  };

  const handleSaved = () => {
    fetchClientDetails();
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center ${overlayZClass}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) safeClose();
      }}
    >
      <div className="bg-white w-full max-w-6xl h-[95vh] sm:h-[80vh] rounded-lg shadow-lg overflow-hidden flex flex-col relative">
        <button
          onClick={safeClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10 p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
        >
                              <X className="h-4  w-4" />
        </button>

        <div className="flex items-start justify-between p-4 border-b border-gray-200">
          <div className="text-sm">
            <div className="text-lg font-semibold text-gray-900">
              {client?.name || "Details"}
            </div>
            <div className="text-xs text-gray-500">
              {selectedTab === "CONTACTS"
                ? "View contact details"
                : "Manage client information and settings"}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row flex-1 min-h-0 overflow-hidden">
          <aside className="w-full sm:w-52 border-b sm:border-b-0 sm:border-r border-gray-200 bg-white flex-shrink-0 sm:overflow-y-auto">
            <nav className="flex flex-row sm:flex-col gap-1 overflow-x-auto sm:overflow-visible whitespace-nowrap scrollbar-hide py-2 px-2 sm:px-0">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 sm:w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 text-sm text-left transition-colors rounded ${
                      isActive
                        ? "bg-sidebar-active text-white font-medium"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 flex-shrink-0 ${isActive ? "text-white" : "text-gray-400"}`}
                    />
                    <span className="truncate">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          <div className="flex-1 overflow-y-auto bg-gray-50">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-active"></div>
                  <div className="text-gray-500">Loading...</div>
                </div>
              </div>
            ) : (
              <fieldset
                disabled={!!readOnly && activeTab !== "preferences"}
                className="p-3 sm:p-6 border-0 m-0 min-w-0 w-full"
              >
                <TabContent
                  activeTab={activeTab}
                  clientData={clientData}
                  client={client}
                  onRefresh={fetchClientDetails}
                  selectedTab={selectedTab}
                  onSaved={onSavedCallback || handleSaved}
                  onChangeTab={setActiveTab}
                  canShowPreferencesTab={canShowPreferencesTab}
                />
              </fieldset>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TabContent = ({
  activeTab,
  clientData,
  client,
  onRefresh,
  selectedTab,
  onSaved,
  onChangeTab,
  canShowPreferencesTab = false,
}) => {
  if (selectedTab === "CONTACTS") {
    switch (activeTab) {
      case "profile":
        return (
          <ProfileTab
            clientData={clientData}
            client={client}
            onRefresh={onRefresh}
            onSaved={onSaved}
          />
        );
      case "clients":
        return <div>Related clients</div>;
      case "audits":
        return (
          <AuditsTab
            clientData={clientData}
            client={client}
            onRefresh={onRefresh}
          />
        );
      default:
        return <div>Select a tab</div>;
    }
  }

  // For CLIENTS tab (default)
  switch (activeTab) {
    case "profile":
      return (
        <ProfileTab
          clientData={clientData}
          client={client}
          onRefresh={onRefresh}
          onSaved={onSaved}
        />
      );
    case "notes":
      return (
        <NotesTab
          clientData={clientData}
          client={client}
          onRefresh={onRefresh}
        />
      );
    case "files":
      return (
        <FilesTab
          clientData={clientData}
          client={client}
          onRefresh={onRefresh}
        />
      );
    case "shift-settings":
      return (
        <ShiftSettingsTab
          clientData={clientData}
          client={client}
          onRefresh={onRefresh}
        />
      );
    case "contacts":
      return (
        <ContactsTab
          clientData={clientData}
          client={client}
          onRefresh={onRefresh}
        />
      );
    case "positions":
      return (
        <PositionsTab
          clientData={clientData}
          client={client}
          onRefresh={onRefresh}
        />
      );
    case "preferences":
      if (!canShowPreferencesTab) {
        return (
          <ProfileTab
            clientData={clientData}
            client={client}
            onRefresh={onRefresh}
            onSaved={onSaved}
          />
        );
      }
      return (
        <PreferencesTab
          clientData={clientData}
          client={client}
          onRefresh={onRefresh}
        />
      );
    case "locations":
      return (
        <LocationsTab
          clientData={clientData}
          client={client}
          onRefresh={onRefresh}
          onChangeTab={onChangeTab}
        />
      );
    case "audits":
      return (
        <AuditsTab
          clientData={clientData}
          client={client}
          onRefresh={onRefresh}
        />
      );
    default:
      return <div>Select a tab</div>;
  }
};

// Profile Tab Component

const ProfileTab = ({
  clientData,
  client,
  onRefresh,
  selectedTab,
  onSaved,
}) => {
  // Different initial state based on whether it's a contact or client
  const initialState =
    selectedTab === "CONTACTS"
      ? {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        role: "",
        clientName: "",
        status: "active",
        profilePicture: "",
      }
      : {
        name: "",
        status: "active",
        employmentOffice: "",
        parentClient: "",
        divisions: "",
        email: "",
        phoneNumber: "",
        fax: "",
        defaultContact: "",
        defaultSupervisor: "",
        billingAddress: "",
        profilePicture: "",
      };

  const [formData, setFormData] = useState(initialState);
  const [originalProfile, setOriginalProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  // Dropdown option lists and selected IDs

  const [employmentOfficeOptions, setEmploymentOfficeOptions] = useState([]); // {id, name}
  const [divisionOptions, setDivisionOptions] = useState([]); // {id, name}

  const [parentClientOptions, setParentClientOptions] = useState([]); // {id, name}

  const [contactOptions, setContactOptions] = useState([]); // {id, first_name, last_name}

  const [supervisorOptions, setSupervisorOptions] = useState([]); // {id, first_name, last_name}

  const [selectedDivisionIds, setSelectedDivisionIds] = useState([]);
  const [originalDivisionIds, setOriginalDivisionIds] = useState([]);
  const [selectedEmploymentOfficeId, setSelectedEmploymentOfficeId] = useState(null);

  const [parentClientId, setParentClientId] = useState(null);

  const [defaultContactId, setDefaultContactId] = useState(null);

  const [defaultSupervisorId, setDefaultSupervisorId] = useState(null);

  // Ref for division dropdown to handle click outside
  const divisionDropdownRef = useRef(null);

  // Close division dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (divisionDropdownRef.current && !divisionDropdownRef.current.contains(event.target)) {
        const dropdown = document.getElementById('divisionDropdown');
        if (dropdown && !dropdown.classList.contains('hidden')) {
          dropdown.classList.add('hidden');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (clientData) {
      // Some APIs return the profile directly, others nest it under `profile`.
      const prof = clientData.profile || clientData;

      setFormData({
        name: prof.name != null ? prof.name : "",
        status: prof.state != null ? prof.state : "active",
        employmentOffice: prof.employment_office != null ? prof.employment_office : "",
        parentClient: prof.parent_client_name != null ? prof.parent_client_name : "",
        divisions: prof.divisions != null ? prof.divisions : "",
        email: prof.email != null ? prof.email : "",
        phoneNumber: prof.phone != null ? prof.phone : "",
        fax: prof.fax != null ? prof.fax : "",
        defaultContact: prof.default_contact_object_name != null ? prof.default_contact_object_name : "",
        defaultSupervisor: prof.default_supervisor_object_name != null ? prof.default_supervisor_object_name : "",
        billingAddress: prof.billing_address != null ? prof.billing_address : "",
        serviceAddress: prof.service_address != null ? prof.service_address : "",
        profilePicture: prof.profile_picture != null ? prof.profile_picture : "",
      });

      // Keep a copy of the original profile values so we only send changed fields on save
      setOriginalProfile({
        name: prof.name ?? null,
        status: prof.state ?? null,
        parentClientId: prof.parent_client_id ?? null,
        parentClient: prof.parent_client_name ?? null,
        email: prof.email ?? null,
        phoneNumber: prof.phone ?? null,
        fax: prof.fax ?? null,
        defaultContactId: prof.default_contact_account_id ?? null,
        defaultSupervisorId: prof.default_supervisor_account_id ?? null,
        employmentOfficeId: prof.employment_office_id ?? null,
        billingAddress: prof.billing_address ?? null,
        serviceAddress: prof.service_address ?? null,
      });

      // Preselect by IDs from profile when available
      if (prof.parent_client_id != null) setParentClientId(prof.parent_client_id);
      if (prof.default_contact_account_id != null)
        setDefaultContactId(prof.default_contact_account_id);
      if (prof.default_supervisor_account_id != null)
        setDefaultSupervisorId(prof.default_supervisor_account_id);
      if (prof.employment_office_id != null || prof.office_id != null)
        setSelectedEmploymentOfficeId(
          prof.employment_office_id != null
            ? prof.employment_office_id
            : prof.office_id
        );
    }
  }, [clientData]);

  // Load dropdown options and current divisions for this client

  useEffect(() => {
    const loadOptions = async () => {
      if (!client?.id) return;

      try {
        const [
          employmentOfficesRes,
          divisionsRes,
          parentsRes,
          contactsRes,
          supervisorsRes,
          currentDivsRes,
        ] = await Promise.all([
          API.get(`${BASE_URL}/api/admin/offices`, {
            params: {
              page: 1,
              limit: 1000,
              search: "",
              showDeleted: false,
              sort: "name",
              dir: "asc",
            },
          }),
          API.get(`${BASE_URL}/api/clients/divisions`),

          API.get(`${BASE_URL}/api/clients`, { params: { all: 1 } }),

          API.get(`${BASE_URL}/api/clients/${client.id}/contacts`),

          API.get(`${BASE_URL}/api/clients/${client.id}/supervisors`),

          API.get(`${BASE_URL}/api/clients/${client.id}/divisions`),
        ]);

        setEmploymentOfficeOptions(
          (employmentOfficesRes.data?.data || []).map((office) => ({
            id: office.id,
            name: office.name,
          }))
        );
        setDivisionOptions(divisionsRes.data?.data || []);

        setParentClientOptions(parentsRes.data?.data || []);

        setContactOptions(contactsRes.data?.data || []);

        setSupervisorOptions(supervisorsRes.data?.data || []);

        const currentDivIds = (currentDivsRes.data?.data || [])
          .map((d) => Number(d.id))
          .filter((id) => Number.isFinite(id));
        setSelectedDivisionIds(currentDivIds);
        setOriginalDivisionIds(currentDivIds);
      } catch (err) {
        console.error("Failed to load profile dropdown options:", err);
      }
    };

    loadOptions();
  }, [client?.id]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !client?.id) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`${BASE_URL}/api/clients/${client.id}/avatar`, {
        method: "POST",
        body: formData,
      });

      const json = await res.json();
      if (json.success && json.url) {
        setFormData((prev) => ({ ...prev, profilePicture: json.url }));
        console.log("Client profile picture updated successfully");
      } else {
        console.error("Client profile picture upload failed:", json);
      }
    } catch (err) {
      console.error("Client avatar upload error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Helper to validate address (works with both string and object)
      const isAddressValid = (addr) => {
        if (!addr) return false;
        if (typeof addr === 'string') return addr.trim() !== '';
        if (typeof addr === 'object') {
          // At least street should be present
          return !!(addr.street && addr.street.trim());
        }
        return false;
      };

      // Validate required fields
      const missingFields = [];
      if (!formData.status || !String(formData.status).trim()) {
        missingFields.push("Status");
      }
      if (!formData.name || !String(formData.name).trim()) {
        missingFields.push("Name");
      }
      if (!selectedEmploymentOfficeId) {
        missingFields.push("Employment Office");
      }
      if (!formData.phoneNumber || !String(formData.phoneNumber).trim()) {
        missingFields.push("Phone Number");
      }

      if (missingFields.length > 0) {
        showError(
          `Please fill in all required fields: ${missingFields.join(", ")}`,
          "Validation Error"
        );
        return;
      }

      // Validate billing address
      if (!isAddressValid(formData.billingAddress)) {
        showError("Billing Address is required");
        return;
      }

      // Build payload with only changed fields compared to originalProfile
      const profileData = {};

      // Helper to normalize strings for comparison (treat empty string as null when original was null)
      const norm = (v) => (v === undefined ? undefined : v === "" ? null : v);

      if (!originalProfile) {
        // Fallback: send only non-empty values (defensive)
        if (formData.name) profileData.name = formData.name;
        if (formData.email) profileData.email = formData.email;
        if (formData.phoneNumber) profileData.phone = formData.phoneNumber;
        if (formData.fax) profileData.fax = formData.fax;
        if (formData.serviceAddress)
          profileData.serviceAddress = formData.serviceAddress; // Send as-is (object or string)
        if (formData.billingAddress)
          profileData.billingAddress = formData.billingAddress; // Send as-is (object or string)
        if (defaultContactId) profileData.defaultContactId = defaultContactId;
        if (defaultSupervisorId)
          profileData.defaultSupervisorId = defaultSupervisorId;
        if (selectedEmploymentOfficeId)
          profileData.employmentOfficeId = selectedEmploymentOfficeId;
        if (Array.isArray(selectedDivisionIds) && selectedDivisionIds.length)
          profileData.divisionsIds = selectedDivisionIds;
      } else {
        if (formData.name !== originalProfile.name)
          profileData.name = formData.name || null;
        if (
          (clientData.profile?.state || null) !== formData.status &&
          formData.status !== undefined
        )
          profileData.status = formData.status;
        if (formData.email !== originalProfile.email)
          profileData.email = formData.email || null;
        if (formData.phoneNumber !== originalProfile.phoneNumber)
          profileData.phone = formData.phoneNumber || null;
        if (formData.fax !== originalProfile.fax)
          profileData.fax = formData.fax || null;

        // Addresses: send as-is (object or string), backend will handle both formats
        // Compare by converting both to strings for comparison
        const addressToCompareString = (addr) => {
          if (!addr) return "";
          if (typeof addr === "string") {
            const raw = addr.trim();
            const dashIdx = raw.indexOf(" - ");
            return dashIdx >= 0 ? raw.slice(dashIdx + 3).trim() : raw;
          }
          if (typeof addr === "object") {
            return [
              addr.street,
              addr.city,
              addr.region || addr.state || addr.province,
              addr.zip || addr.postal_code,
              addr.country,
            ]
              .filter(Boolean)
              .join(", ");
          }
          return "";
        };

        const origService = addressToCompareString(originalProfile.serviceAddress);
        const currService = addressToCompareString(formData.serviceAddress);
        if (currService !== origService)
          profileData.serviceAddress = formData.serviceAddress || null;

        // Billing is required — always send so backend can geocode on every save.
        profileData.billingAddress = formData.billingAddress || null;

        if (defaultContactId !== originalProfile.defaultContactId)
          profileData.defaultContactId = defaultContactId || null;
        if (defaultSupervisorId !== originalProfile.defaultSupervisorId)
          profileData.defaultSupervisorId = defaultSupervisorId || null;
        if (selectedEmploymentOfficeId !== originalProfile.employmentOfficeId)
          profileData.employmentOfficeId = selectedEmploymentOfficeId || null;

        // Divisions: compare as sets
        const origDivs = (originalDivisionIds || [])
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id))
          .sort((a, b) => a - b);
        const newDivs = (selectedDivisionIds || [])
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id))
          .sort((a, b) => a - b);
        const divsChanged = JSON.stringify(origDivs) !== JSON.stringify(newDivs);
        if (divsChanged) profileData.divisionsIds = newDivs;
      }

      // Attach author info so server can attribute the audit (uses header fallback)
      const token = localStorage.getItem("token");
      const authorId = getAuthorAccountId();
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (authorId) headers["x-author-account-id"] = `${authorId}`;

      // If parent changed, update parent separately using dedicated endpoint to avoid accidental
      // creation of new client records (frontend sometimes calls create-subclient elsewhere).
      let didSave = false;
      const currentParent =
        (clientData && (clientData.profile || clientData).parent_client_id) ||
        null;
      const newParent = parentClientId || null;
      if ((currentParent || null) !== (newParent || null)) {
        try {
          await API.put(
            `${BASE_URL}/api/clients/${client.id}/parent`,
            { parentClientId: newParent },
            { headers }
          );
          didSave = true;
        } catch (err) {
          console.error("Failed to update parent client separately:", err);
          // continue  we'll still attempt main profile save for other fields
        }
      }

      // Remove parentClientId from profile payload to avoid duplicate handling on server
      const profilePayload = Object.assign({}, profileData);
      delete profilePayload.parentClientId;

      if (Object.keys(profilePayload).length > 0) {
        try {
          await API.put(
            `${BASE_URL}/api/clients/${client.id}/profile`,
            profilePayload,
            { headers }
          );
          didSave = true;
        } catch (err) {
          console.error("Failed to save profile payload:", err);
        }
      } else {
        if (!didSave)
          console.log("No profile fields changed; skipping profile save.");
      }

      if (didSave) {
        try {
          if (typeof onRefresh === "function") onRefresh();
        } catch (_) { }
        try {
          if (typeof onSaved === "function") onSaved();
        } catch (e) {
          console.warn("onSaved callback failed", e);
        }
      }
    } catch (error) {
      console.error("Failed to save profile:", error);
    }
  };

  // not sure
  const getAuthorAccountId = () => {
    try {
      const explicit = localStorage.getItem("accountId");

      if (explicit && !Number.isNaN(parseInt(explicit, 10)))
        return parseInt(explicit, 10);

      const rawUser = localStorage.getItem("user");

      if (rawUser) {
        const u = JSON.parse(rawUser);

        if (u && (u.account_id || u.accountId || u.id))
          return u.account_id || u.accountId || u.id;
      }
    } catch (_) { }

    return undefined;
  };

  const activeDefaultPeopleOptions = (() => {
    const source = Array.isArray(supervisorOptions) ? supervisorOptions : [];
    const byId = new Map();
    source.forEach((opt) => {
      const id = Number(opt?.id || opt?.contactId);
      if (!Number.isFinite(id)) return;
      const state = String(opt?.state || "").toLowerCase();
      if (state === "deleted") return;
      const name =
        opt?.name ||
        [opt?.first_name, opt?.last_name].filter(Boolean).join(" ").trim() ||
        `#${id}`;
      if (!byId.has(id)) byId.set(id, { id, name });
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  })();

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <User className="h-5 w-5 text-sidebar-active" />
          {selectedTab === "CONTACTS"
            ? "Contact Information"
            : "Client Information"}
        </h3>

        {/* Profile Picture */}
        <div className="space-y-3 mb-8">
          <label className="text-sm font-medium text-gray-700">
            Profile Picture
          </label>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-red-500 to-gray-800 border-4 border-white shadow-lg flex items-center justify-center">
                {formData.profilePicture ? (
                  <img
                    src={resolveBackendUrl(formData.profilePicture)}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="text-white text-xl font-bold">
                    {((formData.name || "").charAt(0) || "C").toUpperCase()}
                  </div>
                )}
              </div>
              <button
                onClick={() =>
                  document.querySelector(".client-avatar-input").click()
                }
                className="absolute -bottom-1 -right-1 w-8 h-8 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </button>
            </div>

            <ProfilePhotoInput
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="client-avatar-input hidden"
            />
          </div>
        </div>

        {/* Form Fields */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Status <span className="text-red-500">*</span>
            </label>

            <select
              value={formData.status}
              onChange={(e) => handleInputChange("status", e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-red-200 focus:border-red-500 transition-colors"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="donotservice">Do not service</option>
              <option value="prospect">Prospect</option>
              <option value="closed">Closed</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Name <span className="text-red-500">*</span>
            </label>

            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-red-200 focus:border-red-500 transition-colors"
              placeholder="Enter client name"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Employment Office <span className="text-red-500">*</span>
            </label>

            <select
              value={selectedEmploymentOfficeId ? String(selectedEmploymentOfficeId) : ""}
              onChange={(e) =>
                setSelectedEmploymentOfficeId(
                  e.target.value ? parseInt(e.target.value, 10) : null
                )
              }
              className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-red-200 focus:border-red-500 transition-colors"
            >
              <option value="">Select employment office</option>
              {(employmentOfficeOptions || []).map((opt) => {
                const id = opt.division_id || opt.id;
                return (
                  <option key={id} value={id}>
                    {opt.name}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Divisions
            </label>

            <div className="relative" ref={divisionDropdownRef}>
              <div className="w-full border border-gray-300 rounded-xl px-3 py-2 bg-white min-h-[40px] flex flex-wrap items-center gap-2 focus-within:ring-2 focus-within:ring-red-200">
                {selectedDivisionIds.map((divId) => {
                  const division = divisionOptions.find(
                    (d) => Number(d.division_id || d.id) === Number(divId)
                  );
                  if (!division) return null;

                  return (
                    <span
                      key={divId}
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-red-100 text-red-700 rounded-full text-sm font-medium"
                    >
                      {division.name}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedDivisionIds((prev) =>
                            prev.filter((id) => Number(id) !== Number(divId))
                          );
                        }}
                        className="hover:bg-sidebar-active/20 rounded-full p-0.5"
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </span>
                  );
                })}

                <input
                  type="text"
                  placeholder={selectedDivisionIds.length === 0 ? "Select divisions..." : ""}
                  readOnly
                  onClick={() => {
                    const dropdown = document.getElementById('divisionDropdown');
                    if (dropdown) {
                      dropdown.classList.toggle('hidden');
                    }
                  }}
                  className="flex-1 min-w-[120px] outline-none bg-transparent cursor-pointer"
                />
              </div>

              <div
                id="divisionDropdown"
                className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-xl shadow-lg z-10 hidden max-h-64 overflow-y-auto"
              >
                {(divisionOptions || []).map((opt) => {
                  const id = Number(opt.division_id || opt.id);
                  const checked = selectedDivisionIds.some(
                    (selectedId) => Number(selectedId) === id
                  );

                  return (
                    <label
                      key={id}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setSelectedDivisionIds((prev) =>
                            checked
                              ? prev.filter((v) => Number(v) !== id)
                              : [...prev, id]
                          );
                        }}
                        className="w-4 h-4 text-sidebar-active rounded border-gray-300 focus:ring-sidebar-active"
                      />
                      <span className="text-sm text-gray-700">{opt.name}</span>
                    </label>
                  );
                })}

                {(!divisionOptions || divisionOptions.length === 0) && (
                  <div className="px-4 py-3 text-sm text-gray-500">No divisions found</div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Parent Client
            </label>

            <select
              value={parentClientId ? String(parentClientId) : ""}
              onChange={(e) =>
                setParentClientId(
                  e.target.value ? parseInt(e.target.value, 10) : null
                )
              }
              className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-red-200 focus:border-red-500 transition-colors"
            >
              <option value="">Select a parent client</option>

              {(parentClientOptions || []).map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>

          {/* Divisions handled by multi-select above */}

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Email
            </label>

            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-red-200 focus:border-red-500 transition-colors"
              placeholder="Enter email address"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Phone Number <span className="text-red-500">*</span>
            </label>

            <PhoneInput
              international
              defaultCountry="CA"
              value={formData.phoneNumber}
              onChange={(value) => handleInputChange("phoneNumber", value)}
              className="w-full border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-200 focus:border-red-500 transition-colors"
              numberInputProps={{
                className: "w-full border-0 outline-none px-3 py-2 rounded-xl"
              }}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Fax
            </label>

            <PhoneInput
              international
              defaultCountry="CA"
              value={formData.fax}
              onChange={(value) => handleInputChange("fax", value)}
              className="w-full border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-200 focus:border-red-500 transition-colors"
              numberInputProps={{
                className: "w-full border-0 outline-none px-3 py-2 rounded-xl"
              }}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Default Contact
            </label>

            <select
              value={defaultContactId || ""}
              onChange={(e) =>
                setDefaultContactId(
                  e.target.value ? parseInt(e.target.value, 10) : null
                )
              }
              className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-red-200 focus:border-red-500 transition-colors"
            >
              <option value="">Select default contact</option>
              {activeDefaultPeopleOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Default Supervisor
            </label>

            <select
              value={defaultSupervisorId || ""}
              onChange={(e) =>
                setDefaultSupervisorId(
                  e.target.value ? parseInt(e.target.value, 10) : null
                )
              }
              className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-red-200 focus:border-red-500 transition-colors"
            >
              <option value="">Select default supervisor</option>
              {activeDefaultPeopleOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2 space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Billing Address <span className="text-red-500">*</span>
            </label>

            <AddressAutocomplete
              value={formData.billingAddress}
              onChange={(v) => {
                console.log("Billing Address onChange:", v);
                handleInputChange("billingAddress", v);
              }}
              onManualSave={(data) => {
                console.log("Billing Address onManualSave:", data);
                // The address with name is already set via onChange
                // No additional action needed here
              }}
            />
          </div>
        </div>

        <div className="flex justify-end pt-6 border-t border-gray-200 mt-8">
          <button
            onClick={handleSave}
            className="px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// Notes Tab Component

const NotesTab = ({ clientData, client, onRefresh }) => {
  const [notes, setNotes] = useState([]);

  const [showCreateModal, setShowCreateModal] = useState(false);

  const [newNote, setNewNote] = useState("");

  const [loading, setLoading] = useState(false);
  const [relatesToText, setRelatesToText] = useState("");
  const [relatesOptions, setRelatesOptions] = useState([]);
  const [selectedRelatesList, setSelectedRelatesList] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [editForm, setEditForm] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState("");
  const notesFilterPanel = useEmbeddedFilterLayout();
  const [pageSize, setPageSize] = useState(100);

  const [filterCreatedBy, setFilterCreatedBy] = useState([]);
  const [filterCreatedFrom, setFilterCreatedFrom] = useState("");
  const [filterCreatedTo, setFilterCreatedTo] = useState("");
  const [filterRelatedTo, setFilterRelatedTo] = useState([]);
  const [filterSortBy, setFilterSortBy] = useState("created");
  const [filterSortDirection, setFilterSortDirection] = useState("desc");
  const [showDeleted, setShowDeleted] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [editRelatesText, setEditRelatesText] = useState("");
  const [editRelatesOptions, setEditRelatesOptions] = useState([]);
  const [editSelectedRelatesList, setEditSelectedRelatesList] = useState([]);
  const [unsavedPromptOpen, setUnsavedPromptOpen] = useState(false);
  const [unsavedSaveAction, setUnsavedSaveAction] = useState(null);
  const [unsavedDiscardAction, setUnsavedDiscardAction] = useState(null);

  // { id, name, type }
  const relatesTimer = useRef(null);
  const relatesRef = useRef(null);
  const editRelatesRef = useRef(null);

  // search helper for the relates autocomplete (localized to NotesTab scope)
  async function searchRelates(q) {
    if (!q || q.trim().length < 1) {
      setRelatesOptions([]);
      return;
    }
    try {
      const resp = await fetch(
        `${BASE_URL}/api/personnel/accounts/search?q=${encodeURIComponent(
          q
        )}&limit=20`,
        { credentials: "include" }
      );
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setRelatesOptions([]);
        return;
      }
      const list = j.data || j || [];
      const opts = Array.isArray(list)
        ? list.map((a) => ({
          id: a.account_id || a.id,
          name: a.name || `${a.first_name || ""} ${a.last_name || ""}`.trim(),
          type: a.type || a.account_type || "account",
        }))
        : [];
      setRelatesOptions(opts);
    } catch (err) {
      console.error("Relates search failed", err);
      setRelatesOptions([]);
    }
  }

  useEffect(() => {
    fetchNotes();
  }, [client?.id, showDeleted]);

  const fetchNotes = async () => {
    if (!client?.id) return;

    setLoading(true);

    try {
      console.log("Fetching notes for client ID:", client.id);

      const response = await API.get(
        `${BASE_URL}/api/clients/${client.id}/notes`,
        { params: { showDeleted: showDeleted ? 1 : 0 } }
      );

      console.log("Notes response:", response.data);

      const rawData = response.data.data || [];

      setNotes(rawData);
    } catch (error) {
      console.error("Failed to fetch notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const normalizeText = (text) => {
    return String(text ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  };

  const getNoteTime = (item) => {
    const raw = item?.create_date || item?.created_at || item?.date || 0;
    const time = new Date(raw).getTime();
    return Number.isNaN(time) ? 0 : time;
  };

  const relationAliases = {
    fieldstaff: ["fieldstaff", "tempstaff", "staff"],
    officestaff: ["officestaff", "office", "staff"],
    candidate: ["candidate"],
    outsourcedstaff: ["outsourcedstaff", "outsourcestaff", "outsource", "staff"],
    contactclient: ["contactclient", "clientcontact", "contact", "client"],
    client: ["client"],
    account: ["account"],
  };

  const normalizeRelationTypeLabel = (type) => {
    const raw = String(type || "").trim().toLowerCase();
    if (!raw) return "";
    const labelMap = {
      fieldstaff: "Field Staff",
      tempstaff: "Temp Staff",
      officestaff: "Office Staff",
      outsourcedstaff: "Outsourced Staff",
      contactclient: "Client Contact",
      account: "Account",
      client: "Client",
      contact: "Contact",
      staff: "Staff",
      candidate: "Candidate",
    };
    return labelMap[raw] || raw.replace(/[_-]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  };

  const getRelatedBadges = (noteItem) => {
    const badges = [];
    const seen = new Set();
    const pushBadge = (name, type) => {
      const cleanedName = String(name || "").trim();
      if (!cleanedName) return;
      const cleanedType = String(type || "").trim();
      const key = `${cleanedName.toLowerCase()}|${cleanedType.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      badges.push({ name: cleanedName, type: cleanedType });
    };

    // Keep created-for/owner person first, matching personnel notes behavior.
    pushBadge(noteItem?.object_owner, noteItem?.object_owner_type);

    if (Array.isArray(noteItem?.relates)) {
      noteItem.relates.forEach((rel) => pushBadge(rel?.label, rel?.type));
    } else if (noteItem?.relates_to) {
      pushBadge(noteItem.relates_to, noteItem?.relates_type);
    }

    return badges;
  };

  const applyFilters = () => {
    let result = [...(notes || [])];
    const createdByList = Array.isArray(filterCreatedBy) ? filterCreatedBy : [];
    const relatedToList = Array.isArray(filterRelatedTo) ? filterRelatedTo : [];

    if (searchTerm && searchTerm.trim()) {
      const q = normalizeText(searchTerm);
      result = result.filter((n) => {
        const noteText = normalizeText(n.note);
        const createdByText = normalizeText(n.created_by);
        const relatesText = normalizeText(n.relates_to);

        return (
          noteText.includes(q) ||
          createdByText.includes(q) ||
          relatesText.includes(q)
        );
      });
    }

    // 2) FILTER: CREATED BY (username)
    if (createdByList.length > 0) {
      const lowerNames = createdByList.map((v) => String(v).trim().toLowerCase());
      result = result.filter((n) => {
        const createdText = String(n.created_by || "").toLowerCase();
        return lowerNames.some((cb) => cb && createdText.includes(cb));
      });
    }

    if (filterCreatedFrom || filterCreatedTo) {
      const fromTime = filterCreatedFrom ? new Date(filterCreatedFrom).getTime() : null;
      const toTime = filterCreatedTo
        ? new Date(filterCreatedTo).getTime() + 24 * 60 * 60 * 1000 - 1
        : null;

      result = result.filter((n) => {
        const dateStr = n.create_date || n.created_at || n.date;
        if (!dateStr) return false;
        const t = new Date(dateStr).getTime();
        if (Number.isNaN(t)) return false;
        if (fromTime && t < fromTime) return false;
        if (toTime && t > toTime) return false;
        return true;
      });
    }

    // 4) FILTER: RELATED TO (field staff / office staff / etc)
    if (relatedToList.length > 0) {
      result = result.filter((n) => {
        const type = normalizeText(n.relates_type);
        const label = normalizeText(n.relates_to);
        return relatedToList.some((relatedValue) => {
          const target = normalizeText(relatedValue);
          const aliases = relationAliases[target] || [target];
          return aliases.some(
            (token) =>
              type === token ||
              type.includes(token) ||
              label.includes(token)
          );
        });
      });
    }

    if (showDeleted) {
      result = result.filter((n) => (n.state || "").toLowerCase() === "deleted");
    } else {
      result = result.filter((n) => (n.state || "active").toLowerCase() !== "deleted");
    }

    if (filterSortBy === "created") {
      result.sort((a, b) => {
        const aTime = getNoteTime(a);
        const bTime = getNoteTime(b);
        if (filterSortDirection === "asc") return aTime - bTime;
        return bTime - aTime;
      });
    }

    setFilteredNotes(result);
  };

  useEffect(() => {
    applyFilters();
  }, [
    notes,
    searchTerm,
    filterCreatedBy,
    filterCreatedFrom,
    filterCreatedTo,
    filterRelatedTo,
    filterSortBy,
    filterSortDirection,
    showDeleted,
  ]);

  const addUniqueRelation = (list, relation) => {
    if (!relation?.id || !relation?.type) return list;
    const exists = list.some(
      (item) =>
        Number(item.id) === Number(relation.id) &&
        String(item.type) === String(relation.type)
    );
    if (exists) return list;
    return [...list, relation];
  };

  const removeRelation = (list, relation) =>
    list.filter(
      (item) =>
        !(
          Number(item.id) === Number(relation.id) &&
          String(item.type) === String(relation.type)
        )
    );

  const handleCreateNote = async () => {
    try {
      if (!newNote.trim()) {
        showError("Note is required");
        return;
      }

      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");

      const headers = { "Content-Type": "application/json" };

      if (token) headers["Authorization"] = `Bearer ${token}`;
      const authorId = getAuthorAccountId();
      if (authorId) headers["x-author-account-id"] = `${authorId}`;

      const payload = { note: newNote, state: "active" };
      if (selectedRelatesList.length > 0) {
        payload.relates = selectedRelatesList.map((rel) => ({
          type: rel.type || "account",
          object_id: rel.id,
        }));
      } else if (relatesToText && relatesToText.trim()) {
        payload.relates_to_text = relatesToText.trim();
      }

      const res = await fetch(`${BASE_URL}/api/clients/${client.id}/notes`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const j = await res.json().catch(() => ({}));

      if (!res.ok || !j.success)
        throw new Error(j.message || "Failed to add note");

      setNewNote("");
      setRelatesToText("");
      setSelectedRelatesList([]);
      setShowCreateModal(false);
      showSuccess("Note created");
      fetchNotes();
    } catch (error) {
      console.error("Failed to create note:", error);
      showError(error.message || "Failed to create note");
    }
  };

  const handleDeleteNote = async (noteId) => {
    const confirmed = await showConfirm("Are you sure you want to delete this note?", "Confirm");
    if (!confirmed) return;
    try {
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");

      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(
        `${BASE_URL}/api/clients/${client.id}/notes/${noteId}`,
        {
          method: "DELETE",
          headers,
          credentials: "include",
        }
      );

      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.message || "Failed to delete note");
      }

      await fetchNotes();

      showSuccess("Note deleted successfully!");
    } catch (error) {
      console.error("Failed to delete note:", error);
      showError("Failed to delete note. Please try again.");
    }
  };

  async function searchRelatesForEdit(q) {
    if (!q || q.trim().length < 1) {
      setEditRelatesOptions([]);
      return;
    }
    try {
      const resp = await fetch(
        `${BASE_URL}/api/personnel/accounts/search?q=${encodeURIComponent(
          q
        )}&limit=20`,
        { credentials: "include" }
      );
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setEditRelatesOptions([]);
        return;
      }
      const list = j.data || j || [];
      const opts = Array.isArray(list)
        ? list.map((a) => ({
          id: a.account_id || a.id,
          name: a.name || `${a.first_name || ""} ${a.last_name || ""}`.trim(),
          type: a.type || a.account_type || "account",
        }))
        : [];
      setEditRelatesOptions(opts);
    } catch (err) {
      console.error("Relates search failed (edit)", err);
      setEditRelatesOptions([]);
    }
  }

  const getInitialEditRelationsFromNote = (noteItem) => {
    return Array.isArray(noteItem?.relates)
      ? noteItem.relates
        .filter((r) => r?.object_id && r?.type)
        .map((r) => ({
          id: Number(r.object_id),
          name: r.label || `#${r.object_id}`,
          type: r.type,
        }))
      : noteItem?.relates_object_id && noteItem?.relates_type
        ? [{
          id: Number(noteItem.relates_object_id),
          name: noteItem.relates_to || `#${noteItem.relates_object_id}`,
          type: noteItem.relates_type,
        }]
        : [];
  };

  const handleRestoreNote = async (noteId) => {
    const confirmed = await showConfirm(
      "Are you sure you want to restore this note?",
      "Confirm"
    );
    if (!confirmed) return;
    try {
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(
        `${BASE_URL}/api/clients/${client.id}/notes/${noteId}/restore`,
        {
          method: "PUT",
          headers,
          credentials: "include",
        }
      );

      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.message || "Failed to restore note");
      }

      await fetchNotes();
      showSuccess("Note restored successfully!");
    } catch (error) {
      console.error("Failed to restore note:", error);
      showError(error.message || "Failed to restore note. Please try again.");
    }
  };

  const normalizeRelates = (list = []) =>
    list
      .map((r) => `${String(r?.type || "")}:${String(r?.id || "")}`)
      .sort()
      .join("|");

  const isCreateNoteDirty =
    String(newNote || "").trim() !== "" ||
    String(relatesToText || "").trim() !== "" ||
    (selectedRelatesList || []).length > 0;

  const isEditNoteDirty = (() => {
    if (!showEditModal || !editingNote) return false;
    const initialNote = String(editingNote?.note || "").trim();
    const currentNote = String(editNoteText || "").trim();
    const initialRelates = normalizeRelates(getInitialEditRelationsFromNote(editingNote));
    const currentRelates = normalizeRelates(editSelectedRelatesList || []);
    return currentNote !== initialNote || currentRelates !== initialRelates;
  })();

  const openUnsavedPrompt = (onSave, onDiscard) => {
    setUnsavedSaveAction(() => onSave);
    setUnsavedDiscardAction(() => onDiscard);
    setUnsavedPromptOpen(true);
  };

  const closeUnsavedPrompt = () => {
    setUnsavedPromptOpen(false);
    setUnsavedSaveAction(null);
    setUnsavedDiscardAction(null);
  };

  const requestCloseCreateNoteModal = () => {
    if (!isCreateNoteDirty) {
      setShowCreateModal(false);
      return;
    }
    openUnsavedPrompt(
      async () => {
        await handleCreateNote();
      },
      () => {
        setShowCreateModal(false);
        setNewNote("");
        setRelatesToText("");
        setSelectedRelatesList([]);
      }
    );
  };

  const requestCloseEditNoteModal = () => {
    if (!isEditNoteDirty) {
      setShowEditModal(false);
      setEditingNote(null);
      return;
    }
    openUnsavedPrompt(
      async () => {
        await handleSaveEditedNote();
      },
      () => {
        setShowEditModal(false);
        setEditingNote(null);
      }
    );
  };

  const handleOpenEditNote = (noteItem) => {
    setEditingNote(noteItem);
    setEditNoteText(noteItem?.note || "");
    setEditRelatesText("");
    const relationList = getInitialEditRelationsFromNote(noteItem);
    setEditSelectedRelatesList(relationList);
    setEditRelatesOptions([]);
    setShowEditModal(true);
  };

  const handleSaveEditedNote = async () => {
    if (!editingNote?.note_id) return;
    if (!editNoteText || !editNoteText.trim()) {
      showError("Note is required");
      return;
    }
    try {
      const token =
        localStorage.getItem("authToken") || localStorage.getItem("token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const authorId = getAuthorAccountId();
      if (authorId) headers["x-author-account-id"] = `${authorId}`;

      const payload = {
        note: editNoteText.trim(),
      };

      payload.relates = editSelectedRelatesList.map((rel) => ({
        type: rel.type,
        object_id: rel.id,
      }));

      const res = await fetch(
        `${BASE_URL}/api/clients/${client.id}/notes/${editingNote.note_id}`,
        {
          method: "PUT",
          headers,
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.success === false) {
        throw new Error(j.message || "Failed to update note");
      }
      showSuccess("Note updated");
      setShowEditModal(false);
      setEditingNote(null);
      await fetchNotes();
    } catch (error) {
      console.error("Failed to update note:", error);
      showError(error.message || "Failed to update note");
    }
  };

  const formatCreatedShort = (dateTime) =>
    formatAppDate(dateTime, { emptyValue: "" });

  const getInitials = (name) => {
    const cleaned = String(name || "").trim();
    if (!cleaned) return "NA";
    const parts = cleaned.split(/\s+/);
    return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
  };

  const formatDateTime = (dateTime) =>
    formatAppDateTime(dateTime, { emptyValue: "" });

  const createdByFilterOptions = useMemo(() => {
    const names = new Set();
    (notes || []).forEach((note) => {
      const name = String(note?.created_by || "").trim();
      if (name) names.add(name);
    });
    return [...names]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }));
  }, [notes]);

  const notesFilterChips = useMemo(
    () =>
      buildClientNotesFilterChips({
        filterCreatedBy,
        filterCreatedFrom,
        filterCreatedTo,
        filterRelatedTo,
        showDeleted,
      }),
    [
      filterCreatedBy,
      filterCreatedFrom,
      filterCreatedTo,
      filterRelatedTo,
      showDeleted,
    ],
  );

  const notesBase = Array.isArray(filteredNotes) ? filteredNotes : [];
  const visibleNotes = notesBase.slice(0, pageSize);

  const clearNotesFilters = () => {
    setFilterCreatedBy([]);
    setFilterCreatedFrom("");
    setFilterCreatedTo("");
    setFilterRelatedTo([]);
    setShowDeleted(false);
    setFilterSortBy("created");
    setFilterSortDirection("desc");
  };

  const removeNotesFilter = (key) => {
    if (key === "filterCreatedBy") setFilterCreatedBy([]);
    if (key === "filterCreatedFrom") setFilterCreatedFrom("");
    if (key === "filterCreatedTo") setFilterCreatedTo("");
    if (key === "filterRelatedTo") setFilterRelatedTo([]);
    if (key === "showDeleted") setShowDeleted(false);
  };

  const handleNotesHeaderSort = (key) => {
    if (filterSortBy === key) {
      setFilterSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setFilterSortBy(key);
    setFilterSortDirection(key === "created" ? "desc" : "asc");
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <FolderOpen className="h-5 w-5 text-sidebar-active" />
          Notes
        </h3>

        <AtlasListToolbar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search notes..."
          onFilterClick={notesFilterPanel.toggle}
          filterOpen={notesFilterPanel.isOpen}
          onRefresh={fetchNotes}
          refreshDisabled={loading}
          refreshSpinning={loading}
          resultsMeta={{
            startRow: notesBase.length === 0 ? 0 : 1,
            endRow: Math.min(pageSize, notesBase.length),
            totalCount: notesBase.length,
            pageSize,
            pageSizeOptions: [10, 20, 50, 100],
            onPageSizeChange: setPageSize,
            sortBy: filterSortBy,
            sortOrder: filterSortDirection,
            sortOptions: CLIENT_NOTES_SORT_OPTIONS,
            onSortByChange: setFilterSortBy,
            onSortOrderToggle: () =>
              setFilterSortDirection((prev) => (prev === "asc" ? "desc" : "asc")),
          }}
          actions={
            <button
              type="button"
              onClick={() => {
                setSelectedNotes(null);
                setEditForm({
                  name: "",
                  note: "",
                  state: "active",
                  type: "",
                  file: null,
                });
                setShowCreateModal(true);
              }}
              className="whitespace-nowrap rounded-lg bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
            >
              Create Note
            </button>
          }
          chipBar={
            notesFilterChips.length > 0 ? (
              <AtlasFilterChipBar
                chips={notesFilterChips}
                onChipClick={(chip) => notesFilterPanel.focusField(chip.fieldId)}
                onRemove={(chip) => removeNotesFilter(chip.key)}
                onClearAll={clearNotesFilters}
              />
            ) : null
          }
        />

        <EmbeddedFilterPanel
          isOpen={notesFilterPanel.isOpen}
          onClose={notesFilterPanel.close}
          onClear={clearNotesFilters}
          footerMode="clearOnly"
        >
          <FilterMultiSelect
            id="notes-created-by"
            label="Created By"
            labelVariant="embedded"
            options={createdByFilterOptions}
            value={filterCreatedBy}
            onChange={setFilterCreatedBy}
            placeholder="Select users..."
          />
          <FilterField
            id="notes-created-from"
            label="Created From"
            labelVariant="embedded"
            focusedFieldId={notesFilterPanel.focusedFieldId}
            onFocusClear={notesFilterPanel.clearFocus}
          >
            <DateInput
              id="notes-created-from-control"
              value={filterCreatedFrom}
              onChange={setFilterCreatedFrom}
              className="h-8 w-full rounded border border-gray-200 bg-white px-2 text-xs text-gray-800 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
            />
          </FilterField>
          <FilterField
            id="notes-created-to"
            label="Created To"
            labelVariant="embedded"
            focusedFieldId={notesFilterPanel.focusedFieldId}
            onFocusClear={notesFilterPanel.clearFocus}
          >
            <DateInput
              id="notes-created-to-control"
              value={filterCreatedTo}
              onChange={setFilterCreatedTo}
              className="h-8 w-full rounded border border-gray-200 bg-white px-2 text-xs text-gray-800 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
            />
          </FilterField>
          <FilterMultiSelect
            id="notes-related-to"
            label="Related To"
            labelVariant="embedded"
            options={CLIENT_NOTES_RELATED_OPTIONS}
            value={filterRelatedTo}
            onChange={setFilterRelatedTo}
            placeholder="Select types..."
          />
          <FilterToggle
            id="notes-show-deleted"
            label="Show Deleted"
            checked={showDeleted}
            onChange={setShowDeleted}
          />
          <FilterSelect
            id="notes-sort-by"
            label="Sort By"
            labelVariant="embedded"
            value={filterSortBy}
            onChange={setFilterSortBy}
            options={CLIENT_NOTES_SORT_OPTIONS}
            placeholder={null}
          />
          <FilterSelect
            id="notes-sort-direction"
            label="Sort Direction"
            labelVariant="embedded"
            value={filterSortDirection}
            onChange={setFilterSortDirection}
            options={[
              { value: "asc", label: "Ascending" },
              { value: "desc", label: "Descending" },
            ]}
            placeholder={null}
          />
        </EmbeddedFilterPanel>

        {loading ? (
          <div className="text-sm text-gray-500">Loading...</div>
        ) : visibleNotes.length === 0 ? (
          <div className="border border-dashed border-gray-300 rounded p-6 text-gray-400 text-center">
            No notes yet
          </div>
        ) : (
          <div className="space-y-4 w-full max-w-full overflow-hidden">
            <div className="atlas-list-table-wrap bg-card rounded-lg border border-border overflow-x-auto block max-w-full no-mobile-table-stack">
              <table className="w-full table-fixed border-collapse min-w-[800px]">
                <thead className="bg-[#dc1f0080] text-white sticky top-0 z-20">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-medium text-white" style={{ width: "35%" }}>Note</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-white" style={{ width: "35%" }}>Related</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-white cursor-pointer" style={{ width: "20%" }} onClick={() => handleNotesHeaderSort("created")}>
                      Created {filterSortBy === "created" ? (filterSortDirection === "asc" ? "↑" : "↓") : ""}
                    </th>
                    <th className="py-3 px-4" style={{ width: "10%" }}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {visibleNotes.map((n) => (
                    <tr key={n.note_id} className="border-t border-border hover:bg-table-row-hover transition-colors align-top">
                      <td className="py-3 px-4 text-sm break-words" style={{ width: "35%" }}>
                        <button
                          type="button"
                          onClick={() => {
                            if (!showDeleted) handleOpenEditNote(n);
                          }}
                          disabled={showDeleted}
                          className={`text-left break-words w-full ${showDeleted
                            ? "text-gray-700 cursor-default"
                            : "text-red-600 hover:text-red-700 hover:underline"
                            }`}
                          title={n.note || "-"}
                        >
                          {n.note || "-"}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-[12px] text-gray-700 break-words leading-tight" style={{ width: "35%" }}>
                        {(() => {
                          const relatedBadges = getRelatedBadges(n);
                          return relatedBadges.length > 0 ? (
                            <div className="flex flex-col gap-2">
                              {relatedBadges.map((rel, idx) => (
                                <div key={`${rel.name}-${rel.type || "none"}-${idx}`}>
                                  {rel.type ? (
                                    <div className="text-gray-900 font-medium">{normalizeRelationTypeLabel(rel.type)}:</div>
                                  ) : null}
                                  <div>{rel.name}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            "-"
                          );
                        })()}
                      </td>
                      <td className="py-3 px-4 text-sm" style={{ width: "20%" }}>
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-2">
                          <span className="shrink-0 inline-flex w-7 h-7 rounded-full bg-gray-900 text-white text-[10px] items-center justify-center font-semibold overflow-hidden">
                            {getInitials(n.created_by || "")}
                          </span>
                          <span className="text-gray-500 text-xs text-left leading-tight">
                            {formatCreatedShort(n.create_date || n.created_at || n.date)}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-center" style={{ width: "10%" }}>
                        <ActionDropdown
                          actions={
                            showDeleted
                              ? [
                                  {
                                    label: "Restore",
                                    onClick: () => handleRestoreNote(n.note_id),
                                    className: "text-green-600",
                                  },
                                ]
                              : [
                                  {
                                    label: "Edit",
                                    onClick: () => handleOpenEditNote(n),
                                  },
                                  {
                                    label: "Delete",
                                    onClick: () => handleDeleteNote(n.note_id),
                                    className: "text-red-500",
                                  },
                                ]
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-md shadow-2xl w-full max-w-xl p-6 relative border border-gray-100 max-h-[90vh] flex flex-col">
            <h4 className="text-xl font-medium text-gray-700 mb-8">Edit Note</h4>

            <div className="space-y-6 overflow-y-auto pr-1">
              <div className="space-y-2">
                <label className="block text-sm text-gray-600">Relates To</label>
                <div className="relative" ref={editRelatesRef}>
                  <div className="w-full border border-gray-300 rounded-xl px-4 py-2 min-h-[42px] flex items-center gap-2 bg-gray-50">
                    <input
                      type="text"
                      value={editRelatesText}
                      onChange={(e) => {
                        const v = e.target.value;
                        setEditRelatesText(v);
                        if (relatesTimer.current) clearTimeout(relatesTimer.current);
                        relatesTimer.current = setTimeout(() => {
                          if (v && v.trim().length > 0) searchRelatesForEdit(v.trim());
                          else setEditRelatesOptions([]);
                        }, 250);
                      }}
                      placeholder="Search account/client"
                      className="w-full bg-transparent outline-none text-sm"
                    />
                    {editRelatesText && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditRelatesText("");
                          setEditRelatesOptions([]);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        x
                      </button>
                    )}
                  </div>

                  {editRelatesOptions.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-sm max-h-56 overflow-auto">
                      {editRelatesOptions.map((opt) => (
                        <div
                          key={`${opt.type}-${opt.id}`}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setEditSelectedRelatesList((prev) =>
                              addUniqueRelation(prev, {
                                id: opt.id,
                                name: opt.name,
                                type: opt.type,
                              })
                            );
                            setEditRelatesText("");
                            setEditRelatesOptions([]);
                          }}
                        >
                          <div className="text-sm font-medium">{opt.name}</div>
                          <div className="text-xs text-gray-400">{opt.type || "account"}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {editSelectedRelatesList.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {editSelectedRelatesList.map((rel) => (
                      <span
                        key={`edit-rel-${rel.type}-${rel.id}`}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-sm text-gray-700"
                      >
                        {rel.name}
                        <button
                          type="button"
                          className="text-gray-500 hover:text-gray-700"
                          onClick={() =>
                            setEditSelectedRelatesList((prev) =>
                              removeRelation(prev, rel)
                            )
                          }
                        >
                          x
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm text-gray-600">
                  Note <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editNoteText}
                  onChange={(e) => setEditNoteText(e.target.value)}
                  rows={4}
                  className="w-full border-b border-gray-300 rounded-none px-1 py-2 outline-none resize-none"
                />
              </div>
            </div>

            <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-10">
                <button
                  type="button"
                  onClick={handleSaveEditedNote}
                  disabled={!editNoteText.trim()}
                  className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  SAVE
                </button>
                <button
                  type="button"
                  onClick={requestCloseEditNoteModal}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  CANCEL
                </button>
              </div>
              <button
                type="button"
                onClick={requestCloseEditNoteModal}
                className="text-sm text-red-600 hover:text-red-700"
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Note Modal */}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-md shadow-2xl w-full max-w-xl p-6 relative border border-gray-100 max-h-[90vh] flex flex-col">
            <h4 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <FileText className="h-5 w-5 text-sidebar-active" />
              Create New Note
            </h4>

            <div className="space-y-6 overflow-y-auto pr-1">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Relates To
                </label>
                <div className="relative" ref={relatesRef}>
                  <input
                    type="text"
                    value={relatesToText}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRelatesToText(v);
                      // debounce search
                      if (relatesTimer.current)
                        clearTimeout(relatesTimer.current);
                      relatesTimer.current = setTimeout(() => {
                        if (v && v.trim().length > 0) searchRelates(v.trim());
                        else setRelatesOptions([]);
                      }, 250);
                    }}
                    placeholder="Search accounts or objects to relate"
                    className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active transition-colors"
                  />

                  {relatesOptions && relatesOptions.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-sm max-h-56 overflow-auto">
                      {relatesOptions.map((opt) => (
                        <div
                          key={`${opt.type}-${opt.id}`}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setSelectedRelatesList((prev) =>
                              addUniqueRelation(prev, {
                                id: opt.id,
                                name: opt.name,
                                type: opt.type,
                              })
                            );
                            setRelatesToText("");
                            setRelatesOptions([]);
                          }}
                        >
                          <div className="text-sm font-medium">{opt.name}</div>
                          <div className="text-xs text-gray-400">
                            {opt.type || "account"}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedRelatesList.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedRelatesList.map((rel) => (
                      <span
                        key={`create-rel-${rel.type}-${rel.id}`}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-sm text-gray-700"
                      >
                        {rel.name}
                        <button
                          type="button"
                          className="text-gray-500 hover:text-gray-700"
                          onClick={() =>
                            setSelectedRelatesList((prev) =>
                              removeRelation(prev, rel)
                            )
                          }
                        >
                          x
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <label className="block text-sm font-semibold text-gray-700">
                  Note <span className="text-red-500">*</span>
                </label>

                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={4}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active transition-colors resize-none"
                  placeholder="Enter your note..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={requestCloseCreateNoteModal}
                className="px-5 py-2.5 bg-gray-100 text-sm text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>

              <button
                onClick={handleCreateNote}
                className="px-5 py-2.5 bg-sidebar-active text-sm text-white rounded-xl hover:bg-red-600 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
              >
                Create Note
              </button>
            </div>
          </div>
        </div>
      )}

      <UnsavedChangesDialog
        isOpen={unsavedPromptOpen}
        onSaveAndContinue={async () => {
          if (unsavedSaveAction) await unsavedSaveAction();
          closeUnsavedPrompt();
        }}
        onDiscardAndContinue={() => {
          if (unsavedDiscardAction) unsavedDiscardAction();
          closeUnsavedPrompt();
        }}
        onReview={closeUnsavedPrompt}
      />

    </div>
  );
};

// Files Tab Component

const FilesTab = ({ clientData, client, onRefresh }) => {
  const [files, setFiles] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  const [editForm, setEditForm] = useState({
    name: "",

    note: "",

    state: "active",

    type: "",

    expiry_date: "",

    file: null,
  });

  const isSinDocumentTypeSlug = (slug) =>
    String(slug || "").trim() === "sin-document";

  const renderFileExpiryCell = (file) => {
    if ((file.type || "").trim() !== "SIN Document") return "—";
    if (!file.expiry_date) return "—";
    const ymd = String(file.expiry_date).slice(0, 10);
    if (file.is_expired) {
      return (
        <span>
          {ymd}{" "}
          <span className="text-red-600 font-semibold text-xs ml-1">Expired</span>
        </span>
      );
    }
    return ymd;
  };

  const [fileErrors, setFileErrors] = useState({});

  const [loading, setLoading] = useState(false);
  const [initialFileSnapshot, setInitialFileSnapshot] = useState(null);
  const [unsavedPromptOpen, setUnsavedPromptOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredFiles, setFilteredFiles] = useState([]);
  const filesFilterPanel = useEmbeddedFilterLayout();
  const [fileTypeFilters, setFileTypeFilters] = useState(
    CLIENT_FILES_DEFAULT_FILTERS.fileTypeFilters,
  );
  const [statusFilters, setStatusFilters] = useState(
    CLIENT_FILES_DEFAULT_FILTERS.statusFilters,
  );
  const [showDeleted, setShowDeleted] = useState(
    CLIENT_FILES_DEFAULT_FILTERS.showDeleted,
  );
  const [sortBy, setSortBy] = useState(CLIENT_FILES_DEFAULT_FILTERS.sortBy);
  const [sortDirection, setSortDirection] = useState(
    CLIENT_FILES_DEFAULT_FILTERS.sortDirection,
  );
  const [pageSize, setPageSize] = useState(100);

  const slugFromDisplayType = (displayType) => {
    const normalized = String(displayType || "").trim().toUpperCase();
    const match = CLIENT_FILE_TYPE_OPTIONS.find(
      (opt) => opt.label.toUpperCase() === normalized,
    );
    return match?.value || "";
  };

  useEffect(() => {
    fetchFiles();
  }, [client?.id, showDeleted]);

  const fetchFiles = async () => {
    if (!client?.id) return;

    setLoading(true);

    try {
      const response = await API.get(
        `${BASE_URL}/api/clients/${client.id}/files`,
        { params: { showDeleted: showDeleted ? 1 : 0 } }
      );

      const data = response.data.data || [];

      setFiles(data); // main list
      setFilteredFiles(data); // filtered list (search)
    } catch (error) {
      console.error("Failed to fetch files:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);

    const typeSlug = slugFromDisplayType(file.type);

    setEditForm({
      name: getDocumentDisplayName(file.name),

      note: file.note || "",

      state: file.state,

      type: typeSlug,

      expiry_date: file.expiry_date
        ? String(file.expiry_date).slice(0, 10)
        : "",

      file: null,
    });

    setFileErrors({});
    setInitialFileSnapshot({
      name: getDocumentDisplayName(file.name) || "",
      note: file.note || "",
      state: file.state || "active",
      type: typeSlug,
      expiry_date: file.expiry_date
        ? String(file.expiry_date).slice(0, 10)
        : "",
    });
    setShowEditModal(true);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];

    if (file) {
      setEditForm((prev) => ({ ...prev, file }));
      setFileErrors((prev) => ({ ...prev, file: undefined }));
    }
  };

  const handleSaveFile = async () => {
    // client-side validation
    const errors = {};
    if (!editForm.state) errors.state = "Status is required.";
    if (!editForm.type) errors.type = "Type is required.";
    if (!selectedFile && !editForm.file) errors.file = "File is required.";

    if (Object.keys(errors).length) {
      setFileErrors(errors);
      return;
    }

    try {
      const formData = new FormData();

      formData.append("name", editForm.name);

      formData.append("note", editForm.note);

      formData.append("state", editForm.state);

      formData.append("type", editForm.type);

      if (isSinDocumentTypeSlug(editForm.type)) {
        formData.append("expiry_date", editForm.expiry_date || "");
      }

      if (editForm.file) {
        formData.append("file", editForm.file);
      }

      if (selectedFile) {
        const fid = selectedFile.id ?? selectedFile.file_id;
        await API.put(
          `${BASE_URL}/api/clients/${client.id}/files/${fid}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        await API.post(
          `${BASE_URL}/api/clients/${client.id}/files`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      setFileErrors({});
      setShowEditModal(false);

      setSelectedFile(null);

      fetchFiles();
    } catch (error) {
      console.error("Failed to save file:", error);
      showError(
        error?.response?.data?.message ||
          "Failed to save file. Please try again.",
      );
    }
  };

  const readDownloadErrorMessage = async (res) => {
    const text = await res.text().catch(() => "");
    try {
      const j = JSON.parse(text);
      return j?.message || j?.error || text || "Failed to download file.";
    } catch {
      return (text && String(text).trim().slice(0, 400)) || "Failed to download file.";
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      const fid = file?.id ?? file?.file_id;
      if (!fid) {
        showError("Missing file id.");
        return;
      }
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/api/clients/${client.id}/files/${fid}/download`,
        {
          redirect: "manual",
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (res.status === 404) {
        showError(await readDownloadErrorMessage(res));
        return;
      }
      if (res.status === 302) {
        const redirectUrl = res.headers.get("Location");
        if (redirectUrl) {
          const link = document.createElement("a");
          link.href = redirectUrl;
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          document.body.appendChild(link);
          link.click();
          link.remove();
          return;
        }
      }
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = file.name || "download";
        link.click();
        URL.revokeObjectURL(url);
        return;
      }
      showError(await readDownloadErrorMessage(res));
    } catch (error) {
      console.error("Failed to download file:", error);
      showError("Failed to download file. Please try again.");
    }
  };

  const loadFilePreview = async (file) => {
    const fid = file?.id ?? file?.file_id;
    if (!fid) throw new Error("Missing file id.");

    const token = localStorage.getItem("token");
    const res = await fetch(
      `${BASE_URL}/api/clients/${client.id}/files/${fid}/download`,
      {
        redirect: "manual",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
    if (res.status === 404) throw new Error(await readDownloadErrorMessage(res));
    if (res.status === 302) {
      const redirectUrl = res.headers.get("Location");
      if (!redirectUrl) throw new Error("Preview URL was not returned.");
      const redirectedRes = await fetch(redirectUrl);
      if (!redirectedRes.ok) throw new Error("Failed to load file preview.");
      return redirectedRes.blob();
    }
    if (!res.ok) throw new Error(await readDownloadErrorMessage(res));
    return res.blob();
  };

  const handleDeleteFile = async (fileId) => {
    if (!(await showConfirm("Are you sure you want to delete this file?")))
      return;
    try {
      await API.delete(
        `${BASE_URL}/api/clients/${client.id}/files/${fileId}`
      );
      showSuccess("File deleted successfully!");
      fetchFiles();
    } catch (error) {
      console.error("Failed to delete file:", error);
      showError("Failed to delete file. Please try again.");
    }
  };

  const normalizeType = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const getDocumentDisplayName = (nameValue) => {
    const raw = String(nameValue || "").trim();
    if (!raw) return "Untitled document";

    let candidate = raw;
    if (/^https?:\/\//i.test(raw)) {
      try {
        const parsedUrl = new URL(raw);
        candidate = parsedUrl.pathname.split("/").pop() || raw;
      } catch {
        candidate = raw;
      }
    } else if (raw.includes("/") || raw.includes("\\")) {
      candidate = raw.split(/[/\\]/).pop() || raw;
    }

    const cleaned = decodeURIComponent(candidate)
      .replace(/^[0-9]{10,}_/, "")
      .trim();
    return cleaned || raw;
  };

  //! simple comparator for sorting strings/numbers
  const compareValues = (a, b, dir = "asc") => {
    if (a == null) a = "";
    if (b == null) b = "";
    // numeric comparison if both are numbers
    const aNum = parseFloat(a);
    const bNum = parseFloat(b);
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return dir === "asc" ? aNum - bNum : bNum - aNum;
    }
    // fallback string compare
    const res = String(a).localeCompare(String(b), undefined, {
      sensitivity: "base",
    });
    return dir === "asc" ? res : -res;
  };

  const applyFilters = () => {
    let list = Array.isArray(files) ? [...files] : [];

    // 1) showDeleted (enabled: deleted-only, disabled: exclude deleted)
    if (showDeleted) {
      list = list.filter((f) => (f.state || "").toLowerCase() === "deleted");
    } else {
      list = list.filter((f) => (f.state || "").toLowerCase() !== "deleted");
    }

    // 2) file type filter
    if (Array.isArray(fileTypeFilters) && fileTypeFilters.length > 0) {
      const allowed = new Set(
        fileTypeFilters.map((t) => normalizeType(t))
      );
      list = list.filter((f) => allowed.has(normalizeType(f.type)));
    }

    // 3) status filter
    if (Array.isArray(statusFilters) && statusFilters.length > 0) {
      // If "all" is the only selection or there are other selections alongside "all", show all statuses
      const hasNonAll = statusFilters.some((s) => s !== "all");
      if (hasNonAll) {
        const allowed = new Set(
          statusFilters.map((s) => (s || "").toLowerCase())
        );
        list = list.filter((f) => allowed.has((f.state || "").toLowerCase()));
      }
    }

    // 4) search term filter
    if (searchTerm && searchTerm.trim() !== "") {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (f) =>
          getDocumentDisplayName(f.name).toLowerCase().includes(q) ||
          (f.note || "").toLowerCase().includes(q) ||
          (f.type || "").toLowerCase().includes(q)
      );
    }

    // 5) sort
    if (sortBy) {
      const sortField = (file) => {
        if (sortBy === "name") return getDocumentDisplayName(file.name);
        if (sortBy === "status") return file.state;
        if (sortBy === "expiry") return file.expiry_date;
        return file[sortBy];
      };
      list.sort((a, b) =>
        compareValues(sortField(a), sortField(b), sortDirection),
      );
    }

    setFilteredFiles(list);
  };

  const filesBase = Array.isArray(filteredFiles) ? filteredFiles : [];
  const visibleFiles = filesBase.slice(0, pageSize);
  const filesFilterChips = useMemo(
    () =>
      buildClientFilesFilterChips({
        fileTypeFilters,
        statusFilters,
        showDeleted,
      }),
    [fileTypeFilters, statusFilters, showDeleted],
  );

  const clearFilesFilters = () => {
    setSearchTerm("");
    setFileTypeFilters(CLIENT_FILES_DEFAULT_FILTERS.fileTypeFilters);
    setStatusFilters(CLIENT_FILES_DEFAULT_FILTERS.statusFilters);
    setShowDeleted(CLIENT_FILES_DEFAULT_FILTERS.showDeleted);
    setSortBy(CLIENT_FILES_DEFAULT_FILTERS.sortBy);
    setSortDirection(CLIENT_FILES_DEFAULT_FILTERS.sortDirection);
  };

  const removeFilesFilter = (key) => {
    if (key === "fileTypeFilters") setFileTypeFilters([]);
    if (key === "statusFilters") setStatusFilters([]);
    if (key === "showDeleted") setShowDeleted(false);
  };

  const handleFileColumnSort = (key) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDirection("asc");
    }
  };

  const handleRestoreFile = async (fileId) => {
    const confirmed = await showConfirm("Are you sure you want to restore this file?", "Confirm");
    if (!confirmed) return;
    try {
      await API.put(`${BASE_URL}/api/clients/${client.id}/files/${fileId}/restore`);
      showSuccess("File restored successfully!");
      fetchFiles();
    } catch (error) {
      console.error("Failed to restore file:", error);
      showError("Failed to restore file. Please try again.");
    }
  };

  const isFileModalDirty = (() => {
    if (!showEditModal) return false;
    const baseline = initialFileSnapshot || {
      name: "",
      note: "",
      state: "active",
      type: "",
      expiry_date: "",
    };
    return (
      String(editForm.name || "") !== String(baseline.name || "") ||
      String(editForm.note || "") !== String(baseline.note || "") ||
      String(editForm.state || "active") !== String(baseline.state || "active") ||
      String(editForm.type || "") !== String(baseline.type || "") ||
      String(editForm.expiry_date || "") !== String(baseline.expiry_date || "") ||
      !!editForm.file
    );
  })();

  const requestCloseFileModal = () => {
    if (!isFileModalDirty) {
      setShowEditModal(false);
      return;
    }
    setUnsavedPromptOpen(true);
  };

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    files,
    searchTerm,
    fileTypeFilters,
    statusFilters,
    showDeleted,
    sortBy,
    sortDirection,
  ]);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <FolderOpen className="h-5 w-5 text-sidebar-active" />
          Files
        </h3>

        <AtlasListToolbar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search files..."
          onFilterClick={filesFilterPanel.toggle}
          filterOpen={filesFilterPanel.isOpen}
          onRefresh={fetchFiles}
          refreshDisabled={loading}
          refreshSpinning={loading}
          resultsMeta={{
            startRow: filesBase.length === 0 ? 0 : 1,
            endRow: Math.min(pageSize, filesBase.length),
            totalCount: filesBase.length,
            pageSize,
            pageSizeOptions: [10, 20, 50, 100],
            onPageSizeChange: setPageSize,
            sortBy,
            sortOrder: sortDirection,
            sortOptions: CLIENT_FILES_SORT_OPTIONS,
            onSortByChange: setSortBy,
            onSortOrderToggle: () =>
              setSortDirection((prev) => (prev === "asc" ? "desc" : "asc")),
          }}
          actions={
            <button
              type="button"
              onClick={() => {
                setSelectedFile(null);
                setEditForm({
                  name: "",
                  note: "",
                  state: "active",
                  type: "",
                  expiry_date: "",
                  file: null,
                });
                setFileErrors({});
                setInitialFileSnapshot({
                  name: "",
                  note: "",
                  state: "active",
                  type: "",
                  expiry_date: "",
                });
                setShowEditModal(true);
              }}
              className="whitespace-nowrap rounded-lg bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
            >
              Add File
            </button>
          }
          chipBar={
            filesFilterChips.length > 0 ? (
              <AtlasFilterChipBar
                chips={filesFilterChips}
                onChipClick={(chip) => filesFilterPanel.focusField(chip.fieldId)}
                onRemove={(chip) => removeFilesFilter(chip.key)}
                onClearAll={clearFilesFilters}
              />
            ) : null
          }
        />

        <EmbeddedFilterPanel
          isOpen={filesFilterPanel.isOpen}
          onClose={filesFilterPanel.close}
          onClear={clearFilesFilters}
          footerMode="clearOnly"
        >
          <FilterMultiSelect
            id="files-type"
            label="Type"
            labelVariant="embedded"
            options={CLIENT_FILE_TYPE_OPTIONS}
            value={fileTypeFilters}
            onChange={setFileTypeFilters}
          />
          <FilterMultiSelect
            id="files-status"
            label="Status"
            labelVariant="embedded"
            options={CLIENT_FILE_STATUS_OPTIONS}
            value={statusFilters}
            onChange={setStatusFilters}
          />
          <FilterToggle
            id="files-show-deleted"
            label="Show Deleted"
            checked={showDeleted}
            onChange={setShowDeleted}
          />
          <FilterSelect
            id="files-sort-by"
            label="Sort By"
            labelVariant="embedded"
            value={sortBy}
            onChange={setSortBy}
            options={CLIENT_FILES_SORT_OPTIONS}
            placeholder={null}
          />
          <FilterSelect
            id="files-sort-direction"
            label="Sort Direction"
            labelVariant="embedded"
            value={sortDirection}
            onChange={setSortDirection}
            options={[
              { value: "asc", label: "Ascending" },
              { value: "desc", label: "Descending" },
            ]}
            placeholder={null}
          />
        </EmbeddedFilterPanel>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-active mx-auto mb-3"></div>

          <div className="text-gray-500">Loading files...</div>
        </div>
      ) : (
        <div className="overflow-x-auto overflow-y-visible w-full max-w-full block no-mobile-table-stack">
          <table className="w-full table-auto bg-white border border-gray-200 rounded-xl overflow-hidden min-w-[900px]">
            <thead className="bg-gray-50">
              <tr>
                <SortableTableHeader
                  label="Name"
                  sortKey="name"
                  sortBy={sortBy}
                  sortOrder={sortDirection}
                  onSort={handleFileColumnSort}
                />
                <TableHeaderCell label="Note" />
                <SortableTableHeader
                  label="Status"
                  sortKey="status"
                  sortBy={sortBy}
                  sortOrder={sortDirection}
                  onSort={handleFileColumnSort}
                />
                <SortableTableHeader
                  label="Type"
                  sortKey="type"
                  sortBy={sortBy}
                  sortOrder={sortDirection}
                  onSort={handleFileColumnSort}
                />
                <SortableTableHeader
                  label="Expiry"
                  sortKey="expiry"
                  sortBy={sortBy}
                  sortOrder={sortDirection}
                  onSort={handleFileColumnSort}
                />
                <TableHeaderCell label="Actions" />
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {filesBase.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />

                    <div className="text-gray-500 text-lg font-medium">
                      No files found
                    </div>

                    <div className="text-gray-400 text-sm">
                      Upload your first file to get started
                    </div>
                  </td>
                </tr>
              ) : (
                visibleFiles.map((file) => (
                  <tr
                    key={file.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td
                      className="px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                      onClick={() => handleFileClick(file)}
                    >
                      <div className="flex items-center gap-2">
                        <svg
                          className="h-4 w-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>

                        {getDocumentDisplayName(file.name)}
                      </div>
                    </td>

                    <td className="px-3 py-2 text-sm text-gray-500">
                      {file.note || "-"}
                    </td>

                    <td className="px-3 py-2 text-sm">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${file.state === "active"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                          }`}
                      >
                        {file.state}
                      </span>
                    </td>

                    <td className="px-3 py-2 text-sm text-gray-500">
                      {file.type || "-"}
                    </td>

                    <td className="px-3 py-2 text-sm text-gray-500">
                      {renderFileExpiryCell(file)}
                    </td>

                    <td className="px-3 py-2 text-sm align-top">
                      <ActionDropdown
                        actions={
                          showDeleted
                            ? [
                                {
                                  label: "Restore",
                                  onClick: () =>
                                    handleRestoreFile(file.id ?? file.file_id),
                                  className: "text-green-600",
                                },
                              ]
                            : [
                                {
                                  label: "Preview",
                                  onClick: () => setPreviewFile(file),
                                  className: "text-gray-700",
                                },
                                {
                                  label: "Download",
                                  onClick: () => handleDownloadFile(file),
                                  className: "text-blue-600",
                                },
                                {
                                  label: "Remove",
                                  onClick: () =>
                                    handleDeleteFile(file.id ?? file.file_id),
                                  className: "text-red-500",
                                },
                              ]
                        }
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {/* Edit File Modal */}

      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[84vw] sm:max-w-xs md:max-w-sm lg:max-w-md p-3 sm:p-4 md:p-6 relative border border-gray-100 max-h-[85vh] overflow-hidden flex flex-col">
            <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-5 flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-sidebar-active" />

              {selectedFile ? "Edit File" : "Add File"}
            </h4>

            <div className="flex-1 overflow-y-auto pr-2 pb-6 sm:pb-8">
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Status <span className="text-red-500">*</span>
                  </label>

                  <select
                    value={editForm.state}
                    onChange={(e) => {
                      setEditForm((prev) => ({
                        ...prev,
                        state: e.target.value,
                      }));
                      setFileErrors((prev) => ({ ...prev, state: undefined }));
                    }}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active transition-colors"
                  >
                    <option value="active">Active</option>

                    <option value="inactive">Inactive</option>
                    <option value="pending-review">Pending Review</option>
                  </select>
                  {fileErrors.state && (
                    <div className="text-sm text-red-600 mt-1">
                      {fileErrors.state}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Type <span className="text-red-500">*</span>
                  </label>

                  <select
                    value={editForm.type || ""}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setEditForm((prev) => ({
                        ...prev,
                        type: nextType,
                        expiry_date: isSinDocumentTypeSlug(nextType)
                          ? prev.expiry_date
                          : "",
                      }));
                      setFileErrors((prev) => ({ ...prev, type: undefined }));
                    }}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active transition-colors"
                  >
                    <option value="">Select type</option>
                    <option value="object-pdf">Object PDF</option>
                    <option value="sin-document">SIN Document</option>
                    <option value="identity-document">Identity Document</option>
                    <option value="resume-and-other">Resume and Other</option>
                  </select>
                  {fileErrors.type && (
                    <div className="text-sm text-red-600 mt-1">
                      {fileErrors.type}
                    </div>
                  )}
                </div>

                {isSinDocumentTypeSlug(editForm.type) ? (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Expiry date (optional)
                    </label>
                    <DateInput
                      value={editForm.expiry_date || ""}
                      onChange={(value) =>
                        setEditForm((prev) => ({
                          ...prev,
                          expiry_date: value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active transition-colors"
                    />
                  </div>
                ) : null}

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Note
                  </label>

                  <textarea
                    value={editForm.note}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, note: e.target.value }))
                    }
                    rows={2}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                    placeholder="Enter file note"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    File <span className="text-red-500">*</span>
                  </label>

                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active transition-colors"
                  />
                  {fileErrors.file && (
                    <div className="text-sm text-red-600 mt-1">
                      {fileErrors.file}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={requestCloseFileModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-semibold text-sm"
              >
                Cancel
              </button>

              {selectedFile && (
                <button
                  onClick={() => handleDownloadFile(selectedFile)}
                  className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-semibold shadow-sm"
                >
                  Download
                </button>
              )}

              <button
                onClick={handleSaveFile}
                className="px-4 py-2 bg-sidebar-active text-white rounded-xl hover:bg-red-600 transition-all duration-200 font-semibold shadow-sm text-sm"
              >
                {selectedFile ? "Update" : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
      <UnsavedChangesDialog
        isOpen={unsavedPromptOpen}
        onSaveAndContinue={async () => {
          await handleSaveFile();
          setUnsavedPromptOpen(false);
        }}
        onDiscardAndContinue={() => {
          setShowEditModal(false);
          setUnsavedPromptOpen(false);
        }}
        onReview={() => setUnsavedPromptOpen(false)}
      />
      <DocumentPreviewModal
        isOpen={Boolean(previewFile)}
        file={previewFile}
        loadPreview={loadFilePreview}
        onClose={() => setPreviewFile(null)}
      />
    </div>
  );
};

// Shift Settings Tab Component

const ShiftSettingsTab = ({ clientData, client, onRefresh }) => {
  const hourlyTravelTimeOptions = [
    { value: 0.5, label: "30m" },
    { value: 1, label: "1h" },
    { value: 1.5, label: "1h 30m" },
    { value: 2, label: "2h" },
    { value: 2.5, label: "2h 30m" },
    { value: 3, label: "3h" },
    { value: 3.5, label: "3h 30m" },
    { value: 4, label: "4h" },
    { value: 4.5, label: "4h 30m" },
    { value: 5, label: "5h" },
    { value: 5.5, label: "5h 30m" },
    { value: 6, label: "6h" },
    { value: 6.5, label: "6h 30m" },
    { value: 7, label: "7h" },
    { value: 7.5, label: "7h 30m" },
    { value: 8, label: "8h" },
  ];
  const hourlyTravelTimeValues = new Set(
    hourlyTravelTimeOptions.map((opt) => opt.value)
  );
  const [settings, setSettings] = useState({
    // Breaks

    defaultBreakSetting: "30min",

    allowClientModifyBreak: true,

    // Check in/out rules

    graceTime: 15,

    markAsScheduled: true,

    outsideGraceRounding: 5,

    checkInRoundingMode: "nearest",

    checkOutRoundingMode: "nearest",

    allowExtendedDuration: true,

    extendedDurationPasscode: false,

    // Timesheet Sign-off

    timesheetReviewRequired: false,

    timesheetSignoffEmail: "",

    onDevicePrompt: "none",

    signoffDelay: 0,

    // Overtime

    paysOvertime: true,

    overtimeThreshold: 44,

    overtimeMultiplier: 1.5,

    // Travel Time

    travelTimeSetting: "hourly",

    hourlyTravelTime: 0,
  });

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [client?.id]);

  const fetchSettings = async () => {
    if (!client?.id) return;

    setLoading(true);

    try {
      const response = await API.get(
        `${BASE_URL}/api/clients/${client.id}/shift-settings`
      );

      const backendData = response.data.data || {};

      console.log("Fetched shift settings:", backendData);

      // Map backend fields to frontend form fields

      setSettings({
        // Breaks

        defaultBreakSetting: backendData.unpaid_break ? "30min" : "none",

        allowClientModifyBreak:
          backendData.modify_unpaid_break === 1 ||
          backendData.modify_unpaid_break === true,

        // Check in/out rules

        graceTime: backendData.grace_time || 15,

        markAsScheduled: true,

        outsideGraceRounding: backendData.grace_time_rounding || 5,

        checkInRoundingMode: backendData.check_in_rounding || "nearest",

        checkOutRoundingMode: backendData.check_out_rounding || "nearest",

        allowExtendedDuration:
          backendData.allow_extended_duration === 1 ||
          backendData.allow_extended_duration === true,

        extendedDurationPasscode:
          backendData.extended_duration_passcode_required === 1 ||
          backendData.extended_duration_passcode_required === true,

        // Timesheet Sign-off

        timesheetReviewRequired:
          backendData.timesheet_review_required === 1 ||
          backendData.timesheet_review_required === true,

        timesheetSignoffEmail: (() => {
          const raw = backendData.checkin_signoff_emails;
          if (!raw) return "";
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              return parsed.join(", ");
            }
          } catch (e) {}
          return raw;
        })(),

        onDevicePrompt: backendData.checkin_restriction || "none",

        signoffDelay: backendData.checkin_signoff_delay || 0,

        // Overtime

        paysOvertime:
          backendData.pays_overtime === 1 || backendData.pays_overtime === true,

        overtimeThreshold: (() => {
          const value = Number(backendData.overtime_threshold);
          return Number.isFinite(value) && value > 0 ? value : 44;
        })(),

        overtimeMultiplier: (() => {
          const value = Number(backendData.overtime_multiplier);
          return Number.isFinite(value) && value > 0 ? value : 1.5;
        })(),

        // Travel Time

        travelTimeSetting: backendData.travel_time_type || "hourly",

        hourlyTravelTime: (() => {
          const raw = backendData.travel_time_amount;
          const isFlat =
            String(backendData.travel_time_type || "").toLowerCase() === "flat";
          const n = parseTravelHoursAmount(raw);
          if (!Number.isFinite(n) || n <= 0) return isFlat ? 1 : 0.5;
          if (isFlat) return n;
          return snapToHourlyTravelStep(n);
        })(),
      });
    } catch (error) {
      console.error("Failed to fetch shift settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (field, value) => {
    console.log(`Changing ${field} to:`, value, typeof value);

    setSettings((prev) => {
      const newSettings = { ...prev, [field]: value };
      if (field === "paysOvertime" && value === true && prev.paysOvertime !== true) {
        const threshold = Number(prev.overtimeThreshold);
        const multiplier = Number(prev.overtimeMultiplier);
        if (!Number.isFinite(threshold) || threshold <= 0) {
          newSettings.overtimeThreshold = 44;
        }
        if (!Number.isFinite(multiplier) || multiplier <= 0) {
          newSettings.overtimeMultiplier = 1.5;
        }
      }
      if (field === "travelTimeSetting" && value === "flat") {
        const currentAmount = Number(prev.hourlyTravelTime);
        if (!Number.isFinite(currentAmount) || currentAmount <= 0) {
          newSettings.hourlyTravelTime = 1;
        }
      }
      if (field === "travelTimeSetting" && value === "hourly") {
        const currentAmount = Number(prev.hourlyTravelTime);
        if (!hourlyTravelTimeValues.has(currentAmount)) {
          newSettings.hourlyTravelTime = 0.5;
        }
      }

      console.log("New settings:", newSettings);

      return newSettings;
    });
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const normalizeNullableNumber = (value) => {
        if (value === "" || value === null || value === undefined) return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };

      // Map frontend form fields to backend database fields

      const backendPayload = {
        pays_overtime: settings.paysOvertime,

        overtime_threshold: normalizeNullableNumber(settings.overtimeThreshold),

        overtime_multiplier: normalizeNullableNumber(settings.overtimeMultiplier),

        travel_time_amount: settings.hourlyTravelTime,

        travel_time_type: settings.travelTimeSetting,

        unpaid_break: settings.defaultBreakSetting === "30min" ? 30 : 0,

        modify_unpaid_break: settings.allowClientModifyBreak,

        grace_time: settings.graceTime,

        grace_time_rounding: settings.outsideGraceRounding,

        check_in_rounding: settings.checkInRoundingMode,

        check_out_rounding: settings.checkOutRoundingMode,

        allow_extended_duration: settings.allowExtendedDuration,

        checkin_signoff_emails: (() => {
          const raw = settings.timesheetSignoffEmail || "";
          const emails = raw.split(",").map(e => e.trim()).filter(Boolean);
          return JSON.stringify(emails);
        })(),

        checkin_signoff_delay: settings.signoffDelay,

        checkin_restriction: settings.onDevicePrompt,

        checkin_signoff_reminder_date: null,

        extended_duration_passcode: null,

        timesheet_review_required: settings.timesheetReviewRequired,

        extended_duration_passcode_required: settings.extendedDurationPasscode,
      };

      console.log("Saving shift settings:", backendPayload);

      const token = localStorage.getItem("token");
      const authorId = getAuthorAccountId();
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (authorId) headers["x-author-account-id"] = `${authorId}`;

      const response = await API.put(
        `${BASE_URL}/api/clients/${client.id}/shift-settings`,
        backendPayload,
        { headers }
      );

      if (response.status === 200) {
        console.log("Save response:", response.data);

        showSuccess("Shift settings saved successfully!");

        onRefresh();
      }
    } catch (error) {
      console.error("Failed to save shift settings:", error);

      showError("Failed to save shift settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading shift settings...</div>;
  }

  console.log(
    "Current paysOvertime value:",
    settings.paysOvertime,
    typeof settings.paysOvertime
  );

  // not sure about this
  const getAuthorAccountId = () => {
    try {
      const explicit = localStorage.getItem("accountId");

      if (explicit && !Number.isNaN(parseInt(explicit, 10)))
        return parseInt(explicit, 10);

      const rawUser = localStorage.getItem("user");

      if (rawUser) {
        const u = JSON.parse(rawUser);

        if (u && (u.account_id || u.accountId || u.id))
          return u.account_id || u.accountId || u.id;
      }
    } catch (_) { }

    return undefined;
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="h-5 w-5 text-sidebar-active" />
            Shift Settings
          </h3>

          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-6 py-3 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl ${saving
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-sidebar-active hover:bg-red-600"
              }`}
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>

        {/* Breaks Section */}

        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-2 h-2 bg-sidebar-active rounded-full"></div>
            Breaks
          </h4>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Default break setting
              </label>

              <div className="space-y-3">
                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="defaultBreakSetting"
                    value="30min"
                    checked={settings.defaultBreakSetting === "30min"}
                    onChange={(e) =>
                      handleSettingChange("defaultBreakSetting", e.target.value)
                    }
                    className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                  />

                  <span className="text-gray-700 font-medium">
                    30 minute break
                  </span>
                </label>

                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="defaultBreakSetting"
                    value="none"
                    checked={settings.defaultBreakSetting === "none"}
                    onChange={(e) =>
                      handleSettingChange("defaultBreakSetting", e.target.value)
                    }
                    className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                  />

                  <span className="text-gray-700 font-medium">No break</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Allow client to modify break setting when signing off
              </label>

              <div className="space-y-3">
                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="allowClientModifyBreak"
                    value={true}
                    checked={settings.allowClientModifyBreak === true}
                    onChange={(e) =>
                      handleSettingChange("allowClientModifyBreak", true)
                    }
                    className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                  />

                  <span className="text-gray-700 font-medium">Yes</span>
                </label>

                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="allowClientModifyBreak"
                    value={false}
                    checked={settings.allowClientModifyBreak === false}
                    onChange={(e) =>
                      handleSettingChange("allowClientModifyBreak", false)
                    }
                    className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                  />

                  <span className="text-gray-700 font-medium">No</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Check in/check out rules */}

        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-2 h-2 bg-sidebar-active rounded-full"></div>
            Check in/check out rules
          </h4>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Check in / check out grace time
                </label>

                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={settings.graceTime}
                    onChange={(e) =>
                      handleSettingChange("graceTime", parseInt(e.target.value))
                    }
                    className="w-24 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />

                  <span className="text-sm text-gray-500 font-medium">
                    minutes
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  When outside of grace time, round to
                </label>

                <select
                  value={settings.outsideGraceRounding}
                  onChange={(e) =>
                    handleSettingChange(
                      "outsideGraceRounding",
                      parseInt(e.target.value)
                    )
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  <option value={1}>1 minute</option>

                  <option value={5}>5 minutes</option>

                  <option value={10}>10 minutes</option>

                  <option value={15}>15 minutes</option>
                </select>
              </div>
            </div>

            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.markAsScheduled}
                  onChange={(e) =>
                    handleSettingChange("markAsScheduled", e.target.checked)
                  }
                  className="mt-1 text-blue-600 focus:ring-blue-500 rounded"
                />

                <span className="text-sm text-gray-700 font-medium">
                  Mark check in / check out time as originally scheduled time if
                  within specified minutes.
                </span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Check in rounding mode
                </label>

                <select
                  value={settings.checkInRoundingMode}
                  onChange={(e) =>
                    handleSettingChange("checkInRoundingMode", e.target.value)
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  <option value="nearest">Nearest</option>

                  <option value="up">Round up (reduces duration)</option>

                  <option value="down">Round down (increases duration)</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Check out rounding mode
                </label>

                <select
                  value={settings.checkOutRoundingMode}
                  onChange={(e) =>
                    handleSettingChange("checkOutRoundingMode", e.target.value)
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  <option value="nearest">Nearest</option>

                  <option value="up">Round up (increases duration)</option>

                  <option value="down">Round down (reduces duration)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Allow staff to request extended duration shift approval
                </label>

                <div className="space-y-3">
                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="allowExtendedDuration"
                      value={false}
                      checked={settings.allowExtendedDuration === false}
                      onChange={(e) =>
                        handleSettingChange("allowExtendedDuration", false)
                      }
                      className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                    />

                    <span className="text-gray-700 font-medium">No</span>
                  </label>

                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="allowExtendedDuration"
                      value={true}
                      checked={settings.allowExtendedDuration === true}
                      onChange={(e) =>
                        handleSettingChange("allowExtendedDuration", true)
                      }
                      className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                    />

                    <span className="text-gray-700 font-medium">Yes</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Extended duration passcode required
                </label>

                <div className="space-y-3">
                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="extendedDurationPasscode"
                      value={false}
                      checked={settings.extendedDurationPasscode === false}
                      onChange={(e) =>
                        handleSettingChange("extendedDurationPasscode", false)
                      }
                      className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                    />

                    <span className="text-gray-700 font-medium">No</span>
                  </label>

                  <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="extendedDurationPasscode"
                      value={true}
                      checked={settings.extendedDurationPasscode === true}
                      onChange={(e) =>
                        handleSettingChange("extendedDurationPasscode", true)
                      }
                      className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                    />

                    <span className="text-gray-700 font-medium">Yes</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timesheet Sign-off */}

        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-2 h-2 bg-sidebar-active rounded-full"></div>
            Timesheet Sign-off
          </h4>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Timesheet review required
              </label>

              <div className="space-y-3">
                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="timesheetReviewRequired"
                    value={false}
                    checked={settings.timesheetReviewRequired === false}
                    onChange={(e) =>
                      handleSettingChange("timesheetReviewRequired", false)
                    }
                    className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                  />

                  <span className="text-gray-700 font-medium">No</span>
                </label>

                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="timesheetReviewRequired"
                    value={true}
                    checked={settings.timesheetReviewRequired === true}
                    onChange={(e) =>
                      handleSettingChange("timesheetReviewRequired", true)
                    }
                    className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                  />

                  <span className="text-gray-700 font-medium">Yes</span>
                </label>
              </div>

              <p className="text-sm text-gray-500 mt-2 ml-6">
                Timesheets will require a sign off manager to review and submit
                before shift actuals are recorded.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Timesheet sign-off email
                </label>

                <TimesheetSignoffEmailChipsInput
                  value={settings.timesheetSignoffEmail}
                  onChange={(val) =>
                    handleSettingChange("timesheetSignoffEmail", val)
                  }
                />

                <p className="text-sm text-gray-500">
                  When configured, specified addresses will be emailed to sign
                  off on timesheets.
                </p>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700">
                  On-device timesheet sign-off prompt
                </label>

                <div className="flex flex-col space-y-2.5">
                  <label
                    className={`flex items-start p-3 border rounded-xl cursor-pointer transition-colors ${
                      settings.onDevicePrompt === "none"
                        ? "border-[#DC1F00] bg-red-50/50"
                        : "border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="onDevicePrompt"
                      value="none"
                      checked={settings.onDevicePrompt === "none"}
                      onChange={(e) =>
                        handleSettingChange("onDevicePrompt", e.target.value)
                      }
                      className="mt-0.5 mr-3 w-4 h-4 text-[#DC1F00] focus:ring-[#DC1F00] border-gray-300 cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className={`text-sm font-medium ${settings.onDevicePrompt === "none" ? "text-gray-900" : "text-gray-700"}`}>
                        None
                      </div>
                    </div>
                  </label>

                  <label
                    className={`flex items-start p-3 border rounded-xl cursor-pointer transition-colors ${
                      settings.onDevicePrompt === "prompt"
                        ? "border-[#DC1F00] bg-red-50/50"
                        : "border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="onDevicePrompt"
                      value="prompt"
                      checked={settings.onDevicePrompt === "prompt"}
                      onChange={(e) =>
                        handleSettingChange("onDevicePrompt", e.target.value)
                      }
                      className="mt-0.5 mr-3 w-4 h-4 text-[#DC1F00] focus:ring-[#DC1F00] border-gray-300 cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className={`text-sm font-medium ${settings.onDevicePrompt === "prompt" ? "text-gray-900" : "text-gray-700"}`}>
                        Prompt (Allow Check-ins)
                      </div>
                      <div className="text-xs text-gray-500 mt-1 leading-snug">
                        Prompt but still allow for check-ins
                      </div>
                    </div>
                  </label>

                  <label
                    className={`flex items-start p-3 border rounded-xl cursor-pointer transition-colors ${
                      settings.onDevicePrompt === "restrict"
                        ? "border-[#DC1F00] bg-red-50/50"
                        : "border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="onDevicePrompt"
                      value="restrict"
                      checked={settings.onDevicePrompt === "restrict"}
                      onChange={(e) =>
                        handleSettingChange("onDevicePrompt", e.target.value)
                      }
                      className="mt-0.5 mr-3 w-4 h-4 text-[#DC1F00] focus:ring-[#DC1F00] border-gray-300 cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className={`text-sm font-medium ${settings.onDevicePrompt === "restrict" ? "text-gray-900" : "text-gray-700"}`}>
                        Restrict Check-ins
                      </div>
                      <div className="text-xs text-gray-500 mt-1 leading-snug">
                        Prompt and don't allow check-ins until sign-off completed
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Delay on timesheet sign-off email
              </label>

              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={settings.signoffDelay}
                  onChange={(e) =>
                    handleSettingChange(
                      "signoffDelay",
                      parseInt(e.target.value)
                    )
                  }
                  className="w-24 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />

                <span className="text-sm text-gray-500 font-medium">
                  minutes
                </span>
              </div>

              <p className="text-sm text-gray-500">
                When configured, specifies the delay period for sending the
                timesheet sign-off email.
              </p>
            </div>
          </div>
        </div>

        {/* Overtime */}

        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-2 h-2 bg-sidebar-active rounded-full"></div>
            Overtime
          </h4>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Pays Overtime
              </label>

              <div className="space-y-3">
                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="paysOvertime"
                    value={false}
                    checked={settings.paysOvertime === false}
                    onChange={(e) => handleSettingChange("paysOvertime", false)}
                    className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                    style={{ accentColor: "#dc2626" }}
                  />

                  <span className="text-gray-700 font-medium">No</span>
                </label>

                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="paysOvertime"
                    value={true}
                    checked={settings.paysOvertime === true}
                    onChange={(e) => handleSettingChange("paysOvertime", true)}
                    className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                    style={{ accentColor: "#dc2626" }}
                  />

                  <span className="text-gray-700 font-medium">Yes</span>
                </label>
              </div>
            </div>

            {settings.paysOvertime && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Overtime Threshold (hours)
                  </label>

                  <input
                    type="number"
                    step="0.5"
                    value={settings.overtimeThreshold ?? ""}
                    onChange={(e) =>
                      handleSettingChange(
                        "overtimeThreshold",
                        e.target.value === "" ? "" : parseFloat(e.target.value)
                      )
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="44"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Overtime Multiplier
                  </label>

                  <input
                    type="number"
                    step="0.1"
                    value={settings.overtimeMultiplier ?? ""}
                    onChange={(e) =>
                      handleSettingChange(
                        "overtimeMultiplier",
                        e.target.value === "" ? "" : parseFloat(e.target.value)
                      )
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="1.5"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Travel Time */}

        <div className="bg-gray-50 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-2 h-2 bg-sidebar-active rounded-full"></div>
            Travel Time
          </h4>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Travel time setting
              </label>

              <div className="space-y-3">
                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="travelTimeSetting"
                    value="hourly"
                    checked={settings.travelTimeSetting === "hourly"}
                    onChange={(e) =>
                      handleSettingChange("travelTimeSetting", e.target.value)
                    }
                    className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                  />

                  <span className="text-gray-700 font-medium">Hourly</span>
                </label>

                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="travelTimeSetting"
                    value="flat"
                    checked={settings.travelTimeSetting === "flat"}
                    onChange={(e) =>
                      handleSettingChange("travelTimeSetting", e.target.value)
                    }
                    className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                  />

                  <span className="text-gray-700 font-medium">Flat Rate</span>
                </label>

                <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="travelTimeSetting"
                    value="none"
                    checked={settings.travelTimeSetting === "none"}
                    onChange={(e) =>
                      handleSettingChange("travelTimeSetting", e.target.value)
                    }
                    className="mr-3 h-4 w-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
                  />

                  <span className="text-gray-700 font-medium">None</span>
                </label>
              </div>
            </div>

            {(settings.travelTimeSetting === "hourly" ||
              settings.travelTimeSetting === "flat") && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  {settings.travelTimeSetting === "flat"
                    ? "Amount"
                    : "Hourly travel time"}
                </label>

                <div className="flex items-center gap-2">
                  {settings.travelTimeSetting === "hourly" ? (
                    <select
                      value={String(settings.hourlyTravelTime)}
                      onChange={(e) =>
                        handleSettingChange("hourlyTravelTime", Number(e.target.value))
                      }
                      className="w-40 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                    >
                      {hourlyTravelTimeOptions.map((opt) => (
                        <option key={opt.value} value={String(opt.value)}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="number"
                      step="0.01"
                      value={settings.hourlyTravelTime}
                      onChange={(e) =>
                        handleSettingChange(
                          "hourlyTravelTime",
                          parseFloat(e.target.value)
                        )
                      }
                      className="w-32 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  )}

                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Contacts Tab Component

const ContactsTab = ({ clientData, client, onRefresh }) => {
  const [contacts, setContacts] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showContactDetail, setShowContactDetail] = useState(false);

  const [selectedContact, setSelectedContact] = useState(null);

  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    extended_duration_passcode: "",
    location_id: "",
    role: "",
    sendAccountInvite: false,
  });

  const [loading, setLoading] = useState(false);
  const [initialContactSnapshot, setInitialContactSnapshot] = useState(null);
  const [unsavedPromptOpen, setUnsavedPromptOpen] = useState(false);

  const contactsFilterPanel = useEmbeddedFilterLayout();
  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [sortBy, setSortBy] = useState("firstName");
  const [sortDirection, setSortDirection] = useState("asc");
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(100);

  const [contactActionBusyId, setContactActionBusyId] = useState(null);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [isSavingContact, setIsSavingContact] = useState(false);

  const editRoleDropdownRef = useRef(null);

  // Handler for closing the Role dropdown inside the Edit Modal when clicking outside
  useEffect(() => {
    function handleEditRoleClickOutside(e) {
      if (showRoleDropdown && editRoleDropdownRef.current && !editRoleDropdownRef.current.contains(e.target)) {
        setShowRoleDropdown(false);
      }
    }
    if (showRoleDropdown) {
      document.addEventListener("mousedown", handleEditRoleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleEditRoleClickOutside);
  }, [showRoleDropdown]);

  const locationFilterOptions = useMemo(
    () =>
      locations
        .map((loc) => {
          const name = String(loc?.name || "").trim();
          return name ? { value: name, label: name } : null;
        })
        .filter(Boolean),
    [locations],
  );

  const roleFilterOptions = useMemo(
    () =>
      roles
        .map((role) => {
          const name = String(role?.name || "").trim();
          return name ? { value: name, label: name } : null;
        })
        .filter(Boolean),
    [roles],
  );

  const contactsFilterChips = useMemo(
    () => buildClientContactsFilterChips({ selectedLocations, selectedRoles }),
    [selectedLocations, selectedRoles],
  );

  const contactsBase = Array.isArray(filteredContacts) ? filteredContacts : [];
  const visibleContacts = contactsBase.slice(0, pageSize);

  const clearContactsFilters = () => {
    setSelectedLocations([]);
    setSelectedRoles([]);
    setSortBy("firstName");
    setSortDirection("asc");
  };

  const removeContactsFilter = (key) => {
    if (key === "selectedLocations") setSelectedLocations([]);
    if (key === "selectedRoles") setSelectedRoles([]);
  };

  const handleContactHeaderSort = (key) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(key);
    setSortDirection("asc");
  };

  const toContactSnapshot = (form) => ({
    firstName: String(form?.firstName || ""),
    lastName: String(form?.lastName || ""),
    email: String(form?.email || ""),
    phone: String(form?.phone || ""),
    extended_duration_passcode: String(form?.extended_duration_passcode || ""),
    location_id: String(form?.location_id || ""),
    roles: Array.isArray(form?.roles) ? [...form.roles].sort() : [],
    sendAccountInvite: !!form?.sendAccountInvite,
  });

  useEffect(() => {
    if (showEditModal && !initialContactSnapshot) {
      setInitialContactSnapshot(toContactSnapshot(editForm));
    }
  }, [showEditModal, initialContactSnapshot, editForm]);

  const isContactModalDirty = (() => {
    if (!showEditModal) return false;
    const baseline = initialContactSnapshot || toContactSnapshot({});
    return JSON.stringify(toContactSnapshot(editForm)) !== JSON.stringify(baseline);
  })();

  const requestCloseContactModal = () => {
    if (!isContactModalDirty) {
      setShowEditModal(false);
      setInitialContactSnapshot(null);
      return;
    }
    setUnsavedPromptOpen(true);
  };
  // -----------------------------

  // fetch roles
  const fetchRoles = async () => {
    try {
      const res = await API.get("/api/personnel/roles", { params: { level: "client" } });
      const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
      setRoles(rows);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
      showError("Error fetching roles. Please try again.");
    }
  };

  //fetch locations
  const fetchLocations = async () => {
    if (!client?.id) {
      console.log("No client ID, skipping fetch locations");
      return;
    }

    try {
      console.log("Fetching locations for client:", client.id);
      const data = await API.get(
        `${BASE_URL}/api/clients/${client.id}/locations`
      );
      const payload = data?.data || {};
      const locationArray = Array.isArray(payload.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      console.log("Fetched", locationArray.length, "locations:", locationArray);
      setLocations(locationArray);
    } catch (err) {
      console.error("Fetch locations error:", err.response?.data || err.message);
      showError("Failed to load locations");
      setLocations([]);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchRoles();
    fetchLocations();
  }, [client?.id]);

  // Keep location empty by default for Add Contact.

  const fetchContacts = async () => {
    if (!client?.id) return;

    setLoading(true);

    try {
      const response = await API.get(
        `${BASE_URL}/api/clients/${client.id}/client-contacts`
      );

      const contactsData = response.data.data || [];
      console.log("Fetched contacts:", contactsData);

      setContacts(contactsData);
      setFilteredContacts(contactsData);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);

      showError("Failed to load contacts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const normalizeText = (text) => {
    return String(text ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  };

  const getContactRoleNames = (contact) => {
    if (Array.isArray(contact?.roles) && contact.roles.length) {
      return contact.roles
        .map((r) => String(r).trim().toLowerCase())
        .filter(Boolean);
    }
    if (contact?.role) {
      return String(contact.role)
        .split(",")
        .map((r) => r.trim().toLowerCase())
        .filter(Boolean);
    }
    return [];
  };

  const applyFilter = (
    searchValue = searchTerm,
    sortKey = sortBy,
    rolesValue = selectedRoles,
    locationsValue = selectedLocations,
    sortDir = sortDirection
  ) => {
    let filtered = [...contacts];

    /* ---------- SEARCH ---------- */
    const q = normalizeText(searchValue);
    if (q) {
      filtered = filtered.filter((c) => {
        const nameText = normalizeText(c.name);
        const emailText = normalizeText(c.email);
        const phoneText = normalizeText(c.phone);
        const roleText = normalizeText(
          Array.isArray(c.roles) ? c.roles.join(" ") : c.role,
        );
        const locationText = normalizeText(c.location);

        return (
          nameText.includes(q) ||
          emailText.includes(q) ||
          phoneText.includes(q) ||
          roleText.includes(q) ||
          locationText.includes(q)
        );
      });
    }

    /* ---------- ROLE FILTER ---------- */
    if (rolesValue.length > 0) {
      const selectedNames = rolesValue.map((name) =>
        String(name).trim().toLowerCase(),
      );
      filtered = filtered.filter((c) => {
        const contactRoles = getContactRoleNames(c);
        return selectedNames.some((name) => contactRoles.includes(name));
      });
    }

    /* ---------- LOCATION FILTER ---------- */
    if (locationsValue.length > 0) {
      filtered = filtered.filter((c) =>
        locationsValue.some((name) => String(name) === String(c.location))
      );
    }

    /* ---------- SORT ---------- */
    filtered.sort((a, b) => {
      let valA = "";
      let valB = "";

      if (sortKey === "firstName") {
        valA = a.name?.split(" ")[0] || "";
        valB = b.name?.split(" ")[0] || "";
      } else if (sortKey === "lastName") {
        valA = a.name?.split(" ").slice(1).join(" ") || "";
        valB = b.name?.split(" ").slice(1).join(" ") || "";
      }

      return sortDir === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    });

    /* Save filtered result */
    setFilteredContacts(filtered);
  };

  useEffect(() => {
    applyFilter();
  }, [
    searchTerm,
    sortBy,
    sortDirection,
    selectedRoles,
    selectedLocations,
    contacts,
  ]);

  const handleContactClick = (contact) => {
    setSelectedContact({
      ...contact,
      client_id: contact?.client_id ?? client?.id ?? client?.client_id ?? null,
    });
    setShowContactDetail(true);
  };

  const handleSaveContact = async () => {
    if (isSavingContact) return;

    // Validation
    if (!editForm.firstName || !editForm.firstName.trim()) {
      return showError("First Name is required");
    }
    if (!editForm.lastName || !editForm.lastName.trim()) {
      return showError("Last Name is required");
    }
    if (!editForm.email || !editForm.email.trim()) {
      return showError("Email is required");
    }
    if (!isValidEmail(editForm.email)) {
      return showError("Please enter a valid email address");
    }
    if (!editForm.phone) {
      return showError("Phone Number is required");
    }
    if (!isValidPhoneNumber(editForm.phone)) {
      return showError("Invalid phone number");
    }
    if (!editForm.roles || editForm.roles.length === 0) {
      return showError("At least one Role is required");
    }

    try {
      setIsSavingContact(true);
      // Convert camelCase to snake_case for backend
      const contactData = {
        first_name: editForm.firstName,
        last_name: editForm.lastName,
        email: editForm.email || null,
        phone: editForm.phone || null,
        extended_duration_passcode: editForm.extended_duration_passcode || null,
        location_id: editForm.location_id ? parseInt(editForm.location_id) : null,
        roles: editForm.roles && editForm.roles.length > 0 ? editForm.roles : null,
        send_account_invite: selectedContact ? false : !!editForm.sendAccountInvite,
      };

      console.log("Saving contact with data:", contactData);

      let contactIdToOpen = null;
      if (selectedContact) {
        await API.put(
          `${BASE_URL}/api/clients/${client.id}/client-contacts/${selectedContact.contact_id}`,
          contactData
        );
        contactIdToOpen = selectedContact.contact_id;
        showSuccess("Contact updated successfully!");
      } else {
        const response = await API.post(
          `${BASE_URL}/api/clients/${client.id}/client-contacts`,
          contactData
        );
        contactIdToOpen = response.data?.data?.contact_id || response.data?.contact_id;
        showSuccess("Contact added successfully!");
      }

      setShowEditModal(false);

      // Refresh contacts and open detail modal if we have an ID
      try {
        const response = await API.get(
          `${BASE_URL}/api/clients/${client.id}/client-contacts`
        );
        const contactsData = response.data.data || [];
        setContacts(contactsData);
        setFilteredContacts(contactsData);

        if (contactIdToOpen) {
          const newContact = contactsData.find(c => Number(c.contact_id) === Number(contactIdToOpen));
          if (newContact) {
            setSelectedContact(newContact);
            setShowContactDetail(true);
          }
        }
      } catch (err) {
        console.error("Error refreshing contacts after save:", err);
      }
    } catch (error) {
      console.error("Failed to save contact:", error);
      console.error("Error response:", error.response?.data);
      console.error("Form data sent:", editForm);
      console.error("Client ID:", client?.id);

      showError(error.response?.data?.message || "Failed to save contact. Please try again.");
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!contactId || isNaN(contactId)) {
      showError("Invalid contact ID");
      return;
    }

    // Protection: check if this is a default contact or supervisor
    const prof = clientData?.profile || clientData || {};
    const isDefault =
      contactId === prof.default_contact_account_id ||
      contactId === prof.default_supervisor_account_id;

    if (isDefault) {
      showError("Cannot delete this contact because it is set as the Default Contact or Default Supervisor. Please change the default person in the Profile tab first.");
      return;
    }

    const confirmed = await showConfirm("Are you sure you want to delete this contact?", "Confirm");
    if (!confirmed) return;

    try {
      await API.delete(
        `${BASE_URL}/api/clients/${client.id}/client-contacts/${contactId}`
      );

      showSuccess("Contact deleted successfully!");
      fetchContacts();
    } catch (error) {
      console.error("Failed to delete contact:", error);
      showError("Failed to delete contact. Please try again.");
    }
  };

  const handleSetContactInactive = async (contact) => {
    const contactId = Number(contact?.contact_id);
    if (!contactId || !client?.id) return;
    const confirmed = await showConfirm(
      "Set this contact as inactive for this client?",
      "Confirm"
    );
    if (!confirmed) return;
    try {
      setContactActionBusyId(contactId);
      await API.put(
        `${BASE_URL}/api/clients/${client.id}/client-contacts/${contactId}/state`,
        { state: "inactive" }
      );
      showSuccess("Contact set as inactive");
      await fetchContacts();
    } catch (error) {
      console.error("Set contact inactive failed:", error);
      showError(error?.response?.data?.message || "Failed to set contact inactive");
    } finally {
      setContactActionBusyId(null);
    }
  };

  const handleRemoveContactFromClient = async (contact) => {
    const contactId = Number(contact?.contact_id);
    if (!contactId || !client?.id) return;
    const confirmed = await showConfirm(
      "Remove this contact from this client?",
      "Confirm"
    );
    if (!confirmed) return;
    try {
      setContactActionBusyId(contactId);
      await API.delete(
        `${BASE_URL}/api/clients/${client.id}/client-contacts/${contactId}/unlink`
      );
      showSuccess("Contact removed from client");
      await fetchContacts();
    } catch (error) {
      console.error("Remove from client failed:", error);
      showError(error?.response?.data?.message || "Failed to remove contact from client");
    } finally {
      setContactActionBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-sidebar-active" />
          Contacts
        </h3>

        <AtlasListToolbar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search contacts..."
          onFilterClick={contactsFilterPanel.toggle}
          filterOpen={contactsFilterPanel.isOpen}
          onRefresh={fetchContacts}
          refreshDisabled={loading}
          refreshSpinning={loading}
          resultsMeta={{
            startRow: contactsBase.length === 0 ? 0 : 1,
            endRow: Math.min(pageSize, contactsBase.length),
            totalCount: contactsBase.length,
            pageSize,
            pageSizeOptions: [10, 20, 50, 100],
            onPageSizeChange: setPageSize,
            sortBy,
            sortOrder: sortDirection,
            sortOptions: CLIENT_CONTACTS_SORT_OPTIONS,
            onSortByChange: setSortBy,
            onSortOrderToggle: () =>
              setSortDirection((prev) => (prev === "asc" ? "desc" : "asc")),
          }}
          actions={
            <button
              type="button"
              onClick={() => {
                setSelectedContact(null);

                const nextForm = {
                  firstName: "",
                  lastName: "",
                  email: "",
                  phone: "",
                  extended_duration_passcode: "",
                  location_id: "",
                  roles: [],
                  sendAccountInvite: false,
                };
                setEditForm(nextForm);
                setInitialContactSnapshot({
                  ...nextForm,
                  location_id: String(nextForm.location_id || ""),
                  roles: [],
                });

                setShowEditModal(true);
              }}
              className="whitespace-nowrap rounded-lg bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
            >
              Add Contact
            </button>
          }
          chipBar={
            contactsFilterChips.length > 0 ? (
              <AtlasFilterChipBar
                chips={contactsFilterChips}
                onChipClick={(chip) => contactsFilterPanel.focusField(chip.fieldId)}
                onRemove={(chip) => removeContactsFilter(chip.key)}
                onClearAll={clearContactsFilters}
              />
            ) : null
          }
        />

        <EmbeddedFilterPanel
          isOpen={contactsFilterPanel.isOpen}
          onClose={contactsFilterPanel.close}
          onClear={clearContactsFilters}
          footerMode="clearOnly"
        >
          <FilterMultiSelect
            id="contacts-locations"
            label="Locations"
            labelVariant="embedded"
            options={locationFilterOptions}
            value={selectedLocations}
            onChange={setSelectedLocations}
            placeholder="Select locations..."
          />
          <FilterMultiSelect
            id="contacts-roles"
            label="Roles"
            labelVariant="embedded"
            options={roleFilterOptions}
            value={selectedRoles}
            onChange={setSelectedRoles}
            placeholder="Select roles..."
          />
          <FilterSelect
            id="contacts-sort-by"
            label="Sort By"
            labelVariant="embedded"
            value={sortBy}
            onChange={setSortBy}
            options={CLIENT_CONTACTS_SORT_OPTIONS}
            placeholder={null}
          />
          <FilterSelect
            id="contacts-sort-direction"
            label="Sort Direction"
            labelVariant="embedded"
            value={sortDirection}
            onChange={setSortDirection}
            options={[
              { value: "asc", label: "Ascending" },
              { value: "desc", label: "Descending" },
            ]}
            placeholder={null}
          />
        </EmbeddedFilterPanel>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-active mx-auto mb-3"></div>
            <div className="text-gray-500">Loading contacts...</div>
          </div>
        ) : contacts.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl py-12 text-center">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <div className="text-gray-500 text-lg font-medium">No contacts found</div>
            <div className="text-gray-400 text-sm">Add your first contact to get started..</div>
          </div>
        ) : (
          <div className="space-y-4 w-full max-w-full">
            <div className="atlas-list-table-wrap bg-card rounded-lg border border-border overflow-x-auto block max-w-full no-mobile-table-stack">
              <table className="w-full table-auto bg-white border-collapse min-w-[800px]">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <tr>
                    <SortableTableHeader
                      label="Name"
                      sortKey={sortBy}
                      sortBy={sortBy}
                      sortOrder={sortDirection}
                      onSort={handleContactHeaderSort}
                      className="text-xs uppercase tracking-wider"
                    />
                    <TableHeaderCell label="Role" className="text-xs uppercase tracking-wider" />
                    <TableHeaderCell label="Email" className="text-xs uppercase tracking-wider" />
                    <TableHeaderCell label="Phone" className="text-xs uppercase tracking-wider" />
                    <TableHeaderCell label="Actions" className="text-xs uppercase tracking-wider text-right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {visibleContacts.map((contact) => (
                    <tr key={contact.contact_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-sm font-medium text-red-600 hover:text-red-800 cursor-pointer" onClick={() => handleContactClick(contact)}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-50 text-red-600 rounded-full flex items-center justify-center shrink-0 border border-red-100 overflow-hidden">
                            {contact.profile_picture ? (
                              <img
                                src={resolveBackendUrl(contact.profile_picture)}
                                alt={contact.name || "Contact"}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  const fallback = e.currentTarget.nextElementSibling;
                                  if (fallback) fallback.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <span
                              className="text-sm font-bold"
                              style={{ display: contact.profile_picture ? "none" : "flex" }}
                            >
                              {contact.first_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            <span className="font-semibold text-red-600">{contact.name}</span>
                            {(() => {
                              const prof = clientData?.profile || clientData || {};
                              const isDefContact = contact.contact_id === prof.default_contact_account_id;
                              const isDefSup = contact.contact_id === prof.default_supervisor_account_id;
                              if (isDefContact || isDefSup) {
                                return (
                                  <span className="inline-flex items-center text-[10px] font-bold text-black uppercase tracking-tighter">
                                    Default {isDefContact && isDefSup ? "Contact & Supervisor" : isDefContact ? "Contact" : "Supervisor"}
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">
                        {Array.isArray(contact.roles) ? contact.roles.join(', ') : (contact.role || "-")}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">{contact.email || "-"}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{contact.phone || "-"}</td>
                      <td className="px-3 py-2 text-sm align-top">
                        {(() => {
                          const prof = clientData?.profile || clientData || {};
                          const isDefault = contact.contact_id === prof.default_contact_account_id || contact.contact_id === prof.default_supervisor_account_id;
                          const isBusy = contactActionBusyId === contact.contact_id;
                          return (
                            <ActionDropdown
                              disabled={isBusy}
                              actions={[
                                {
                                  label: "Set as Inactive",
                                  onClick: () => {
                                    if (isDefault || isBusy) return;
                                    handleSetContactInactive(contact);
                                  },
                                  className: isDefault ? "text-gray-300" : "text-gray-700",
                                },
                                {
                                  label: "Remove From Client",
                                  onClick: () => {
                                    if (isDefault || isBusy) return;
                                    handleRemoveContactFromClient(contact);
                                  },
                                  className: isDefault ? "text-gray-300" : "text-red-500",
                                },
                              ]}
                            />
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>

      {/* Edit Contact Modal */}

      {
        showEditModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] relative border border-gray-100 flex flex-col">
              {/* Header - Sticky */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 md:p-8 z-10">
                <h4 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="h-5 w-5 text-sidebar-active" />
                  {selectedContact ? "Edit Contact" : "Add Contact"}
                </h4>
              </div>

              {/* Form Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-6 md:p-8">
                <div className="space-y-6">
                  {(() => {
                    console.log("Modal rendering with editForm:", editForm);
                    return null;
                  })()}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        First Name <span className="text-red-500">*</span>
                      </label>

                      <input
                        type="text"
                        value={editForm.firstName}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            firstName: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 text-sm md:text-base focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active transition-colors"
                        placeholder="Enter first name"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Last Name <span className="text-red-500">*</span>
                      </label>

                      <input
                        type="text"
                        value={editForm.lastName}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            lastName: e.target.value,
                          }))
                        }
                        className="w-full border border-gray-300 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 text-sm md:text-base focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active transition-colors"
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Email <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, email: e.target.value }))
                      }
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active transition-colors"
                      placeholder="Enter email address"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Phone Number <span className="text-red-500">*</span>
                    </label>

                    <PhoneInput
                      international
                      defaultCountry="US"
                      value={editForm.phone || ""}
                      onChange={(phone) =>
                        setEditForm((prev) => ({ ...prev, phone: phone || "" }))
                      }
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active transition-colors"
                      placeholder="Enter phone number"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Extended Duration Passcode
                    </label>

                    <input
                      type="text"
                      value={editForm.extended_duration_passcode || ""}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, extended_duration_passcode: e.target.value }))
                      }
                      maxLength={6}
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active transition-colors"
                      placeholder="Enter passcode (max 6 digits)"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Location
                    </label>

                    {selectedContact ? (
                      <div className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-gray-100 text-gray-700">
                        {locations.find(l => String(l.location_id) === String(editForm.location_id))?.name || "No location selected"}
                      </div>
                    ) : (
                      <select
                        value={editForm.location_id || ""}
                        onChange={(e) =>
                          setEditForm((prev) => ({ ...prev, location_id: e.target.value }))
                        }
                        className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active transition-colors"
                      >
                        <option value="">
                          {locations.length === 0 ? "No locations available" : "Select a location"}
                        </option>

                        {locations && locations.map((location) => (
                          <option key={location.location_id} value={location.location_id}>
                            {location.name}
                          </option>
                        ))}
                      </select>
                    )}

                    {/* Show "This is billing address" if the selected location is primary */}
                    {(() => {
                      const selectedLoc = locations.find(l => String(l.location_id) === String(editForm.location_id));
                      if (selectedLoc && Number(selectedLoc.primary) === 1) {
                        return (
                          <p className="text-xs text-gray-500 mt-1">
                            This is the billing address
                          </p>
                        );
                      }
                      return null;
                    })()}

                    {locations.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Tip: Locations will be available once client locations are set up
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Role <span className="text-red-500">*</span>
                    </label>

                    {/* Multi-select dropdown for roles with chips display */}
                    <div className="relative" ref={editRoleDropdownRef}>
                      <div className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white min-h-[44px] flex flex-wrap items-center gap-2 focus-within:ring-2 focus-within:ring-sidebar-active/20">
                        {/* Selected roles as chips */}
                        {(editForm.roles || []).map((role) => (
                          <div
                            key={role}
                            className="inline-flex items-center gap-1 bg-sidebar-active text-white px-3 py-1 rounded-full text-sm font-medium"
                          >
                            <span>{role}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setEditForm((prev) => ({
                                  ...prev,
                                  roles: (prev.roles || []).filter((r) => r !== role),
                                }));
                              }}
                              className="ml-1 hover:text-gray-200"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}

                        {/* Dropdown trigger input */}
                        <input
                          type="text"
                          placeholder={
                            (editForm.roles || []).length === 0 ? "Select roles..." : ""
                          }
                          className="flex-1 border-0 outline-0 bg-transparent min-w-[100px] text-sm"
                          readOnly
                          onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                        />
                      </div>

                      {/* Dropdown menu */}
                      <div
                        className={`absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-xl shadow-lg z-10 max-h-48 overflow-y-auto ${showRoleDropdown ? 'block' : 'hidden'}`}
                      >
                        {roles.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            No client-level roles found. Add them under Admin → Accounts (Roles).
                          </div>
                        ) : (
                          roles.map((role) => {
                            const name = role?.name;
                            if (!name) return null;
                            const rid = role.id ?? role.role_id;
                            return (
                              <label
                                key={rid ?? name}
                                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                              >
                                <input
                                  type="checkbox"
                                  checked={(editForm.roles || []).includes(name)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setEditForm((prev) => ({
                                        ...prev,
                                        roles: [...(prev.roles || []), name],
                                      }));
                                    } else {
                                      setEditForm((prev) => ({
                                        ...prev,
                                        roles: (prev.roles || []).filter((r) => r !== name),
                                      }));
                                    }
                                  }}
                                  className="w-4 h-4 border border-gray-300 rounded cursor-pointer accent-sidebar-active"
                                />
                                <span className="text-sm text-gray-700">{name}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4">
                    <input
                      type="checkbox"
                      id="sendAccountInvite"
                      checked={editForm.sendAccountInvite || false}
                      onChange={(e) =>
                        setEditForm((prev) => ({ ...prev, sendAccountInvite: e.target.checked }))
                      }
                      className="w-5 h-5 border border-gray-300 rounded cursor-pointer accent-sidebar-active"
                    />
                    <label htmlFor="sendAccountInvite" className="text-sm font-medium text-gray-700 cursor-pointer">
                      Send account invite
                    </label>
                  </div>
                </div>
              </div>

              {/* Footer Buttons - Sticky */}
              <div className="sticky bottom-0 border-t border-gray-200 bg-white p-6 md:p-8 flex justify-end gap-2 md:gap-3">
                <button
                  onClick={requestCloseContactModal}
                  className="px-3 md:px-6 py-2 md:py-3 bg-gray-100 text-gray-700 rounded-lg md:rounded-xl hover:bg-gray-200 transition-colors font-semibold text-sm md:text-base"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSaveContact}
                  disabled={isSavingContact}
                  className="px-3 md:px-6 py-2 md:py-3 bg-sidebar-active text-white rounded-lg md:rounded-xl hover:bg-red-600 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl text-sm md:text-base disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSavingContact
                    ? selectedContact
                      ? "Updating..."
                      : "Adding..."
                    : `${selectedContact ? "Update" : "Add"} Contact`}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Contact Detail Modal */}
      <ContactDetailModal
        contact={selectedContact}
        clientId={client?.id ?? client?.client_id}
        isOpen={showContactDetail}
        onClose={() => {
          setShowContactDetail(false);
          setSelectedContact(null);
        }}
        onSaved={fetchContacts}
      />
      <UnsavedChangesDialog
        isOpen={unsavedPromptOpen}
        onSaveAndContinue={async () => {
          await handleSaveContact();
          setUnsavedPromptOpen(false);
          setInitialContactSnapshot(null);
        }}
        onDiscardAndContinue={() => {
          setShowEditModal(false);
          setUnsavedPromptOpen(false);
          setInitialContactSnapshot(null);
        }}
        onReview={() => setUnsavedPromptOpen(false)}
      />
    </div >
  );
};

// Positions Tab Component

const PositionsTab = ({ clientData, client, onRefresh }) => {
  const [positions, setPositions] = useState([]);
  const positionsRequestRef = useRef(0);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [initialPositionEditSnapshot, setInitialPositionEditSnapshot] = useState(null);
  const [initialPositionCreateSnapshot, setInitialPositionCreateSnapshot] = useState(null);
  const [unsavedPromptOpen, setUnsavedPromptOpen] = useState(false);
  const [unsavedSaveAction, setUnsavedSaveAction] = useState(null);
  const [unsavedDiscardAction, setUnsavedDiscardAction] = useState(null);

  const [selectedPosition, setSelectedPosition] = useState(null);
  const [createPositionOptions, setCreatePositionOptions] = useState([]);
  const [createPositionOptionsFiltered, setCreatePositionOptionsFiltered] = useState([]);
  const [createDivisionOptions, setCreateDivisionOptions] = useState([]);
  const [createPositionId, setCreatePositionId] = useState("");
  const [createDivisionId, setCreateDivisionId] = useState("");
  const [createOptionsLoading, setCreateOptionsLoading] = useState(false);
  const [createPositionQuery, setCreatePositionQuery] = useState("");
  const [showCreatePositionSuggestions, setShowCreatePositionSuggestions] = useState(false);
  const [createDivisionQuery, setCreateDivisionQuery] = useState("");
  const [showCreateDivisionSuggestions, setShowCreateDivisionSuggestions] = useState(false);

  const [editForm, setEditForm] = useState({
    name: "",

    billRate: "",

    payRate: "",

    uniform: "",

    unpaidBreak: false,
    skill_ids: [],
    certification_ids: [],
  });
  const [mappedDivisionName, setMappedDivisionName] = useState("");
  const [skillsOptions, setSkillsOptions] = useState([]);
  const [certificationOptions, setCertificationOptions] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredPositions, setFilteredPositions] = useState([]);

  const positionsFilterPanel = useEmbeddedFilterLayout();

  const [filterDivision, setFilterDivision] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  const [sortBy, setSortBy] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [pageSize, setPageSize] = useState(100);

  const [divisionList, setDivisionList] = useState([]);
  const createPositionRef = useRef(null);
  const createDivisionRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const toPositionSnapshot = (form, extra = {}) => ({
    name: String(form?.name || ""),
    billRate: String(form?.billRate || ""),
    payRate: String(form?.payRate || ""),
    uniform: String(form?.uniform || ""),
    unpaidBreak: !!form?.unpaidBreak,
    skill_ids: Array.isArray(form?.skill_ids) ? [...form.skill_ids].sort((a, b) => Number(a) - Number(b)) : [],
    certification_ids: Array.isArray(form?.certification_ids) ? [...form.certification_ids].sort((a, b) => Number(a) - Number(b)) : [],
    createPositionId: String(extra?.createPositionId || ""),
    createDivisionId: String(extra?.createDivisionId || ""),
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showCreatePositionSuggestions &&
        createPositionRef.current &&
        !createPositionRef.current.contains(event.target)
      ) {
        setShowCreatePositionSuggestions(false);
      }
      if (
        showCreateDivisionSuggestions &&
        createDivisionRef.current &&
        !createDivisionRef.current.contains(event.target)
      ) {
        setShowCreateDivisionSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCreatePositionSuggestions, showCreateDivisionSuggestions]);

  useEffect(() => {
    fetchPositions();
  }, [client?.id, showDeleted]);

  useEffect(() => {
    const loadRequirements = async () => {
      if (!showEditModal && !showCreateModal) return;
      try {
        const [skillsRes, certsRes] = await Promise.all([
          fetch(`${BASE_URL}/api/personnel/skills`, { credentials: "include" }),
          fetch(`${BASE_URL}/api/personnel/certifications`, {
            credentials: "include",
          }),
        ]);
        const sJson = await skillsRes.json().catch(() => ({}));
        const cJson = await certsRes.json().catch(() => ({}));
        const sList = Array.isArray(sJson?.data)
          ? sJson.data
          : Array.isArray(sJson)
            ? sJson
            : [];
        const cList = Array.isArray(cJson?.data)
          ? cJson.data
          : Array.isArray(cJson)
            ? cJson
            : [];
        setSkillsOptions(sList);
        setCertificationOptions(cList);
      } catch (err) {
        console.error("Failed to load skills/certifications", err);
        setSkillsOptions([]);
        setCertificationOptions([]);
      }
    };
    loadRequirements();
  }, [showEditModal, showCreateModal]);

  //fetch division to dropdown
  useEffect(() => {
    fetchDivisions();
  }, [client?.id]);

  const positionSuggestions = (query) => {
    const q = String(query || "").toLowerCase().trim();
    const mapped = (createPositionOptionsFiltered || createPositionOptions || []).map((p) => ({ id: p.id, name: String(p.name || "") }));
    if (q === "") return mapped;
    return mapped.filter((p) => p.name.toLowerCase().includes(q));
  };

  const divisionSuggestions = (query) => {
    const q = String(query || "").toLowerCase().trim();
    const mapped = (createDivisionOptions || []).map((d) => ({ id: d.id || d.division_id, name: String(d.name || "") }));
    if (q === "") return mapped;
    return mapped.filter((d) => d.name.toLowerCase().includes(q));
  };

  /** When a division is preselected, keep query empty so the dropdown still lists all options. */
  const divisionSuggestionQuery =
    createDivisionId && !String(createDivisionQuery || "").trim()
      ? ""
      : createDivisionQuery;

  //!fetch divisions
  const fetchDivisions = async () => {
    try {
      console.log("[ClientDetailModal] Fetching all divisions for Positions filter");
      const res = await API.get(`${BASE_URL}/api/clients/divisions`);
      console.log("[ClientDetailModal] API response:", res.data);
      setDivisionList(res.data.data || []);
      console.log(
        `[ClientDetailModal] Loaded ${res.data.data?.length || 0} divisions for Positions filter`
      );
    } catch (err) {
      console.error(
        "[ClientDetailModal] Failed to load all divisions for Positions filter",
        err.response?.status,
        err.response?.data,
        err.message
      );
    }
  };

  const loadCreateOptions = async () => {
    if (!client?.id) return;
    try {
      setCreateOptionsLoading(true);
      const posRes = await API.get(
        `${BASE_URL}/api/clients/positions/system-by-client/${client.id}`
      );

      const rows = Array.isArray(posRes.data?.data)
        ? posRes.data.data.map((p) => ({
            id: p.id,
            name: p.name || p.position_name || "",
          }))
        : [];
      setCreatePositionOptions(rows);
      setCreatePositionOptionsFiltered(rows);
      setCreateDivisionOptions([]);
    } catch (err) {
      console.error("Failed to load create position options", err);
      setCreatePositionOptions([]);
      setCreatePositionOptionsFiltered([]);
      setCreateDivisionOptions([]);
    } finally {
      setCreateOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (!showCreateModal) return;
    loadCreateOptions();
  }, [showCreateModal, client?.id]);

  const fetchPositions = async () => {
    if (!client?.id) return;

    const requestId = ++positionsRequestRef.current;
    setLoading(true);

    try {
      const response = await API.get(
        `${BASE_URL}/api/clients/${client.id}/positions`,
        {
          params: {
            showDeleted: showDeleted ? 1 : 0,
            _: Date.now(),
          },
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        }
      );

      if (requestId !== positionsRequestRef.current) return;
      console.log("=== POSITIONS DEBUG ===");
      console.log("Full API response:", response.data);
      console.log("Positions data array:", response.data.data);
      console.log("Positions count:", response.data.data?.length);
      console.log("First position:", response.data.data?.[0]);
      const positionData = response.data.data || [];
      setPositions(positionData);
      setFilteredPositions(positionData);
    } catch (error) {
      console.error("Failed to fetch positions:", error);
    } finally {
      setLoading(false);
    }
  };

  const normalizeText = (text) => {
    return String(text ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  };

  const getPositionState = (p) =>
    String(p?.state || (p?.deleted ? "deleted" : "active")).toLowerCase();
  const getPositionDivisionId = (p) =>
    p?.division_id || p?.divisionId || p?.client_division_id || null;
  const getPositionDivisionName = (p) =>
    p?.division || p?.divisionName || "";

  //! /apply filter
  const applyFilters = (
    search = searchTerm,
    division = filterDivision,
    deleted = showDeleted,
    sortKey = sortBy,
    direction = sortDirection
  ) => {
    let data = [...positions];

    // 1. Division filter
    if (Array.isArray(division) && division.length > 0) {
      data = data.filter(
        (p) => division.some((id) => String(getPositionDivisionId(p)) === String(id))
      );
    }

    // 2. Deleted filter
    if (deleted) {
      data = data.filter((p) => getPositionState(p) === "deleted");
    } else {
      data = data.filter((p) => getPositionState(p) !== "deleted");
    }

    // 3. Search by name
    if (search) {
      const q = normalizeText(search);

      data = data.filter((p) => {
        const nameText = normalizeText(p.name);
        const divisionText = normalizeText(getPositionDivisionName(p));
        const uniformText = normalizeText(p.uniform);

        return (
          nameText.includes(q) ||
          divisionText.includes(q) ||
          uniformText.includes(q)
        );
      });
    }

    // 4. Sort
    data.sort((a, b) => {
      if (sortKey === "name") {
        return direction === "asc"
          ? (a.name || "").localeCompare(b.name || "")
          : (b.name || "").localeCompare(a.name || "");
      }
      return 0;
    });

    setFilteredPositions(data);
  };

  useEffect(() => {
    applyFilters();
  }, [positions, searchTerm, filterDivision, showDeleted, sortBy, sortDirection]);

  const handlePositionClick = async (position) => {
    try {
      // Fetch the latest full position details (including skills/certs/division)
      const res = await API.get(
        `${BASE_URL}/api/clients/positions/${position.id}`
      );
      const detailed = res?.data?.data || position;
      setSelectedPosition(detailed);

      const nextForm = {
        name: detailed.name,
        billRate: detailed.bill_rate || detailed.billRate || "",
        payRate: detailed.pay_rate || detailed.payRate || "",
        uniform: detailed.uniform || "",
        unpaidBreak: Boolean(detailed.unpaid_break),
        skill_ids: Array.isArray(detailed.skill_ids) ? detailed.skill_ids : [],
        certification_ids: Array.isArray(detailed.certification_ids)
          ? detailed.certification_ids
          : [],
      };
      setEditForm(nextForm);
      setInitialPositionEditSnapshot(toPositionSnapshot(nextForm));
      setMappedDivisionName(detailed.divisionName || detailed.division || "");
    } catch (err) {
      console.warn(
        "Failed to fetch detailed position; falling back to basic object",
        err
      );
      setSelectedPosition(position);
      const nextForm = {
        name: position.name,
        billRate: position.bill_rate || position.billRate || "",
        payRate: position.pay_rate || position.payRate || "",
        uniform: position.uniform || "",
        unpaidBreak: Boolean(position.unpaid_break),
        skill_ids: [],
        certification_ids: [],
      };
      setEditForm(nextForm);
      setInitialPositionEditSnapshot(toPositionSnapshot(nextForm));
      setMappedDivisionName(position.divisionName || position.division || "");
    } finally {
      setShowEditModal(true);
    }
  };

  const handleSavePosition = async ({
    positionContext = null,
    closeCreateModal = false,
  } = {}) => {
    try {
      const activePosition = positionContext || selectedPosition;
      let targetPositionId = activePosition?.id || null;

      // Mandatory 2-step create flow: if this came from Add Position mapping step,
      // map position+division first, then save details from this form.
      if (activePosition?.needsMapping) {
        const mapPayload = {
          clientId: client.id,
          positionId: activePosition.id,
          positionName: null,
          divisionId: activePosition.divisionId,
        };
        const mapRes = await API.post(`${BASE_URL}/api/clients/positions`, mapPayload);
        targetPositionId =
          mapRes?.data?.positionId || mapRes?.data?.id || activePosition.id;
      }

      const positionData = {
        name: editForm.name || activePosition?.name || "",
        bill_rate: editForm.billRate,
        pay_rate: editForm.payRate,
        uniform: editForm.uniform,
        unpaid_break: editForm.unpaidBreak ? 1 : 0,
        skill_ids: Array.isArray(editForm.skill_ids) ? editForm.skill_ids : [],
        certification_ids: Array.isArray(editForm.certification_ids)
          ? editForm.certification_ids
          : [],
      };

      if (targetPositionId) {
        await API.put(
          `${BASE_URL}/api/clients/${client.id}/positions/${targetPositionId}`,
          positionData
        );
      } else {
        const createResponse = await API.post(
          `${BASE_URL}/api/clients/${client.id}/positions`,
          positionData
        );
        targetPositionId =
          createResponse?.data?.positionId || createResponse?.data?.id || null;
      }

      if (closeCreateModal) {
        setShowCreateModal(false);
      } else {
        setShowEditModal(false);
      }

      setSelectedPosition(null);
      setMappedDivisionName("");

      const savedPositionId = Number(targetPositionId || 0);
      const savedPosition = {
        id: savedPositionId || `pending-${Date.now()}`,
        name: positionData.name,
        bill_rate: positionData.bill_rate,
        pay_rate: positionData.pay_rate,
        uniform: positionData.uniform,
        unpaid_break: positionData.unpaid_break,
        state: "active",
        division_id: activePosition?.divisionId || null,
        division: activePosition?.division || "",
        requirements: "-",
      };
      setPositions((current) => {
        const index = savedPositionId
          ? current.findIndex((position) => Number(position.id) === savedPositionId)
          : -1;
        if (index < 0) return [...current, savedPosition];
        const next = [...current];
        next[index] = { ...next[index], ...savedPosition };
        return next;
      });
      setFilteredPositions((current) => {
        const index = savedPositionId
          ? current.findIndex((position) => Number(position.id) === savedPositionId)
          : -1;
        if (index < 0) return [...current, savedPosition];
        const next = [...current];
        next[index] = { ...next[index], ...savedPosition };
        return next;
      });

      void fetchPositions();
      if (onRefresh) void onRefresh();
    } catch (error) {
      console.error("Failed to save position:", error);
    }
  };

  const openCreatePositionModal = () => {
    setSelectedPosition(null);
    setCreatePositionId("");
    setCreateDivisionId("");
    setCreatePositionQuery("");
    setCreateDivisionQuery("");
    setShowCreatePositionSuggestions(false);
    setShowCreateDivisionSuggestions(false);
    setMappedDivisionName("");
    const nextForm = {
      name: "",
      billRate: "",
      payRate: "",
      uniform: "",
      unpaidBreak: false,
      skill_ids: [],
      certification_ids: [],
    };
    setEditForm(nextForm);
    setInitialPositionCreateSnapshot(
      toPositionSnapshot(nextForm, { createPositionId: "", createDivisionId: "" })
    );
    setShowCreateModal(true);
  };

  const handleCreatePositionSubmit = async () => {
    // try to resolve typed values to IDs if user typed but didn't pick
    let resolvedPositionId = createPositionId;
    if (!resolvedPositionId && createPositionQuery && createPositionQuery.trim()) {
      const match = (createPositionOptionsFiltered || createPositionOptions || []).find(
        (p) => String(p.name || "").trim().toLowerCase() === String(createPositionQuery || "").trim().toLowerCase()
      );
      if (match) {
        resolvedPositionId = String(match.id);
        setCreatePositionId(resolvedPositionId);
      }
    }

    let resolvedDivisionId = createDivisionId;
    if (!resolvedDivisionId && createDivisionQuery && createDivisionQuery.trim()) {
      const query = String(createDivisionQuery || "").trim().toLowerCase();
      const matchD = createDivisionOptions.find(
        (d) => String(d.name || "").trim().toLowerCase() === query
      );
      if (matchD) {
        resolvedDivisionId = String(matchD.id || matchD.division_id);
        setCreateDivisionId(resolvedDivisionId);
      }
    }

    if (!resolvedPositionId) return showError("Position is required");
    if (!resolvedDivisionId) return showError("Division is required");
    // use resolved ids for rest of logic
    const billRateValue = String(editForm.billRate ?? "").trim();
    const payRateValue = String(editForm.payRate ?? "").trim();
    if (!billRateValue) return showError("Bill Rate is required");
    if (!payRateValue) return showError("Pay Rate is required");
    const foundPos = (createPositionOptionsFiltered || createPositionOptions || []).find(
      (p) => String(p.id) === String(resolvedPositionId)
    );
    const foundDiv = createDivisionOptions.find(
      (d) => String(d.id || d.division_id) === String(resolvedDivisionId)
    );

    const selectedName = foundPos?.name || "";
    const selectedDivisionName = foundDiv?.name || "";
    const context = {
      id: resolvedPositionId,
      name: selectedName,
      divisionId: resolvedDivisionId,
      division: selectedDivisionName,
      needsMapping: true,
    };

    setMappedDivisionName(selectedDivisionName);
    await handleSavePosition({
      positionContext: context,
      closeCreateModal: true,
    });
  };

  const getDivisionFromPosition = (position) => {
    if (!position) return null;

    const rawDivisionId =
      position.divisionId ||
      position.division_id ||
      position.client_division_id ||
      null;

    if (rawDivisionId) {
      const foundById = createDivisionOptions.find(
        (d) => String(d.id || d.division_id) === String(rawDivisionId)
      );
      if (foundById) {
        return {
          id: foundById.id || foundById.division_id,
          name: foundById.name || "",
        };
      }
    }

    const rawDivisionName = position.division || position.divisionName || "";
    if (rawDivisionName) {
      const foundByName = createDivisionOptions.find(
        (d) =>
          String(d.name || "").trim().toLowerCase() ===
          String(rawDivisionName).trim().toLowerCase()
      );
      if (foundByName) {
        return {
          id: foundByName.id || foundByName.division_id,
          name: foundByName.name || "",
        };
      }
      return { id: "", name: rawDivisionName };
    }

    return null;
  };

  const fetchDivisionForPosition = async (positionId, fallbackPosition = null) => {
    if (!positionId) return null;
    try {
      const res = await API.get(
        `${BASE_URL}/api/clients/positions/${positionId}`
      );
      return getDivisionFromPosition(res?.data?.data || fallbackPosition);
    } catch (err) {
      console.warn("Failed to fetch division for selected position", err);
      return getDivisionFromPosition(fallbackPosition);
    }
  };

  const clearCreateDivisionSelection = () => {
    setCreateDivisionId("");
    setCreateDivisionQuery("");
    setMappedDivisionName("");
    setShowCreateDivisionSuggestions(false);
  };

  const clearCreatePositionSelection = () => {
    setCreatePositionId("");
    setCreatePositionQuery("");
    setShowCreatePositionSuggestions(false);
    setEditForm((prev) => ({
      ...prev,
      name: "",
      skill_ids: [],
      certification_ids: [],
    }));
    clearCreateDivisionSelection();
    setCreateDivisionOptions([]);
  };

  const loadSystemPositionTemplate = async (positionId) => {
    if (!positionId) return;
    try {
      const res = await API.get(
        `${BASE_URL}/api/admin/positions/${positionId}`
      );
      const data = res?.data?.data;
      if (!data) return;
      setEditForm((prev) => ({
        ...prev,
        name: data.position_name || data.name || prev.name,
        billRate:
          data.bill_rate != null ? String(data.bill_rate) : prev.billRate,
        payRate: data.pay_rate != null ? String(data.pay_rate) : prev.payRate,
        uniform: data.uniform ?? prev.uniform,
        unpaidBreak: !!data.unpaid_break,
        skill_ids: Array.isArray(data.skill_ids) ? data.skill_ids : [],
        certification_ids: Array.isArray(data.certification_ids)
          ? data.certification_ids
          : [],
      }));
    } catch (err) {
      console.error("Failed to load system position template", err);
    }
  };

  // When a position is selected, load divisions for that position scoped to this client.
  useEffect(() => {
    if (!showCreateModal) return;

    const loadDivisionsForPosition = async () => {
      if (!createPositionId || !client?.id) {
        setCreateDivisionOptions([]);
        clearCreateDivisionSelection();
        return;
      }

      try {
        setCreateOptionsLoading(true);
        const res = await API.get(
          `${BASE_URL}/api/clients/positions/${createPositionId}/divisions-by-client/${client.id}`
        );
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setCreateDivisionOptions(rows);
        if (rows.length > 0) {
          const first = rows[0];
          setCreateDivisionId(String(first.id));
          setCreateDivisionQuery("");
          setMappedDivisionName(first.name || "");
          setShowCreateDivisionSuggestions(false);
        } else {
          clearCreateDivisionSelection();
        }
      } catch (err) {
        console.error("Failed to load divisions for position", err);
        setCreateDivisionOptions([]);
        clearCreateDivisionSelection();
      } finally {
        setCreateOptionsLoading(false);
      }
    };

    loadDivisionsForPosition();
  }, [createPositionId, showCreateModal, client?.id]);

  const handleDeletePosition = async (positionId) => {
    const confirmed = await showConfirm("Are you sure you want to delete this position?", "Confirm");
    if (!confirmed) return;
    try {
      await API.delete(
        `${BASE_URL}/api/clients/${client.id}/positions/${positionId}`
      );

      await fetchPositions();
    } catch (error) {
      console.error("Failed to delete position:", error);
    }
  };

  const handleToggleArchivePosition = async (position) => {
    const positionId = position?.id;
    if (!positionId) return;
    const isArchived = getPositionState(position) === "archived";
    try {
      await API.put(
        `${BASE_URL}/api/clients/${client.id}/positions/${positionId}/${isArchived ? "unarchive" : "archive"}`
      );
      showSuccess(
        isArchived
          ? "Position unarchived successfully"
          : "Position archived successfully"
      );
      await fetchPositions();
    } catch (error) {
      console.error("Failed to toggle archive position:", error);
      showError(
        error?.response?.data?.message ||
        "Failed to update position archive state"
      );
    }
  };

  const positionsBase = Array.isArray(filteredPositions)
    ? filteredPositions
    : [];
  const visiblePositions = positionsBase.slice(0, pageSize);
  const divisionFilterOptions = useMemo(
    () =>
      (divisionList || []).map((d) => ({
        value: String(d.id || d.division_id),
        label: d.name || `Division ${d.id || d.division_id}`,
      })),
    [divisionList]
  );
  const positionsFilterChips = useMemo(
    () =>
      buildClientPositionsFilterChips(
        { filterDivision, showDeleted },
        {
          divisionOptions: (divisionList || []).map((d) => ({
            id: d.id || d.division_id,
            name: d.name,
          })),
        }
      ),
    [filterDivision, showDeleted, divisionList]
  );
  const clearPositionsFilters = () => {
    setSearchTerm("");
    setFilterDivision([]);
    setShowDeleted(false);
    setSortBy("name");
    setSortDirection("asc");
  };
  const removePositionsFilter = (key) => {
    if (key === "filterDivision") setFilterDivision([]);
    if (key === "showDeleted") setShowDeleted(false);
  };
  const selectedPositionState = getPositionState(selectedPosition);

  const handleRestorePosition = async (positionId) => {
    const confirmed = await showConfirm("Are you sure you want to restore this position?", "Confirm");
    if (!confirmed) return;
    try {
      await API.put(`${BASE_URL}/api/clients/${client.id}/positions/${positionId}/restore`);
      showSuccess("Position restored successfully");
      await fetchPositions();
    } catch (error) {
      console.error("Failed to restore position:", error);
      showError(error?.response?.data?.message || "Failed to restore position");
    }
  };

  const openUnsavedPrompt = (onSave, onDiscard) => {
    setUnsavedSaveAction(() => onSave);
    setUnsavedDiscardAction(() => onDiscard);
    setUnsavedPromptOpen(true);
  };

  const closeUnsavedPrompt = () => {
    setUnsavedPromptOpen(false);
    setUnsavedSaveAction(null);
    setUnsavedDiscardAction(null);
  };

  const isEditPositionDirty = (() => {
    if (!showEditModal) return false;
    const baseline = initialPositionEditSnapshot || toPositionSnapshot({});
    return JSON.stringify(toPositionSnapshot(editForm)) !== JSON.stringify(baseline);
  })();

  const isCreatePositionDirty = (() => {
    if (!showCreateModal) return false;
    const baseline =
      initialPositionCreateSnapshot ||
      toPositionSnapshot({}, { createPositionId: "", createDivisionId: "" });
    return (
      JSON.stringify(
        toPositionSnapshot(editForm, { createPositionId, createDivisionId })
      ) !== JSON.stringify(baseline)
    );
  })();

  const requestCloseEditPositionModal = () => {
    if (!isEditPositionDirty) {
      setShowEditModal(false);
      return;
    }
    openUnsavedPrompt(
      async () => {
        await handleSavePosition();
      },
      () => setShowEditModal(false)
    );
  };

  const requestCloseCreatePositionModal = () => {
    if (!isCreatePositionDirty) {
      setShowCreateModal(false);
      return;
    }
    openUnsavedPrompt(
      async () => {
        await handleCreatePositionSubmit();
      },
      () => setShowCreateModal(false)
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Briefcase className="h-5 w-5 text-sidebar-active" />
          Positions
        </h3>

        <AtlasListToolbar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search positions..."
          onFilterClick={positionsFilterPanel.toggle}
          filterOpen={positionsFilterPanel.isOpen}
          onRefresh={fetchPositions}
          refreshDisabled={loading}
          refreshSpinning={loading}
          resultsMeta={{
            startRow: positionsBase.length === 0 ? 0 : 1,
            endRow: Math.min(pageSize, positionsBase.length),
            totalCount: positionsBase.length,
            pageSize,
            pageSizeOptions: [10, 20, 50, 100],
            onPageSizeChange: setPageSize,
            sortBy,
            sortOrder: sortDirection,
            sortOptions: CLIENT_POSITIONS_SORT_OPTIONS,
            onSortByChange: setSortBy,
            onSortOrderToggle: () =>
              setSortDirection((prev) => (prev === "asc" ? "desc" : "asc")),
          }}
          actions={
            <button
              type="button"
              onClick={openCreatePositionModal}
              className="whitespace-nowrap rounded-lg bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
            >
              Create
            </button>
          }
          chipBar={
            positionsFilterChips.length > 0 ? (
              <AtlasFilterChipBar
                chips={positionsFilterChips}
                onChipClick={(chip) => positionsFilterPanel.focusField(chip.fieldId)}
                onRemove={(chip) => removePositionsFilter(chip.key)}
                onClearAll={clearPositionsFilters}
              />
            ) : null
          }
        />

        <EmbeddedFilterPanel
          isOpen={positionsFilterPanel.isOpen}
          onClose={positionsFilterPanel.close}
          onClear={clearPositionsFilters}
          footerMode="clearOnly"
        >
          <FilterMultiSelect
            id="positions-division"
            label="Division"
            labelVariant="embedded"
            options={divisionFilterOptions}
            value={filterDivision.map(String)}
            onChange={setFilterDivision}
          />
          <FilterToggle
            id="positions-show-deleted"
            label="Show Deleted"
            checked={showDeleted}
            onChange={setShowDeleted}
          />
          <FilterSelect
            id="positions-sort-by"
            label="Sort By"
            labelVariant="embedded"
            value={sortBy}
            onChange={setSortBy}
            options={CLIENT_POSITIONS_SORT_OPTIONS}
            placeholder={null}
          />
          <FilterSelect
            id="positions-sort-direction"
            label="Sort Direction"
            labelVariant="embedded"
            value={sortDirection}
            onChange={setSortDirection}
            options={[
              { value: "asc", label: "Ascending" },
              { value: "desc", label: "Descending" },
            ]}
            placeholder={null}
          />
        </EmbeddedFilterPanel>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-active mx-auto mb-3"></div>

            <div className="text-gray-500">Loading positions...</div>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-visible w-full max-w-full block no-mobile-table-stack">
            <table className="w-full table-auto bg-white border border-gray-200 rounded-xl overflow-hidden min-w-[900px]">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTableHeader
                    label="Name"
                    sortKey="name"
                    activeSortKey={sortBy}
                    sortDirection={sortDirection}
                    onSort={() =>
                      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
                    }
                  />
                  <TableHeaderCell label="Bill Rate" />
                  <TableHeaderCell label="Pay Rate" />
                  <TableHeaderCell label="Requirements" />
                  <TableHeaderCell label="Uniform" />
                  <TableHeaderCell label="Action" />
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {positionsBase.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-10 text-center">
                      <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />

                      <div className="text-gray-500 text-lg font-medium">
                        No positions found
                      </div>

                      <div className="text-gray-400 text-sm">
                        Add your first position to get started
                      </div>
                    </td>
                  </tr>
                ) : (
                  visiblePositions.map((position) => (
                    <tr
                      key={position.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td
                        className="px-4 py-3 text-sm font-medium text-red-600 hover:text-red-800 cursor-pointer"
                        onClick={() => handlePositionClick(position)}
                      >
                        <div className="flex items-start gap-2">
                          <div className="w-7 h-7 bg-sidebar-active rounded-full flex items-center justify-center shrink-0 mt-0.5">
                            <Briefcase className="h-3.5 w-3.5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <div className="break-words">{position.name}</div>
                            {getPositionDivisionName(position) ? (
                              <div className="text-xs text-gray-500 font-normal mt-0.5 break-words">
                                {getPositionDivisionName(position)}
                              </div>
                            ) : null}
                            {getPositionState(position) === "archived" && (
                              <span className="mt-1 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                Archived
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3 text-sm text-gray-500 font-medium">
                        ${position.bill_rate || position.billRate || "0.00"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-medium">
                        ${position.pay_rate || position.payRate || "0.00"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {position.requirements || "-"}
                      </td>

                      <td className="px-4 py-3 text-sm text-gray-500">
                        {position.uniform || "None"}
                      </td>

                      <td className="px-4 py-3 text-sm align-top">
                        {showDeleted ? (
                          <ActionDropdown
                            actions={[
                              {
                                label: "Restore",
                                onClick: () => handleRestorePosition(position.id),
                                className: "text-green-600",
                              },
                            ]}
                          />
                        ) : getPositionState(position) === "deleted" ? (
                          <span className="text-gray-400">-</span>
                        ) : (
                          <ActionDropdown
                            actions={[
                              {
                                label: getPositionState(position) === "archived" ? "Unarchive" : "Archive",
                                onClick: () => handleToggleArchivePosition(position),
                                className: "text-amber-600",
                              },
                              {
                                label: "Delete",
                                onClick: () => handleDeletePosition(position.id),
                                className: "text-red-500",
                              },
                            ]}
                          />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Edit Position Modal */}

        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-md shadow-2xl w-full max-w-md p-4 mx-3 relative border border-gray-100 max-h-[86vh] overflow-y-auto">
              <h4 className="text-base font-semibold text-gray-800 mb-3">
                Edit Position
              </h4>

              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-red-600">
                    Position*
                  </label>
                  <div className="w-full px-0 py-0.5 text-base text-gray-800">
                    {editForm.name || "-"}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-red-600">
                    Division*
                  </label>
                  <div className="w-full px-0 py-0.5 text-base text-gray-800">
                    {mappedDivisionName || selectedPosition?.divisionName || "-"}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Status
                  </label>
                  <div className="w-full px-0 py-0.5">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                        selectedPositionState === "archived"
                          ? "bg-amber-100 text-amber-700"
                          : selectedPositionState === "deleted"
                          ? "bg-red-100 text-red-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {selectedPositionState === "archived"
                        ? "Archived"
                        : selectedPositionState === "deleted"
                        ? "Deleted"
                        : "Active"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-red-600">
                      Bill Rate*
                    </label>
                    <div className="flex items-center gap-2 border-b border-gray-300 pb-1">
                      <span className="text-base text-gray-800">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.billRate}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            billRate: e.target.value,
                          }))
                        }
                        className="w-full outline-none text-base text-gray-800"
                        placeholder=""
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-red-600">
                      Pay Rate*
                    </label>
                    <div className="flex items-center gap-2 border-b border-gray-300 pb-1">
                      <span className="text-base text-gray-800">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.payRate}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            payRate: e.target.value,
                          }))
                        }
                        className="w-full outline-none text-base text-gray-800"
                        placeholder=""
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Unpaid 30-minute break
                  </label>

                  <div className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(editForm.unpaidBreak)}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          unpaidBreak: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-sidebar-active focus:ring-sidebar-active"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Uniform
                  </label>

                  <input
                    type="text"
                    value={editForm.uniform}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        uniform: e.target.value,
                      }))
                    }
                    className="w-full border-b border-gray-300 px-0 py-0.5 outline-none text-base text-gray-800"
                    placeholder=""
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Skills
                  </label>
                  <TextboxMultiSelect
                    options={skillsOptions}
                    selectedIds={editForm.skill_ids || []}
                    onChange={(next) =>
                      setEditForm((prev) => ({ ...prev, skill_ids: next }))
                    }
                    placeholder="Type to select skills..."
                    emptyText="No skills found"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Certifications
                  </label>
                  <TextboxMultiSelect
                    options={certificationOptions}
                    selectedIds={editForm.certification_ids || []}
                    onChange={(next) =>
                      setEditForm((prev) => ({
                        ...prev,
                        certification_ids: next,
                      }))
                    }
                    placeholder="Type to select certifications..."
                    emptyText="No certifications found"
                  />
                </div>
              </div>

              <div className="flex justify-start gap-6 mt-4 pt-3 border-t border-gray-100">
                <button
                  onClick={handleSavePosition}
                  className="text-red-600 text-base font-semibold hover:underline"
                >
                  EDIT
                </button>

                <button
                  onClick={requestCloseEditPositionModal}
                  className="text-gray-800 text-base font-semibold hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Position Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-md shadow-2xl w-full max-w-md p-4 mx-3 relative border border-gray-100 max-h-[86vh] overflow-y-auto">
              <h4 className="text-base font-semibold text-gray-800 mb-3">
                Add Position
              </h4>

              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-red-600">Position*</label>
                  <div className="relative" ref={createPositionRef}>
                    <input
                      type="text"
                      value={createPositionQuery || (createPositionId ? (createPositionOptionsFiltered.find(p => String(p.id) === String(createPositionId))?.name || '') : '')}
                      onChange={(e) => {
                        setCreatePositionQuery(e.target.value);
                        setCreatePositionId("");
                        setShowCreatePositionSuggestions(true);
                      }}
                      onFocus={() => setShowCreatePositionSuggestions(true)}
                      disabled={createOptionsLoading}
                      placeholder={
                        createOptionsLoading
                          ? "Loading positions..."
                          : "Type to search or pick a position"
                      }
                      className={`w-full border-b border-gray-300 px-0 py-0.5 outline-none text-base text-gray-800 bg-transparent ${createOptionsLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                    />

                    {showCreatePositionSuggestions && positionSuggestions(createPositionQuery).length > 0 && (
                      <ul className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-sm max-h-56 overflow-auto z-50">
                        {positionSuggestions(createPositionQuery).map((p) => (
                          <li
                            key={p.id}
                            onMouseDown={async () => {
                              setCreatePositionId(String(p.id));
                              setCreatePositionQuery(p.name);
                              setShowCreatePositionSuggestions(false);
                              setEditForm(prev => ({ ...prev, name: p.name }));
                              await loadSystemPositionTemplate(p.id);
                            }}
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                          >
                            {p.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-red-600">Division*</label>
                  <div className="relative" ref={createDivisionRef}>
                    <input
                      type="text"
                      value={createDivisionQuery || (createDivisionId ? (createDivisionOptions.find(d => String(d.id || d.division_id) === String(createDivisionId))?.name || '') : '')}
                      onChange={(e) => {
                        if (!createPositionId) return;
                        setCreateDivisionQuery(e.target.value);
                        setCreateDivisionId("");
                        setShowCreateDivisionSuggestions(true);
                      }}
                      onFocus={() => {
                        if (!createPositionId) return;
                        setShowCreateDivisionSuggestions(true);
                      }}
                      disabled={!createPositionId || createOptionsLoading}
                      placeholder={
                        !createPositionId
                          ? "Select a position first"
                          : createOptionsLoading
                            ? "Loading divisions..."
                            : "Type to search or pick a division"
                      }
                      className={`w-full border-b border-gray-300 px-0 py-0.5 outline-none text-base text-gray-800 bg-transparent ${!createPositionId || createOptionsLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                    />

                    {createPositionId && showCreateDivisionSuggestions && divisionSuggestions(divisionSuggestionQuery).length > 0 && (
                      <ul className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-sm max-h-56 overflow-auto z-50">
                        {divisionSuggestions(divisionSuggestionQuery).map((d) => (
                          <li
                            key={d.id}
                            onMouseDown={() => {
                              setCreateDivisionId(String(d.id));
                              setCreateDivisionQuery(d.name);
                              setMappedDivisionName(d.name || "");
                              setShowCreateDivisionSuggestions(false);
                            }}
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                          >
                            {d.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {!createPositionId && (
                    <p className="text-xs text-gray-500">Choose a position to see available divisions.</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-red-600">
                      Bill Rate*
                    </label>
                    <div className="flex items-center gap-2 border-b border-gray-300 pb-1">
                      <span className="text-base text-gray-800">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.billRate}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            billRate: e.target.value,
                          }))
                        }
                        className="w-full outline-none text-base text-gray-800"
                        placeholder=""
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-red-600">
                      Pay Rate*
                    </label>
                    <div className="flex items-center gap-2 border-b border-gray-300 pb-1">
                      <span className="text-base text-gray-800">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.payRate}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            payRate: e.target.value,
                          }))
                        }
                        className="w-full outline-none text-base text-gray-800"
                        placeholder=""
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Unpaid 30-minute break
                  </label>

                  <div className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(editForm.unpaidBreak)}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          unpaidBreak: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-sidebar-active focus:ring-sidebar-active"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Uniform
                  </label>

                  <input
                    type="text"
                    value={editForm.uniform}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        uniform: e.target.value,
                      }))
                    }
                    className="w-full border-b border-gray-300 px-0 py-0.5 outline-none text-base text-gray-800"
                    placeholder=""
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Skills
                  </label>
                  <TextboxMultiSelect
                    options={skillsOptions}
                    selectedIds={editForm.skill_ids || []}
                    onChange={(next) =>
                      setEditForm((prev) => ({ ...prev, skill_ids: next }))
                    }
                    placeholder="Type to select skills..."
                    emptyText="No skills found"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Certifications
                  </label>
                  <TextboxMultiSelect
                    options={certificationOptions}
                    selectedIds={editForm.certification_ids || []}
                    onChange={(next) =>
                      setEditForm((prev) => ({
                        ...prev,
                        certification_ids: next,
                      }))
                    }
                    placeholder="Type to select certifications..."
                    emptyText="No certifications found"
                  />
                </div>
              </div>

              <div className="flex justify-start gap-6 mt-4 pt-3 border-t border-gray-100">
                <button
                  onClick={handleCreatePositionSubmit}
                  disabled={createOptionsLoading}
                  className="text-red-600 text-base font-semibold hover:underline disabled:text-gray-400 disabled:no-underline"
                >
                  CREATE
                </button>

                <button
                  onClick={requestCloseCreatePositionModal}
                  className="text-gray-800 text-base font-semibold hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        <UnsavedChangesDialog
          isOpen={unsavedPromptOpen}
          onSaveAndContinue={async () => {
            if (unsavedSaveAction) await unsavedSaveAction();
            closeUnsavedPrompt();
          }}
          onDiscardAndContinue={() => {
            if (unsavedDiscardAction) unsavedDiscardAction();
            closeUnsavedPrompt();
          }}
          onReview={closeUnsavedPrompt}
        />
      </div>
    </div>
  );
};

const TextboxMultiSelect = ({
  options = [],
  selectedIds = [],
  onChange,
  placeholder = "Type to search...",
  emptyText = "No options found",
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getOptionId = (option) =>
    option?.id ?? option?.skill_id ?? option?.certification_id;
  const getOptionName = (option) => option?.name || "";
  const selectedAsString = new Set((selectedIds || []).map((id) => String(id)));

  const selectedOptions = (options || []).filter((option) =>
    selectedAsString.has(String(getOptionId(option)))
  );

  const filteredOptions = (options || [])
    .filter((option) => {
      const id = getOptionId(option);
      const name = getOptionName(option);
      if (!id || selectedAsString.has(String(id))) return false;
      if (!query.trim()) return true;
      return name.toLowerCase().includes(query.trim().toLowerCase());
    })
    .slice(0, 10);

  const handleAdd = (option) => {
    const id = getOptionId(option);
    if (!id) return;
    const next = [...(selectedIds || []), id];
    onChange(next);
    setQuery("");
    setIsOpen(true);
  };

  const handleRemove = (idToRemove) => {
    const next = (selectedIds || []).filter(
      (id) => String(id) !== String(idToRemove)
    );
    onChange(next);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white">
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedOptions.map((option) => {
            const id = getOptionId(option);
            return (
              <span
                key={`selected-${id}`}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md"
              >
                {getOptionName(option)}
                <button
                  type="button"
                  onClick={() => handleRemove(id)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  x
                </button>
              </span>
            );
          })}
        </div>
        <input
          type="text"
          value={query}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          placeholder={placeholder}
          className="w-full outline-none text-sm text-gray-800"
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">{emptyText}</div>
          ) : (
            filteredOptions.map((option) => {
              const id = getOptionId(option);
              return (
                <button
                  key={`option-${id}`}
                  type="button"
                  onClick={() => handleAdd(option)}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  {getOptionName(option)}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// Preferences Tab Component

const PreferencesTab = ({ clientData, client, onRefresh }) => {
  const appPerms = useAppPermissions();
  const canAddPreference = appPerms.canAddClientPreference;
  const canEditPreferenceRow = appPerms.canEditClientPreference;

  const [preferences, setPreferences] = useState([]);
  const [filteredPreferences, setFilteredPreferences] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [initialCreatePreferenceSnapshot, setInitialCreatePreferenceSnapshot] = useState(null);
  const [initialEditPreferenceSnapshot, setInitialEditPreferenceSnapshot] = useState(null);
  const [unsavedPromptOpen, setUnsavedPromptOpen] = useState(false);
  const [unsavedSaveAction, setUnsavedSaveAction] = useState(null);
  const [unsavedDiscardAction, setUnsavedDiscardAction] = useState(null);
  const [editingPreference, setEditingPreference] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [newPreference, setNewPreference] = useState({
    key: "", // type
    value: "", // rating
    description: "", // note
  });
  const [editPreference, setEditPreference] = useState({
    type: "",
    rating: 0,
    note: "",
    staffId: "",
    staffName: "",
    contactId: "",
    contactName: "",
  });
  const [loading, setLoading] = useState(false);

  const preferencesFilterPanel = useEmbeddedFilterLayout();
  const [filterType, setFilterType] = useState(
    CLIENT_PREFERENCES_DEFAULT_FILTERS.filterType,
  );
  const [showDeleted, setShowDeleted] = useState(
    CLIENT_PREFERENCES_DEFAULT_FILTERS.showDeleted,
  );
  const [sortBy, setSortBy] = useState(CLIENT_PREFERENCES_DEFAULT_FILTERS.sortBy);
  const [sortDirection, setSortDirection] = useState(
    CLIENT_PREFERENCES_DEFAULT_FILTERS.sortDirection,
  );
  const [pageSize, setPageSize] = useState(100);
  const [showRelatedDetail, setShowRelatedDetail] = useState(false);
  const [selectedRelatedAccount, setSelectedRelatedAccount] = useState(null);
  const [showStaffDetail, setShowStaffDetail] = useState(false);
  const [selectedStaffAccount, setSelectedStaffAccount] = useState(null);

  const [staffList, setStaffList] = useState([]);
  const [contactsList, setContactsList] = useState([]);
  const [staffRelevantContacts, setStaffRelevantContacts] = useState({});
  const [selectedContact, setSelectedContact] = useState("");
  // staff selection (for POST)
  const [data, setData] = useState({
    staffId: "",
    staffName: "",
  });
  const [showCreateStaffSuggestions, setShowCreateStaffSuggestions] = useState(false);
  const [showEditStaffSuggestions, setShowEditStaffSuggestions] = useState(false);

  const getContactId = (contact) =>
    contact?.contactAccountId ??
    contact?.contact_account_id ??
    contact?.contactAccount?.id ??
    contact?.account_id ??
    contact?.contact_id ??
    contact?.id ??
    null;

  const getContactName = (contact) =>
    contact?.contactAccount?.name ||
    contact?.name ||
    [contact?.first_name || contact?.firstName, contact?.last_name || contact?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    "Contact";

  const toContactOption = (contact) => {
    const id = Number(getContactId(contact));
    if (!Number.isFinite(id)) return null;
    return { id, name: getContactName(contact) };
  };

  const fetchRelevantContactsForStaff = async (staffId) => {
    const sid = Number(staffId);
    if (!sid || !client?.id) return [];

    if (Array.isArray(staffRelevantContacts[sid]) && staffRelevantContacts[sid].length > 0) {
      return staffRelevantContacts[sid];
    }

    try {
      const res = await API.get(
        `${BASE_URL}/api/clients/${client.id}/preferences/staff/${sid}/contacts`
      );
      const list = (res.data?.data || [])
        .map(toContactOption)
        .filter(Boolean);
      setStaffRelevantContacts((prev) => ({ ...prev, [sid]: list }));
      return list;
    } catch (err) {
      console.error("Error loading relevant contacts for staff:", err);
      setStaffRelevantContacts((prev) => ({ ...prev, [sid]: [] }));
      return [];
    }
  };

  const getContactOptionsForCreate = () => {
    const staffId = Number(data.staffId);
    if (!staffId) {
      return (contactsList || [])
        .map((c) => ({ id: Number(getContactId(c)), name: getContactName(c) }))
        .filter((c) => Number.isFinite(c.id));
    }
    return staffRelevantContacts[staffId] || [];
  };

  const getContactOptionsForEdit = (staffId) => {
    const sid = Number(staffId);
    if (!sid) {
      return (contactsList || [])
        .map((c) => ({ id: Number(getContactId(c)), name: getContactName(c) }))
        .filter((c) => Number.isFinite(c.id));
    }
    return staffRelevantContacts[sid] || [];
  };

  // map rating -> type
  const getTypeFromRating = (rating) => {
    switch (Number(rating)) {
      case 0:
        return "none";
      case 1:
        return "do-not-place";
      case 2:
        return "needs-improvement";
      case 3:
        return "neutral";
      case 4:
        return "good-fit";
      case 5:
        return "like";
      default:
        return "none";
    }
  };

  const normalizePreferenceType = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/_/g, "-")
      .replace(/\s+/g, "-");

  const resolvePreferenceFilterType = (preference) => {
    const fromType = normalizePreferenceType(preference?.type);
    if (fromType && fromType !== "none") return fromType;
    return getTypeFromRating(preference?.rating);
  };

  const getRatingValue = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    return Math.max(0, Math.min(5, numeric));
  };

  // start rating
  function StarRating({ value, onChange }) {
    const [hover, setHover] = useState(null);

    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => {
          const filled = star <= (hover || value);

          return (
            <span
              key={star}
              onClick={() => onChange(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(null)}
              className={`cursor-pointer text-5xl leading-none transition-colors select-none ${
                filled ? "text-red-600" : "text-gray-300"
              }`}
            >
              &#9733;
            </span>
          );
        })}
      </div>
    );
  }

  useEffect(() => {
    fetchPreferences();
  }, [client?.id]);

  useEffect(() => {
    loadStaff();
    fetchContacts();
    setStaffRelevantContacts({});
  }, [client?.id]);

  useEffect(() => {
    if (!data.staffId) return;
    let cancelled = false;
    (async () => {
      const options = await fetchRelevantContactsForStaff(data.staffId);
      if (cancelled) return;
      if (!options.length) {
        setSelectedContact("");
        return;
      }
      const current = Number(selectedContact);
      const stillValid = options.some((o) => Number(o.id) === current);
      if (!stillValid) {
        setSelectedContact(String(options[0].id));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data.staffId, client?.id]);

  useEffect(() => {
    if (!editPreference?.staffId) return;
    let cancelled = false;
    (async () => {
      await fetchRelevantContactsForStaff(editPreference.staffId);
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [editPreference?.staffId, client?.id]);

  useEffect(() => {
    applyFilters();
  }, [preferences, searchTerm, filterType, showDeleted, sortBy, sortDirection]);

  const fetchPreferences = async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const response = await API.get(
        `${BASE_URL}/api/clients/${client.id}/preferences`
      );
      const list = response.data.data || [];
      setPreferences(list);
      setFilteredPreferences(list);
    } catch (error) {
      console.error("Failed to fetch preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = async () => {
    if (!client?.id) return;
    try {
      const res = await API.get(
        `${BASE_URL}/api/clients/${client.id}/preferences/staff`
      );
      const list = res.data.staff || [];
      setStaffList(list);
    } catch (err) {
      console.error("Error loading staff:", err);
      setStaffList([]);
    }
  };

  const staffSuggestions = (query) => {
    const q = String(query || "").toLowerCase().trim();
    const mapped = (staffList || []).map((s) => ({
      id: s.account_id || s.id,
      name: `${s.first_name || s.firstName || ""} ${s.last_name || s.lastName || ""}`.trim(),
      raw: s,
    }));
    if (q === "") return mapped.slice(0, 50);
    return mapped.filter((s) => s.name.toLowerCase().includes(q)).slice(0, 50);
  };

  const fetchContacts = async () => {
    if (!client?.id) return;
    try {
      const res = await API.get(
        `${BASE_URL}/api/clients/${client.id}/contacts`
      );

      // ... backend sends { data: [...] }
      setContactsList(res.data.data || []);
      console.log("contactsList", res.data.data);
    } catch (err) {
      console.error("Error loading contacts:", err);
      setContactsList([]);
    }
  };

  // search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const normalizeText = (text) => {
    return String(text ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  };

  const applyFilters = () => {
    let result = [...preferences];

    // --- 1. SEARCH ---
    if (searchTerm.trim() !== "") {

      const q = normalizeText(searchTerm);

      result = result.filter((p) => {
        const typeText = normalizeText(p.type);
        const noteText = normalizeText(p.note);
        const ratingText = normalizeText(p.rating);
        const staffFirst = normalizeText(p.staff_first_name);
        const staffLast = normalizeText(p.staff_last_name);
        const contactFirst = normalizeText(p.contact_first);
        const contactLast = normalizeText(p.contact_last);

        return (
          typeText.includes(q) ||
          noteText.includes(q) ||
          ratingText.includes(q) ||
          staffFirst.includes(q) ||
          staffLast.includes(q) ||
          contactFirst.includes(q) ||
          contactLast.includes(q)
        );
      });
    }

    // --- 2. TYPE FILTER ---
    if (filterType !== "all") {
      result = result.filter(
        (p) => resolvePreferenceFilterType(p) === filterType,
      );
    }

    // --- 3. SHOW / HIDE DELETED ---
    if (showDeleted) {
      result = result.filter((p) => String(p.state || "").toLowerCase() === "deleted");
    } else {
      result = result.filter((p) => String(p.state || "active").toLowerCase() !== "deleted");
    }

    // --- 4. SORTING ---
    result.sort((a, b) => {
      let fieldA, fieldB;

      switch (sortBy) {
        case "firstName":
          fieldA = a.staff_first_name || "";
          fieldB = b.staff_first_name || "";
          break;

        case "lastName":
          fieldA = a.staff_last_name || "";
          fieldB = b.staff_last_name || "";
          break;

        case "rating":
          fieldA = Number(a.rating || 0);
          fieldB = Number(b.rating || 0);
          break;

        case "created":
          fieldA = new Date(a.create_date);
          fieldB = new Date(b.create_date);
          break;

        default:
          fieldA = a.type || "";
          fieldB = b.type || "";
      }

      if (sortDirection === "asc") {
        return fieldA > fieldB ? 1 : fieldA < fieldB ? -1 : 0;
      } else {
        return fieldA < fieldB ? 1 : fieldA > fieldB ? -1 : 0;
      }
    });

    // final result
    setFilteredPreferences(result);
  };

  const handleCreatePreference = async () => {
    try {
      const preferenceData = {
        type: newPreference.key, // auto from rating
        note: newPreference.description,
        rating: newPreference.value,
        staffId: data.staffId || null,
        contactId: selectedContact || null,
      };

      await API.post(
        `${BASE_URL}/api/clients/${client.id}/preferences`,
        preferenceData
      );

      setNewPreference({ key: "", value: "", description: "" });
      setSelectedContact("");
      setData({ staffId: "", staffName: "" });
      setShowCreateModal(false);
      fetchPreferences();
    } catch (error) {
      console.error("Failed to create preference:", error);
    }
  };

  const openEditPreference = (preference) => {
    const rating = getRatingValue(preference?.rating);
    const typeFromRow = normalizePreferenceType(preference?.type);
    const mappedType = typeFromRow || getTypeFromRating(rating);
    const resolvedStaff = getPreferenceStaffAccount(preference);
    const resolvedContact = getPreferenceConnectAccount(preference);

    setEditingPreference(preference);
    const nextEditPreference = {
      type: mappedType,
      rating,
      note: String(preference?.note || ""),
      staffId: resolvedStaff?.id ? String(resolvedStaff.id) : "",
      staffName: resolvedStaff?.name || "",
      contactId: resolvedContact?.id ? String(resolvedContact.id) : "",
      contactName: resolvedContact?.name || "",
    };
    setEditPreference(nextEditPreference);
    setInitialEditPreferenceSnapshot({
      type: String(nextEditPreference.type || ""),
      rating: String(nextEditPreference.rating || ""),
      note: String(nextEditPreference.note || ""),
      staffId: String(nextEditPreference.staffId || ""),
      contactId: String(nextEditPreference.contactId || ""),
    });
    setShowEditModal(true);
  };

  const closeEditPreference = () => {
    setShowEditModal(false);
    setEditingPreference(null);
    setEditPreference({
      type: "",
      rating: 0,
      note: "",
      staffId: "",
      staffName: "",
      contactId: "",
      contactName: "",
    });
    setInitialEditPreferenceSnapshot(null);
  };

  const toCreatePreferenceSnapshot = () => ({
    staffId: String(data.staffId || ""),
    contactId: String(selectedContact || ""),
    type: String(newPreference.key || ""),
    rating: String(newPreference.value || ""),
    note: String(newPreference.description || ""),
  });

  const toEditPreferenceSnapshot = () => ({
    type: String(editPreference.type || ""),
    rating: String(editPreference.rating || ""),
    note: String(editPreference.note || ""),
    staffId: String(editPreference.staffId || ""),
    contactId: String(editPreference.contactId || ""),
  });

  const openUnsavedPrompt = (onSave, onDiscard) => {
    setUnsavedSaveAction(() => onSave);
    setUnsavedDiscardAction(() => onDiscard);
    setUnsavedPromptOpen(true);
  };

  const closeUnsavedPrompt = () => {
    setUnsavedPromptOpen(false);
    setUnsavedSaveAction(null);
    setUnsavedDiscardAction(null);
  };

  const requestCloseCreatePreferenceModal = () => {
    const baseline =
      initialCreatePreferenceSnapshot || { staffId: "", contactId: "", type: "", rating: "", note: "" };
    const dirty = JSON.stringify(toCreatePreferenceSnapshot()) !== JSON.stringify(baseline);
    if (!dirty) {
      setShowCreateModal(false);
      return;
    }
    openUnsavedPrompt(
      async () => {
        await handleCreatePreference();
      },
      () => {
        setShowCreateModal(false);
        setNewPreference({ key: "", value: "", description: "" });
        setSelectedContact("");
        setData({ staffId: "", staffName: "" });
      }
    );
  };

  const requestCloseEditPreferenceModal = () => {
    const baseline =
      initialEditPreferenceSnapshot || { type: "", rating: "0", note: "", staffId: "", contactId: "" };
    const dirty = JSON.stringify(toEditPreferenceSnapshot()) !== JSON.stringify(baseline);
    if (!dirty) {
      closeEditPreference();
      return;
    }
    openUnsavedPrompt(
      async () => {
        await handleSaveEditedPreference();
      },
      () => {
        closeEditPreference();
      }
    );
  };

  const handleSaveEditedPreference = async () => {
    if (!editingPreference?.id && !editingPreference?.preference_id) return;

    const preferenceId = editingPreference.id || editingPreference.preference_id;
    setSavingEdit(true);
    try {
      await API.put(`${BASE_URL}/api/clients/${client.id}/preferences/${preferenceId}`, {
        type: editPreference.type || getTypeFromRating(editPreference.rating) || null,
        rating: editPreference.rating || null,
        note: editPreference.note?.trim() || null,
        staffId: editPreference.staffId ? Number(editPreference.staffId) : null,
        contactId: editPreference.contactId ? Number(editPreference.contactId) : null,
      });

      showSuccess("Preference updated successfully");
      closeEditPreference();
      fetchPreferences();
    } catch (error) {
      console.error("Failed to update preference:", error);
      showError(
        error?.response?.data?.message || "Failed to update preference. Please try again."
      );
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    console.log("Selected contact ID:", selectedContact);
  }, [selectedContact]);

  const handleDeletePreference = async (preferenceId) => {
    const confirmed = await showConfirm("Are you sure you want to delete this preference?", "Confirm");
    if (!confirmed) return;
    try {
      await API.delete(
        `${BASE_URL}/api/clients/${client.id}/preferences/${preferenceId}`
      );
      fetchPreferences();
    } catch (error) {
      console.error("Failed to delete preference:", error);
    }
  };

  const handleRestorePreference = async (preferenceId) => {
    try {
      await API.put(
        `${BASE_URL}/api/clients/${client.id}/preferences/${preferenceId}/restore`
      );
      showSuccess("Preference restored successfully");
      fetchPreferences();
    } catch (error) {
      console.error("Failed to restore preference:", error);
      showError(
        error?.response?.data?.message || "Failed to restore preference. Please try again."
      );
    }
  };

  const getPreferenceStaffAccount = (preference) => {
    const rowStaffName =
      [preference?.staff_first_name, preference?.staff_last_name]
        .filter(Boolean)
        .join(" ")
        .trim() || "";

    const staffId =
      preference?.preferenceAccountId ??
      preference?.preference_account_id ??
      preference?.preference_accountId ??
      preference?.preferenceAccount?.id ??
      preference?.preference_account?.id ??
      preference?.staff_id ??
      preference?.staffId ??
      null;

    const staffName =
      preference?.preferenceAccount?.name ||
      preference?.preference_account?.name ||
      rowStaffName ||
      "Staff";
    if (staffId) return { id: staffId, name: staffName };

    // Fallback: resolve staff by name from loaded staff list
    const normalizedRowName = normalizeText(rowStaffName);
    const matchedStaff = (staffList || []).find((s) => {
      const listName = normalizeText(
        `${s?.first_name || ""} ${s?.last_name || ""}`.trim()
      );
      return normalizedRowName && listName === normalizedRowName;
    });

    return {
      id: matchedStaff?.account_id || matchedStaff?.id || null,
      name: staffName,
    };
  };

  const getPreferenceConnectAccount = (preference) => {
    const rowContactName =
      [preference?.contact_first, preference?.contact_last]
        .filter(Boolean)
        .join(" ")
        .trim() || "";

    const contactId =
      preference?.contactAccountId ??
      preference?.contact_account_id ??
      preference?.contact_accountId ??
      preference?.contactAccount?.id ??
      preference?.contact_account?.id ??
      preference?.contact_id ??
      preference?.contactId ??
      null;

    const contactName =
      preference?.contactAccount?.name ||
      preference?.contact_account?.name ||
      rowContactName ||
      "Contact";
    if (contactId) return { id: contactId, name: contactName };

    // Fallback: resolve contact by name from loaded contacts list
    const normalizedRowName = normalizeText(rowContactName);
    const matchedContact = (contactsList || []).find((c) => {
      const listName = normalizeText(
        `${c?.first_name || c?.firstName || ""} ${c?.last_name || c?.lastName || ""}`.trim()
      );
      const altName = normalizeText(c?.name || "");
      return normalizedRowName && (listName === normalizedRowName || altName === normalizedRowName);
    });

    return {
      id: matchedContact ? getContactId(matchedContact) : null,
      name: contactName,
    };
  };

  const openRelatedAccountDetail = (related) => {
    if (!related?.id) {
      showError("No related account found for this preference.");
      return;
    }
    setSelectedRelatedAccount({ id: related.id, name: related.name });
    setShowRelatedDetail(true);
  };

  const goToPersonnelStaffEdit = (related) => {
    if (!related?.id) {
      showError("No related staff account found for this preference.");
      return;
    }
    setSelectedStaffAccount({ id: related.id, name: related.name });
    setShowStaffDetail(true);
  };

  useEffect(() => {
    if (!showEditModal) return;
    let cancelled = false;
    (async () => {
      const options = await fetchRelevantContactsForStaff(editPreference.staffId);
      if (cancelled) return;
      if (!options.length) {
        if (editPreference.contactId || editPreference.contactName) {
          setEditPreference((prev) => ({ ...prev, contactId: "", contactName: "" }));
        }
        return;
      }

      // Match create-flow behavior: auto-select first relevant contact for selected staff
      if (!editPreference.contactId) {
        setEditPreference((prev) => ({
          ...prev,
          contactId: String(options[0].id),
          contactName: options[0].name,
        }));
        return;
      }

      const curr = Number(editPreference.contactId);
      const stillValid = options.some((o) => Number(o.id) === curr);
      if (!stillValid) {
        setEditPreference((prev) => ({
          ...prev,
          contactId: String(options[0].id),
          contactName: options[0].name,
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showEditModal, editPreference.staffId, editPreference.contactId, editPreference.contactName, client?.id]);

  const preferencesBase = Array.isArray(filteredPreferences)
    ? filteredPreferences
    : [];
  const visiblePreferences = preferencesBase.slice(0, pageSize);
  const preferencesFilterChips = useMemo(
    () => buildClientPreferencesFilterChips({ filterType, showDeleted }),
    [filterType, showDeleted],
  );

  const clearPreferencesFilters = () => {
    setSearchTerm("");
    setFilterType(CLIENT_PREFERENCES_DEFAULT_FILTERS.filterType);
    setShowDeleted(CLIENT_PREFERENCES_DEFAULT_FILTERS.showDeleted);
    setSortBy(CLIENT_PREFERENCES_DEFAULT_FILTERS.sortBy);
    setSortDirection(CLIENT_PREFERENCES_DEFAULT_FILTERS.sortDirection);
  };

  const removePreferencesFilter = (key) => {
    if (key === "filterType") setFilterType("all");
    if (key === "showDeleted") setShowDeleted(false);
  };

  const handlePreferenceColumnSort = (key) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDirection(key === "rating" || key === "created" ? "desc" : "asc");
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Settings className="h-5 w-5 text-sidebar-active" />
          Preferences
        </h3>

        <AtlasListToolbar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search preferences..."
          onFilterClick={preferencesFilterPanel.toggle}
          filterOpen={preferencesFilterPanel.isOpen}
          onRefresh={fetchPreferences}
          refreshDisabled={loading}
          refreshSpinning={loading}
          resultsMeta={{
            startRow: preferencesBase.length === 0 ? 0 : 1,
            endRow: Math.min(pageSize, preferencesBase.length),
            totalCount: preferencesBase.length,
            pageSize,
            pageSizeOptions: [10, 20, 50, 100],
            onPageSizeChange: setPageSize,
            sortBy,
            sortOrder: sortDirection,
            sortOptions: CLIENT_PREFERENCES_SORT_OPTIONS,
            onSortByChange: setSortBy,
            onSortOrderToggle: () =>
              setSortDirection((prev) => (prev === "asc" ? "desc" : "asc")),
          }}
          actions={
            canAddPreference ? (
              <button
                type="button"
                onClick={() => {
                  setInitialCreatePreferenceSnapshot({
                    staffId: String(data.staffId || ""),
                    contactId: String(selectedContact || ""),
                    type: String(newPreference.key || ""),
                    rating: String(newPreference.value || ""),
                    note: String(newPreference.description || ""),
                  });
                  setShowCreateModal(true);
                }}
                className="whitespace-nowrap rounded-lg bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
              >
                Create
              </button>
            ) : null
          }
          chipBar={
            preferencesFilterChips.length > 0 ? (
              <AtlasFilterChipBar
                chips={preferencesFilterChips}
                onChipClick={(chip) =>
                  preferencesFilterPanel.focusField(chip.fieldId)
                }
                onRemove={(chip) => removePreferencesFilter(chip.key)}
                onClearAll={clearPreferencesFilters}
              />
            ) : null
          }
        />

        <EmbeddedFilterPanel
          isOpen={preferencesFilterPanel.isOpen}
          onClose={preferencesFilterPanel.close}
          onClear={clearPreferencesFilters}
          footerMode="clearOnly"
        >
          <FilterSelect
            id="preferences-type"
            label="Type"
            labelVariant="embedded"
            value={filterType}
            onChange={setFilterType}
            options={CLIENT_PREFERENCES_TYPE_OPTIONS}
            placeholder={null}
          />
          <FilterToggle
            id="preferences-show-deleted"
            label="Show Deleted"
            checked={showDeleted}
            onChange={setShowDeleted}
          />
          <FilterSelect
            id="preferences-sort-by"
            label="Sort By"
            labelVariant="embedded"
            value={sortBy}
            onChange={setSortBy}
            options={CLIENT_PREFERENCES_SORT_OPTIONS}
            placeholder={null}
          />
          <FilterSelect
            id="preferences-sort-direction"
            label="Sort Direction"
            labelVariant="embedded"
            value={sortDirection}
            onChange={setSortDirection}
            options={[
              { value: "asc", label: "Ascending" },
              { value: "desc", label: "Descending" },
            ]}
            placeholder={null}
          />
        </EmbeddedFilterPanel>

        {/* CONTENT */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-active mx-auto mb-3"></div>
            <div className="text-gray-500">Loading preferences...</div>
          </div>
        ) : (
          <div className="space-y-4">
            {preferencesBase.length === 0 ? (
              <div className="text-center py-12">
                <Settings className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <div className="text-gray-500 text-lg font-medium">
                  No preferences found
                </div>
                <div className="text-gray-400 text-sm">
                  Try adjusting your search or refresh
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto overflow-y-visible border border-gray-200 rounded-xl block max-w-full no-mobile-table-stack">
                <table className="w-full table-auto bg-white min-w-[800px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <SortableTableHeader
                        label="Staff"
                        sortKey="firstName"
                        sortBy={sortBy}
                        sortOrder={sortDirection}
                        onSort={handlePreferenceColumnSort}
                      />
                      <TableHeaderCell label="Contact" />
                      <SortableTableHeader
                        label="Type"
                        sortKey="type"
                        sortBy={sortBy}
                        sortOrder={sortDirection}
                        onSort={handlePreferenceColumnSort}
                      />
                      <SortableTableHeader
                        label="Rating"
                        sortKey="rating"
                        sortBy={sortBy}
                        sortOrder={sortDirection}
                        onSort={handlePreferenceColumnSort}
                      />
                      <TableHeaderCell label="Note" />
                      <SortableTableHeader
                        label="Created"
                        sortKey="created"
                        sortBy={sortBy}
                        sortOrder={sortDirection}
                        onSort={handlePreferenceColumnSort}
                      />
                      <TableHeaderCell label="Actions" className="text-right" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {visiblePreferences.map((preference) => {
                      const staffRelated = getPreferenceStaffAccount(preference);
                      const connectRelated = getPreferenceConnectAccount(preference);
                      const staffName =
                        preference.staff_first_name || preference.staff_last_name
                          ? `${preference.staff_first_name ?? ""} ${preference.staff_last_name ?? ""}`.trim()
                          : "-";
                      const contactName =
                        preference.contact_first || preference.contact_last
                          ? `${preference.contact_first ?? ""} ${preference.contact_last ?? ""}`.trim()
                          : "-";
                      const typeLabel = String(preference.type || "-")
                        .replace(/[-_]/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase());
                      const createdLabel = preference.create_date
                        ? formatAppDate(preference.create_date, { emptyValue: "-" })
                        : "-";
                      const initials =
                        staffName && staffName !== "-"
                          ? staffName
                            .split(" ")
                            .filter(Boolean)
                            .slice(0, 2)
                            .map((p) => p[0]?.toUpperCase())
                            .join("")
                          : "NA";

                      return (
                        <tr key={preference.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-2 text-sm">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-fuchsia-600 text-white flex items-center justify-center text-xs font-semibold shrink-0">
                                {initials}
                              </div>
                              {canEditPreferenceRow ? (
                              <button
                                type="button"
                                onClick={() => openEditPreference(preference)}
                                className="font-semibold text-red-600 hover:text-red-700 hover:underline"
                                title="Edit preference"
                              >
                                {staffName}
                              </button>
                              ) : (
                                <span className="font-semibold text-gray-900">{staffName}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-700">{contactName}</td>
                          <td className="px-3 py-2 text-sm text-gray-800">{typeLabel}</td>
                          <td className="px-3 py-2 text-sm text-gray-800">
                            {preference.rating != null ? `${preference.rating}/5` : "-"}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-700">{preference.note || "-"}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">{createdLabel}</td>
                          <td className="px-3 py-2 text-sm align-top">
                            <ActionDropdown
                              title={!showDeleted ? "View Related" : undefined}
                              actions={
                                showDeleted
                                  ? canEditPreferenceRow
                                    ? [
                                        {
                                          label: "Restore",
                                          onClick: () => handleRestorePreference(preference.id),
                                          className: "text-emerald-600",
                                        },
                                      ]
                                    : []
                                  : [
                                      {
                                        label: "Staff",
                                        onClick: () =>
                                          goToPersonnelStaffEdit(
                                            staffRelated
                                          ),
                                      },
                                      ...(connectRelated?.id
                                        ? [
                                            {
                                              label: "Contact",
                                              onClick: () =>
                                                openRelatedAccountDetail(
                                                  connectRelated
                                                ),
                                            },
                                          ]
                                        : []),
                                      ...(canEditPreferenceRow
                                        ? [
                                            {
                                              label: "Delete",
                                              onClick: () => handleDeletePreference(preference.id),
                                              className: "text-red-500",
                                            },
                                          ]
                                        : []),
                                    ]
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* CREATE MODAL */}
            {showCreateModal && (
              <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
                onClick={requestCloseCreatePreferenceModal}
              >
                <div
                  className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 relative border border-gray-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Settings className="h-5 w-5 text-sidebar-active" />
                    Create New Preference
                  </h4>

                  <div className="space-y-4">
                    {/* Staff */}
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Staff
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={data.staffName}
                          onChange={(e) => {
                            setData({ staffId: "", staffName: e.target.value });
                            setShowCreateStaffSuggestions(true);
                          }}
                          onFocus={() => setShowCreateStaffSuggestions(true)}
                          placeholder="Type to search staff"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                        />

                        {showCreateStaffSuggestions && staffSuggestions(data.staffName).length > 0 && (
                          <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg max-h-48 overflow-auto shadow-lg">
                            {staffSuggestions(data.staffName).map((s) => (
                              <li
                                key={s.id}
                                onMouseDown={() => {
                                  setData({ staffId: String(s.id), staffName: s.name });
                                  setShowCreateStaffSuggestions(false);
                                }}
                                className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                              >
                                {s.name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    {/* Contact Provided Feedback */}
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Contact Provided Feedback
                      </label>

                      <select
                        value={selectedContact}
                        onChange={(e) => setSelectedContact(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                      >
                        <option value="">Select contact</option>
                        {getContactOptionsForCreate().map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Rating */}
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Rating
                      </label>
                      <StarRating
                        value={newPreference.value}
                        onChange={(rating) => {
                          setNewPreference((prev) => ({
                            ...prev,
                            value: rating,
                            key: getTypeFromRating(rating),
                          }));
                        }}
                      />
                    </div>

                    {/* Type (auto) */}
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Type
                      </label>
                      <input
                        type="text"
                        value={newPreference.key}
                        readOnly
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-gray-50 text-gray-600"
                      />
                    </div>

                    {/* Note */}
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Note
                      </label>
                      <textarea
                        value={newPreference.description}
                        onChange={(e) =>
                          setNewPreference((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active transition-colors resize-none"
                        placeholder="Enter preference note"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-gray-200">
                    <button
                      onClick={requestCloseCreatePreferenceModal}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-semibold"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={handleCreatePreference}
                      className="px-4 py-2 bg-sidebar-active text-white rounded-lg hover:bg-red-600 transition-all duration-200 text-sm font-semibold"
                    >
                      Create Preference
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showEditModal && editingPreference && (
              <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
                onClick={requestCloseEditPreferenceModal}
              >
                <div
                  className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 relative border border-gray-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Edit Preference
                  </h4>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">Staff</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={editPreference.staffName}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditPreference((prev) => ({
                              ...prev,
                              staffId: "",
                              staffName: val,
                              contactId: "",
                              contactName: "",
                            }));
                            setShowEditStaffSuggestions(true);
                          }}
                          onFocus={() => setShowEditStaffSuggestions(true)}
                          placeholder="Type to search staff"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                        />

                        {editPreference.staffId || editPreference.staffName ? (
                          <button
                            type="button"
                            onClick={() =>
                              setEditPreference((prev) => ({
                                ...prev,
                                staffId: "",
                                staffName: "",
                                contactId: "",
                                contactName: "",
                              }))
                            }
                            className="absolute right-2 top-2 text-lg text-gray-500 hover:text-gray-700 leading-none"
                            title="Clear selected staff"
                          >
                            x
                          </button>
                        ) : null}

                        {showEditStaffSuggestions && staffSuggestions(editPreference.staffName).length > 0 && (
                          <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg max-h-48 overflow-auto shadow-lg">
                            {staffSuggestions(editPreference.staffName).map((s) => (
                              <li
                                key={s.id}
                                onMouseDown={() => {
                                  setEditPreference((prev) => ({
                                    ...prev,
                                    staffId: String(s.id),
                                    staffName: s.name,
                                    contactId: "",
                                    contactName: "",
                                  }));
                                  setShowEditStaffSuggestions(false);
                                }}
                                className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                              >
                                {s.name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Contact Provided Feedback
                      </label>
                      {editPreference.contactId || editPreference.contactName ? (
                        <div className="flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2.5 bg-gray-50">
                          <span className="text-sm text-gray-800">
                            {editPreference.contactName || "-"}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setEditPreference((prev) => ({
                                ...prev,
                                contactId: "",
                                contactName: "",
                              }))
                            }
                            className="text-lg text-gray-500 hover:text-gray-700 leading-none"
                            title="Clear selected contact"
                          >
                            x
                          </button>
                        </div>
                      ) : (
                        <select
                          value={editPreference.contactId}
                          onChange={(e) => {
                            const selectedId = e.target.value;
                            const selected = getContactOptionsForEdit(editPreference.staffId).find(
                              (c) => String(c.id) === selectedId
                            );
                            setEditPreference((prev) => ({
                              ...prev,
                              contactId: selectedId,
                              contactName: selected?.name || "",
                            }));
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
                        >
                          <option value="">Select contact</option>
                          {getContactOptionsForEdit(editPreference.staffId).map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">Type</label>
                      <div className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-gray-50 text-gray-800">
                        {String(editPreference.type || "")
                          .replace(/[-_]/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase()) || "-"}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">Rating</label>
                      <div className="flex items-center gap-1 text-5xl leading-none">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => {
                              const next = editPreference.rating === star ? 0 : star;
                              setEditPreference((prev) => ({
                                ...prev,
                                rating: next,
                                type: getTypeFromRating(next),
                              }));
                            }}
                            className={
                              star <= editPreference.rating ? "text-red-600" : "text-gray-300"
                            }
                            aria-label={`Set rating ${star}`}
                          >
                            &#9733;
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">Note</label>
                      <textarea
                        value={editPreference.note}
                        onChange={(e) =>
                          setEditPreference((prev) => ({
                            ...prev,
                            note: e.target.value,
                          }))
                        }
                        maxLength={400}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none resize-none focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active"
                      />
                      <div className="text-xs text-gray-400">{editPreference.note.length}/400</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={requestCloseEditPreferenceModal}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveEditedPreference}
                      disabled={savingEdit}
                      className="px-4 py-2 bg-sidebar-active text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-semibold disabled:opacity-60"
                    >
                      {savingEdit ? "Saving..." : "Save Preference"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <ContactDetailModal
              contact={selectedRelatedAccount}
              clientId={client?.id ?? client?.client_id}
              isOpen={showRelatedDetail}
              onClose={() => {
                setShowRelatedDetail(false);
                setSelectedRelatedAccount(null);
              }}
              onSaved={fetchPreferences}
            />
            {showStaffDetail && selectedStaffAccount && (
              <PersonnelDetailModal
                person={selectedStaffAccount}
                type={"temp"}
                isOpen={showStaffDetail}
                onClose={() => {
                  setShowStaffDetail(false);
                  setSelectedStaffAccount(null);
                }}
              />
            )}
            <UnsavedChangesDialog
              isOpen={unsavedPromptOpen}
              onSaveAndContinue={async () => {
                if (unsavedSaveAction) await unsavedSaveAction();
                closeUnsavedPrompt();
              }}
              onDiscardAndContinue={() => {
                if (unsavedDiscardAction) unsavedDiscardAction();
                closeUnsavedPrompt();
              }}
              onReview={closeUnsavedPrompt}
            />
          </div>
        )}

      </div>
    </div>
  );
};

// Locations Tab Component

const LocationsTab = ({ clientData, client, onRefresh, onChangeTab }) => {
  const [locations, setLocations] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);

  const [selectedLocation, setSelectedLocation] = useState(null);

  const [editForm, setEditForm] = useState({
    name: "",
    street: "",
    city: "",
    region: "",
    zip: "",
    country: "",
    primary: false,
  });

  // === FILTER + SEARCH STATES ===
  const [searchTerm, setSearchTerm] = useState("");
  const locationsFilterPanel = useEmbeddedFilterLayout();
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [pageSize, setPageSize] = useState(100);

  const [filteredLocations, setFilteredLocations] = useState([]);

  const [loading, setLoading] = useState(false);
  const [initialLocationSnapshot, setInitialLocationSnapshot] = useState(null);
  const [unsavedPromptOpen, setUnsavedPromptOpen] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, [client?.id, showDeleted]);

  useEffect(() => {
    applyFilters();
  }, [locations, searchTerm, showDeleted, sortBy, sortDirection]);

  const formatAddress = (location) => {
    const parts = [
      location.street,
      location.city,
      location.region,
      location.zip,
      location.country,
    ].filter((x) => x && String(x).trim() !== "");

    return parts.join(", ");
  };

  const getLocationDisplayName = (location) => {
    const explicitName = String(location?.name || "").trim();
    if (explicitName) return explicitName;
    const street = String(location?.street || "").trim();
    if (street) return street;
    return "-";
  };

  const toLocationSnapshot = (form) => ({
    name: String(form?.name || ""),
    street: String(form?.street || ""),
    city: String(form?.city || ""),
    region: String(form?.region || ""),
    zip: String(form?.zip || ""),
    country: String(form?.country || ""),
    primary: !!form?.primary,
  });

  const isLocationModalDirty = (() => {
    if (!showEditModal) return false;
    const baseline = initialLocationSnapshot || toLocationSnapshot({});
    return JSON.stringify(toLocationSnapshot(editForm)) !== JSON.stringify(baseline);
  })();

  const requestCloseLocationModal = () => {
    if (!isLocationModalDirty) {
      setShowEditModal(false);
      return;
    }
    setUnsavedPromptOpen(true);
  };


  const fetchLocations = async () => {
    if (!client?.id) return;

    setLoading(true);

    try {
      const response = await API.get(
        `${BASE_URL}/api/clients/${client.id}/clientLocations`,
        {
          params: { showDeleted: showDeleted ? 1 : 0 },
        }
      );

      const locationsData = (response.data?.data || []).map((row) => {
        return {
          // ... unique row id (for React key + edit/delete)
          id: row.client_location_id || row.id || row.address_id,

          // ... location name from address table (address_name), fallback to computed name
          name: row.address_name || row.name || "",

          // ... address fields
          street: row.street || "",
          city: row.city || "",
          region: row.region || "",
          zip: row.zip || "",
          // ... Convert country code to name immediately
          country: row.country ? countryCodeToName(row.country) : "",

          // ... primary (if backend gives it, else false)
          // primary: Boolean(row.primary),
          primary: Number(row.primary) === 1,

          // ... state (if backend gives it)
          state: row.state || "active",

          // keep original ids if you need later
          address_id: row.address_id || null,
        };
      });

      console.log("[LOCATIONS DEBUG] Raw data from backend:", response.data?.data);
      console.log("[LOCATIONS DEBUG] Mapped locations data:", locationsData);
      console.log("[LOCATIONS DEBUG] Number of locations:", locationsData.length);
      locationsData.forEach((loc, idx) => {
        console.log(`  Location ${idx + 1}:`, {
          name: loc.name,
          street: loc.street,
          city: loc.city,
          region: loc.region,
          zip: loc.zip,
          country: loc.country,
          primary: loc.primary,
          address_id: loc.address_id
        });
      });

      // Show every location as returned by the backend -- the previous dedup step here
      // hid any non-primary location whose street "fuzzy-matched" the Primary Location's
      // address, which wrongly collapsed clients with many distinct named locations inside
      // one building (e.g. named meeting rooms/floors all sharing the same street address)
      // down to just a handful. Client asked to show everything for now.
      locationsData.sort((a, b) => (Number(b.primary) - Number(a.primary)));

      setLocations(locationsData);
      setFilteredLocations(locationsData);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    } finally {
      setLoading(false);
    }
  };


  const applyFilters = () => {
    let result = [...locations];

    // --- - 1. SEARCH ----
    if (searchTerm.trim() !== "") {
      const q = searchTerm.toLowerCase();

      result = result.filter((l) => {
        const name = (l.name || "").toLowerCase();

        const addr = [l.street, l.city, l.region, l.zip, l.country]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return name.includes(q) || addr.includes(q);
      });
    }

    // ---- 2. SHOW DELETED ----
    if (!showDeleted) {
      result = result.filter((l) => l.state !== "deleted");
    }

    // ---- 3. SORTING ----
    // Always show primary locations first, then sort the rest
    result.sort((a, b) => {
      // Primary locations always come first
      if (a.primary && !b.primary) return -1;
      if (!a.primary && b.primary) return 1;

      // For non-primary or both primary, sort by address
      const addrA = [a.street, a.city, a.region, a.zip, a.country]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const addrB = [b.street, b.city, b.region, b.zip, b.country]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (sortDirection === "asc") return addrA.localeCompare(addrB);
      else return addrB.localeCompare(addrA);
    });

    setFilteredLocations(result);
  };

  const handleLocationClick = (location) => {
    console.log("=== EDIT LOCATION DEBUG ===");
    console.log("Clicked location name:", location.name);
    console.log("Clicked location data:", location);
    console.log("Location street:", location.street);
    console.log("Location city:", location.city);
    console.log("Location region:", location.region);
    console.log("Location zip:", location.zip);
    console.log("Location country (raw):", location.country);

    setSelectedLocation(location);

    // Normalize country to ISO2 to match manual address behavior.
    const countryValue = location.country || "";
    const normalizedCountry = normalizeCountryToISO2(countryValue) || countryValue;

    console.log("Country normalization:", countryValue, "=>", normalizedCountry);

    const formData = {
      name: location.name || "",
      street: location.street || "",
      city: location.city || "",
      region: location.region || "",
      zip: location.zip || "",
      country: normalizedCountry,
      primary: Boolean(location.primary),
    };

    console.log("Setting edit form to:", formData);

    setEditForm(formData);
    setInitialLocationSnapshot(toLocationSnapshot(formData));
    setShowEditModal(true);
  };

  const handleSaveLocation = async () => {
    try {
      const locationData = {
        primary: editForm.primary || false,
        address: {
          name: editForm.name,
          street: editForm.street,
          city: editForm.city,
          region: editForm.region,
          zip: editForm.zip,
          country: editForm.country,
        },
      };

      // Validation
      if (
        !editForm.name?.trim() ||
        !editForm.street?.trim() ||
        !editForm.city?.trim() ||
        !editForm.region?.trim() ||
        !editForm.zip?.trim() ||
        !editForm.country?.trim()
      ) {
        showError("All fields are required (Name, Street, City, Province, Zip, Country)");
        return;
      }

      console.log("=== SAVE LOCATION DEBUG ===");

      console.log("Location data being sent:", locationData);
      console.log("Selected location:", selectedLocation);
      console.log("Is update?", !!selectedLocation);

      if (selectedLocation && selectedLocation.isBilling) {
        // Saving the synthetic billing row should update the client's billing address
        console.log("Updating billing address via profile endpoint");
        const payload = { billingAddress: locationData.street };
        // also support snake_case field name on server
        try {
          await API.put(
            `${BASE_URL}/api/clients/${client.id}/profile`,
            payload
          );
        } catch (err) {
          // try snake_case key if server expects it
          await API.put(`${BASE_URL}/api/clients/${client.id}/profile`, {
            billing_address: locationData.street,
          });
        }
      } else if (selectedLocation) {
        console.log("Updating location with ID:", selectedLocation.id);
        const response = await API.put(
          `${BASE_URL}/api/clients/${client.id}/locations/${selectedLocation.id}`,
          locationData
        );
        console.log("Update response:", response.data);
      } else {
        console.log("Creating new location");
        const response = await API.post(
          `${BASE_URL}/api/clients/${client.id}/locations`,
          locationData
        );
        console.log("Create response:", response.data);
      }

      console.log("Location saved; closing modal...");
      setShowEditModal(false);

      console.log("Clearing selected location...");
      setSelectedLocation(null);

      // Refresh data
      console.log("Starting data refresh...");
      try {
        if (typeof onRefresh === "function") {
          console.log("Calling onRefresh...");
          await onRefresh();
        }
      } catch (_) {
        console.log("onRefresh failed or not available");
      }

      console.log("Calling fetchLocations...");
      await fetchLocations();
      console.log("fetchLocations completed");

      // Show success message
      if (selectedLocation && selectedLocation.isBilling) {
        showSuccess("Billing address updated successfully!");
      } else {
        showSuccess(
          selectedLocation
            ? "Location updated successfully!"
            : "Location created successfully!"
        );
      }
    } catch (error) {
      console.error("Failed to save location:", error);
      showError("Failed to save location. Please try again.");
    }
  };

  const handleDeleteLocation = async (location) => {
    const confirmed = await showConfirm(
      location && location.isBilling
        ? "Are you sure you want to clear this billing address?"
        : "Are you sure you want to delete this location?",
      "Confirm"
    );
    if (!confirmed) return;
    try {
      if (location && location.isBilling) {
        // Clear the billing address on the profile
        try {
          await API.put(`${BASE_URL}/api/clients/${client.id}/profile`, {
            billingAddress: null,
          });
        } catch (err) {
          await API.put(`${BASE_URL}/api/clients/${client.id}/profile`, {
            billing_address: null,
          });
        }
        // refresh profile and locations
        try {
          if (typeof onRefresh === "function") await onRefresh();
        } catch (_) { }
        await fetchLocations();
        showSuccess("Billing address cleared");
        return;
      }

      await API.delete(
        `${BASE_URL}/api/clients/${client.id}/locations/${location.id}`
      );

      await fetchLocations();
      showSuccess("Location deleted");
    } catch (error) {
      console.error("Failed to delete location:", error);
      showError("Failed to delete location");
    }
  };

  const locationsBase = Array.isArray(filteredLocations) ? filteredLocations : [];
  const visibleLocations = locationsBase.slice(0, pageSize);
  const locationsFilterChips = useMemo(
    () => buildClientLocationsFilterChips({ showDeleted, sortBy, sortDirection }),
    [showDeleted, sortBy, sortDirection],
  );

  const clearLocationsFilters = () => {
    setSearchTerm("");
    setShowDeleted(false);
    setSortBy("name");
    setSortDirection("asc");
  };

  const removeLocationsFilter = (key) => {
    if (key === "showDeleted") setShowDeleted(false);
    if (key === "sortBy") setSortBy("name");
    if (key === "sortDirection") setSortDirection("asc");
  };

  const handleRestoreLocation = async (location) => {
    const confirmed = await showConfirm(
      "Are you sure you want to restore this location?",
      "Confirm"
    );
    if (!confirmed) return;
    try {
      await API.put(`${BASE_URL}/api/clients/${client.id}/locations/${location.id}/restore`);
      await fetchLocations();
      showSuccess("Location restored");
    } catch (error) {
      console.error("Failed to restore location:", error);
      showError("Failed to restore location");
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <MapPin className="h-5 w-5 text-sidebar-active" />
          Locations
        </h3>

        <AtlasListToolbar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search locations..."
          onFilterClick={locationsFilterPanel.toggle}
          filterOpen={locationsFilterPanel.isOpen}
          onRefresh={fetchLocations}
          refreshDisabled={loading}
          refreshSpinning={loading}
          resultsMeta={{
            startRow: locationsBase.length === 0 ? 0 : 1,
            endRow: Math.min(pageSize, locationsBase.length),
            totalCount: locationsBase.length,
            pageSize,
            pageSizeOptions: [10, 20, 50, 100],
            onPageSizeChange: setPageSize,
            sortBy,
            sortOrder: sortDirection,
            sortOptions: CLIENT_LOCATIONS_SORT_OPTIONS,
            onSortByChange: setSortBy,
            onSortOrderToggle: () =>
              setSortDirection((prev) => (prev === "asc" ? "desc" : "asc")),
          }}
          actions={
            <button
              type="button"
              onClick={() => {
                setSelectedLocation(null);
                const nextForm = {
                  name: "",
                  street: "",
                  city: "",
                  region: "",
                  zip: "",
                  country: "",
                  primary: false,
                };
                setEditForm(nextForm);
                setInitialLocationSnapshot(toLocationSnapshot(nextForm));
                setShowEditModal(true);
              }}
              className="whitespace-nowrap rounded-lg bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
            >
              Add Location
            </button>
          }
          chipBar={
            locationsFilterChips.length > 0 ? (
              <AtlasFilterChipBar
                chips={locationsFilterChips}
                onChipClick={(chip) => locationsFilterPanel.focusField(chip.fieldId)}
                onRemove={(chip) => removeLocationsFilter(chip.key)}
                onClearAll={clearLocationsFilters}
              />
            ) : null
          }
        />

        <EmbeddedFilterPanel
          isOpen={locationsFilterPanel.isOpen}
          onClose={locationsFilterPanel.close}
          onClear={clearLocationsFilters}
          footerMode="clearOnly"
        >
          <FilterToggle
            id="locations-show-deleted"
            label="Show Deleted"
            checked={showDeleted}
            onChange={setShowDeleted}
          />
          <FilterSelect
            id="locations-sort-by"
            label="Sort By"
            labelVariant="embedded"
            value={sortBy}
            onChange={setSortBy}
            options={CLIENT_LOCATIONS_SORT_OPTIONS}
            placeholder={null}
          />
          <FilterSelect
            id="locations-sort-direction"
            label="Sort Direction"
            labelVariant="embedded"
            value={sortDirection}
            onChange={setSortDirection}
            options={[
              { value: "asc", label: "Ascending" },
              { value: "desc", label: "Descending" },
            ]}
            placeholder={null}
          />
        </EmbeddedFilterPanel>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-active mx-auto mb-3"></div>

            <div className="text-gray-500">Loading locations...</div>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-visible w-full max-w-full block no-mobile-table-stack">
            <table className="w-full table-auto bg-white border border-gray-200 rounded-xl overflow-hidden min-w-[800px]">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTableHeader
                    label="Location Name"
                    sortKey="name"
                    activeSortKey={sortBy}
                    sortDirection={sortDirection}
                    onSort={() =>
                      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
                    }
                    className="w-1/4"
                  />
                  <TableHeaderCell label="Address" className="w-2/5" />
                  <TableHeaderCell label="Primary" className="w-1/6" />
                  <TableHeaderCell label="Actions" className="w-1/6" />
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {locationsBase.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center">
                      <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />

                      <div className="text-gray-500 text-lg font-medium">
                        No locations found
                      </div>

                      <div className="text-gray-400 text-sm">
                        Add your first location to get started
                      </div>
                    </td>
                  </tr>
                ) : (
                  visibleLocations.map((location) => {
                    const address = formatAddress(location) || "-";

                    return (
                      <tr
                        key={location.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        {/* ... Location Name */}
                        <td
                          className="px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                          onClick={() => handleLocationClick(location)}
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-sidebar-active rounded-full flex items-center justify-center flex-shrink-0">
                              <MapPin className="h-4 w-4 text-white" />
                            </div>

                            <div className="text-sm font-medium text-red-600 truncate">
                              {getLocationDisplayName(location)}
                            </div>
                          </div>
                        </td>

                        {/* ... Address column */}
                        <td className="px-3 py-2 text-sm text-gray-600">
                          <div className="truncate" title={address}>{address}</div>
                        </td>

                        {/* ... Primary Yes/No */}
                        <td className="px-3 py-2 text-sm">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${location.primary
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                              }`}
                          >
                            {location.primary ? "Yes" : "No"}
                          </span>
                        </td>

                        {/* ... Actions */}
                        <td className="px-3 py-2 text-sm align-top">
                          <ActionDropdown
                            actions={
                              showDeleted
                                ? [
                                    {
                                      label: "Restore",
                                      onClick: () => handleRestoreLocation(location),
                                      className: "text-green-600",
                                    },
                                  ]
                                : [
                                    {
                                      label: "Edit",
                                      onClick: () => handleLocationClick(location),
                                      className: "text-blue-600",
                                    },
                                    ...(!location.primary
                                      ? [
                                          {
                                            label: "Delete",
                                            onClick: () => handleDeleteLocation(location),
                                            className: "text-red-500",
                                          },
                                        ]
                                      : []),
                                  ]
                            }
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Edit Location Modal */}

        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 relative border border-gray-100">
              <h4 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-sidebar-active" />

                {selectedLocation ? "Edit Location" : "Add Location"}
              </h4>

              <div className="space-y-6">
                {(() => {
                  console.log(
                    "Locations modal rendering with editForm:",
                    editForm
                  );
                  return null;
                })()}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Location Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Head Office, Warehouse 1, Primary Branch..."
                      maxLength={255}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Street <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="text"
                      value={editForm.street}
                      onChange={(e) => {
                        setEditForm((prev) => ({
                          ...prev,
                          street: e.target.value,
                        }));
                      }}
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter street address"
                      maxLength={255}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      City <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="text"
                      value={editForm.city}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter city"
                      maxLength={100}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Province/State <span className="text-red-500">*</span>
                    </label>


                    <GoogleAutocompleteInput
                      value={editForm.region}
                      onChange={(val) => {
                        const regionStr = String(val || "").split(",")[0].trim();
                        const matchedCountryIso2 =
                          extractCountryISO2FromRegionValue(val);

                        if (matchedCountryIso2) {
                          setEditForm((prev) => ({
                            ...prev,
                            region: regionStr,
                            country: matchedCountryIso2,
                          }));
                          return;
                        }

                        setEditForm((prev) => ({
                          ...prev,
                          region: regionStr,
                        }));
                      }}
                      placeholder="Enter province/state"
                      types="administrative_area_level_1"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      ZIP/Postal Code <span className="text-red-500">*</span>
                    </label>

                    <input
                      type="text"
                      value={editForm.zip}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          zip: e.target.value,
                        }))
                      }
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter ZIP/postal code"
                      maxLength={20}
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Country <span className="text-red-500">*</span>
                    </label>

                    <GoogleAutocompleteInput
                      value={COUNTRY_CODE_TO_NAME[editForm.country] || editForm.country}
                      onChange={(val) => {
                        const iso2 = normalizeCountryToISO2(val);

                        if (!iso2) {
                          console.log("Invalid country input from Google:", val);
                          return;
                        }

                        setEditForm((prev) => ({ ...prev, country: iso2 }));
                      }}
                      placeholder="Enter country"
                      types="country"
                    />
                  </div>
                </div>


              </div>

              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={requestCloseLocationModal}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-semibold"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSaveLocation}
                  className="px-6 py-3 bg-sidebar-active text-white rounded-xl hover:bg-red-600 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
                >
                  {selectedLocation ? "Update" : "Add"} Location
                </button>
              </div>
            </div>
          </div>
        )}
        <UnsavedChangesDialog
          isOpen={unsavedPromptOpen}
          onSaveAndContinue={async () => {
            await handleSaveLocation();
            setUnsavedPromptOpen(false);
          }}
          onDiscardAndContinue={() => {
            setShowEditModal(false);
            setUnsavedPromptOpen(false);
          }}
          onReview={() => setUnsavedPromptOpen(false)}
        />
      </div>
    </div>
  );
};

// Audits Tab Component

const AuditsTab = ({ client }) => (
  <RichAuditsPanel
    subjectId={client?.id}
    fetchUrl={client?.id ? `${BASE_URL}/api/clients/${client.id}/audits/rich` : ""}
    title="Audit History"
  />
);

// ISO Codes for filtering states by country
const LOCATION_ISO_CODES = {
  "Canada": "CA",
  "United States": "US",
  "United Kingdom": "GB",
  "Australia": "AU",
  "New Zealand": "NZ",
  "Ireland": "IE",
  "India": "IN",
  "Singapore": "SG",
  // Add others as needed or rely on no-filter
};

export default ClientDetailModal;






